import {
  AlertTriangle,
  ArchiveRestore,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Command,
  Copy,
  Download,
  Eye,
  ExternalLink,
  FileText,
  Filter,
  Folder,
  FolderPlus,
  Grid3X3,
  Home,
  KeyRound,
  LayoutList,
  Link2,
  ListTree,
  LogIn,
  LogOut,
  Maximize2,
  Minimize2,
  ImagePlus,
  Palette,
  PanelRight,
  Plus,
  RefreshCw,
  RotateCcw,
  Rows3,
  Save,
  Search,
  Settings,
  Share2,
  ShieldCheck,
  SlidersHorizontal,
  Star,
  Tag,
  Trash2,
  Upload,
  X,
  ZoomIn,
  ZoomOut
} from 'lucide-react';
import { type CSSProperties, type DragEvent as ReactDragEvent, type PointerEvent as ReactPointerEvent, type ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  APIError,
  CollectionInfo,
  KnowledgeTag,
  PreviewInfo,
  Resource,
  SearchStatus,
  SearchResult,
  ShareInfo,
  ThemeSettings,
  TrashItem,
  addCollectionItem,
  cleanupTrash,
  createCollection,
  createExternalLink,
  createFolder,
  createShare,
  createTag,
  currentFolderZipURL,
  deleteResource,
  getResourcePreview,
  getResourcePreviewPages,
  getPublicShare,
  getSearchStatus,
  getThemeSettings,
  listCollections,
  listFavorites,
  listRecent,
  listResources,
  listShares,
  listTags,
  listTrash,
  login,
  logout,
  me,
  publicDownloadURL,
  publicPreviewURL,
  recordRecent,
  resourcesZipURL,
  restoreTrash,
  purgeTrash,
  revokeShare,
  saveThemeSettings,
  searchResources,
  setFavorite,
  unlockPublicShare,
  uploadThemeBackground,
  uploadResource,
  zipDownloadName
} from './api/client';
import type { QueryClient } from '@tanstack/react-query';

type ViewMode = 'list' | 'grid' | 'compact';
type SortMode = 'name' | 'modified' | 'size' | 'type';
type FilterMode = 'all' | 'documents' | 'media' | 'images' | 'archives' | 'code' | 'folders';
type ThemeMode = 'calm' | 'warm' | 'contrast';
type BackgroundPreset = 'soft' | 'immersive' | 'focus';
type MobileWorkspacePanel = 'files' | 'knowledge' | 'preview' | 'manage';
type WorkspaceRouteCommand = 'search' | 'knowledge' | 'preview' | 'manage';
type WorkspaceRouteState = { workspaceCommand?: WorkspaceRouteCommand; initialSearch?: string; knowledgeSearchFilter?: KnowledgeSearchFilter };
type KnowledgeRouteState = { organizationTargets?: Resource[] };
type ManagementRouteState = { currentParent?: WorkspaceParentState; selectedResourceIds?: string[]; shareSelectedId?: string };
type WorkspacePane = 'sidebar' | 'files' | 'preview';
type SettingsFeedbackKind = 'busy' | 'success' | 'error' | 'info';
type ManagementFeedbackKind = 'busy' | 'success' | 'error' | 'info';
type KnowledgeSearchFilter = { kind: 'tag' | 'collection'; id: string; label: string };

type ManagementFeedback = {
  kind: ManagementFeedbackKind;
  title: string;
  description: string;
};

type ThemePreferences = {
  mode: ThemeMode;
  accent: string;
  backgroundImage: string;
  backgroundBlur: number;
  backgroundDim: number;
  surfaceOpacity: number;
  defaultView: ViewMode;
};

const themeStorageKey = 'janestra:theme';
const themeUpdateEventName = 'janestra:theme-updated';
const themeAssetsDBName = 'janestra-theme-assets';
const themeAssetsStoreName = 'assets';
const themeBackgroundImageKey = 'workspace-background';
const themeBackgroundImageRef = 'indexeddb:workspace-background';
const themeServerBackgroundImageRef = 'server:account-background';
const themePendingBackgroundUploadKey = 'janestra:theme-pending-background-upload';
const workspaceStateKey = 'janestra:workspace-state';
const commandHistoryKey = 'janestra:command-history';
const workspaceFilesPath = '/workspace';
const workspaceKnowledgePath = '/workspace/knowledge';
const workspaceManagePath = '/workspace/manage';
const workspacePreviewWidthKey = 'janestra:workspace-preview-width:v3';
const workspacePreviewHeightKey = 'janestra:workspace-preview-height';
const workspaceResourcesStaleMs = 30 * 1000;
const userSessionStaleMs = 60 * 1000;
const previewMinWidth = 640;
const previewMinWidthCompact = 400;
const previewDefaultWidth = 860;
const previewMinHeight = 340;
const previewDefaultHeight = 430;
const workspaceCompactBreakpoint = 1180;
const workspaceSidebarWidthWide = 230;
const workspaceSidebarWidthCompact = 180;
const workspaceFilesWidthWideMin = 310;
const workspaceFilesWidthCompactMin = 280;
const previewZoomMin = 60;
const previewZoomMax = 240;
const previewZoomStep = 10;

type PendingThemeBackgroundUpload = {
  size: number;
  uploadedAt: string;
  expiresAt: number;
};

const defaultThemePreferences: ThemePreferences = {
  mode: 'calm',
  accent: '#1f6f7e',
  backgroundImage: '',
  backgroundBlur: 12,
  backgroundDim: 46,
  surfaceOpacity: 74,
  defaultView: 'list'
};

const filterLabels: Record<FilterMode, string> = {
  all: '全部',
  documents: '文档',
  media: '音视频',
  images: '图片',
  archives: '压缩包',
  code: '代码',
  folders: '文件夹'
};

const sortLabels: Record<SortMode, string> = {
  name: '名称',
  modified: '修改时间',
  size: '大小',
  type: '类型'
};

const viewModeLabels: Record<ViewMode, string> = {
  list: '清单',
  grid: '卡片',
  compact: '紧凑'
};

const themeModeLabels: Record<ThemeMode, string> = {
  calm: '清爽',
  warm: '暖色',
  contrast: '高对比'
};

const themeModeDescriptions: Record<ThemeMode, string> = {
  calm: '中性浅色，适合日常使用',
  warm: '色温更柔和，适合长时间浏览',
  contrast: '边界更明确，适合复杂背景'
};

const themeModeOptions: Array<{ mode: ThemeMode; accent: string }> = [
  { mode: 'calm', accent: '#1f6f7e' },
  { mode: 'warm', accent: '#a15c00' },
  { mode: 'contrast', accent: '#172033' }
];

const backgroundPresetOptions: Array<{ id: BackgroundPreset; label: string; description: string; blur: number; dim: number; surface: number }> = [
  { id: 'soft', label: '通透玻璃', description: '背景能透出来，面板仍然清楚', blur: 12, dim: 46, surface: 74 },
  { id: 'immersive', label: '沉浸背景', description: '背景更明显，界面更轻', blur: 7, dim: 34, surface: 58 },
  { id: 'focus', label: '专注阅读', description: '遮罩和面板更稳，适合复杂背景', blur: 18, dim: 62, surface: 86 }
];

const accentOptions: Array<{ value: string; label: string }> = [
  { value: '#1f6f7e', label: '青绿' },
  { value: '#355c7d', label: '湖蓝' },
  { value: '#2f6f3f', label: '森林' },
  { value: '#8a4b2a', label: '陶土' },
  { value: '#7c3aed', label: '紫色' },
  { value: '#a13a32', label: '砖红' }
];
const backgroundSourceMaxBytes = 256 * 1024 * 1024;
const backgroundTargetBytes = 16 * 1024 * 1024;
const backgroundDimensions = [5120, 4480, 3840, 3200, 2560, 2048];
const backgroundOutputFormats = ['image/webp', 'image/jpeg'];
const backgroundQualities = [0.96, 0.9, 0.84, 0.78, 0.7, 0.62];
const previewMetadataStaleMs = 5 * 60 * 1000;

type WorkspaceParentState = { id?: string; path: string; title: string };
type WorkspaceSavedState = {
  parent: WorkspaceParentState;
  parentHistory: WorkspaceParentState[];
  openTabs: Resource[];
  activeTabId: string;
  mobilePanel: MobileWorkspacePanel;
};

const defaultWorkspaceParent: WorkspaceParentState = { path: '', title: '资料库' };

function normalizeWorkspaceParent(input: unknown): WorkspaceParentState | null {
  if (!input || typeof input !== 'object') return null;
  const parent = input as Partial<WorkspaceParentState>;
  if (typeof parent.path !== 'string' || typeof parent.title !== 'string') return null;
  return { id: typeof parent.id === 'string' ? parent.id : undefined, path: parent.path, title: parent.title };
}

function readWorkspaceState(): WorkspaceSavedState {
  try {
    const raw = sessionStorage.getItem(workspaceStateKey);
    if (!raw) {
      return { parent: defaultWorkspaceParent, parentHistory: [], openTabs: [], activeTabId: '', mobilePanel: 'files' };
    }
    const parsed = JSON.parse(raw) as Partial<WorkspaceSavedState>;
    const parent = normalizeWorkspaceParent(parsed.parent) ?? defaultWorkspaceParent;
    const parentHistory = Array.isArray(parsed.parentHistory) ? parsed.parentHistory.map(normalizeWorkspaceParent).filter((item): item is WorkspaceParentState => !!item).slice(-20) : [];
    const openTabs = Array.isArray(parsed.openTabs) ? parsed.openTabs.filter((item) => item && item.type === 'file' && typeof item.id === 'string') : [];
    const activeTabId = typeof parsed.activeTabId === 'string' && openTabs.some((item) => item.id === parsed.activeTabId) ? parsed.activeTabId : openTabs.at(-1)?.id ?? '';
    const mobilePanel = parsed.mobilePanel && ['files', 'knowledge', 'preview', 'manage'].includes(parsed.mobilePanel) ? parsed.mobilePanel : 'files';
    return { parent, parentHistory, openTabs: openTabs.slice(-8), activeTabId, mobilePanel };
  } catch {
    return { parent: defaultWorkspaceParent, parentHistory: [], openTabs: [], activeTabId: '', mobilePanel: 'files' };
  }
}

function saveWorkspaceState(state: WorkspaceSavedState) {
  try {
    sessionStorage.setItem(workspaceStateKey, JSON.stringify(state));
  } catch {
    // Session persistence is a convenience; the workspace should keep working if storage is unavailable.
  }
}

function readCommandHistory() {
  try {
    const raw = localStorage.getItem(commandHistoryKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string').slice(0, 5) : [];
  } catch {
    return [];
  }
}

function saveCommandHistory(ids: string[]) {
  try {
    localStorage.setItem(commandHistoryKey, JSON.stringify(ids.slice(0, 5)));
  } catch {
    // Command history is only a UI convenience.
  }
}

function clearCommandHistory() {
  try {
    localStorage.removeItem(commandHistoryKey);
  } catch {
    // Command history is only a UI convenience.
  }
}

function previewMinWidthFor(innerWidth: number) {
  return innerWidth <= workspaceCompactBreakpoint ? previewMinWidthCompact : previewMinWidth;
}

// Maximum usable preview width for the current viewport. Must stay in sync with
// the reserved sidebar/files widths declared in the workspace grid CSS so that
// the stored --workspace-preview-width value never exceeds what the grid can
// actually render (which previously caused the resize handle to desync).
function computePreviewMaxWidth(innerWidth: number) {
  const minWidth = previewMinWidthFor(innerWidth);
  if (innerWidth <= workspaceCompactBreakpoint) {
    return Math.max(minWidth, innerWidth - workspaceSidebarWidthCompact - workspaceFilesWidthCompactMin);
  }
  return Math.max(minWidth, innerWidth - workspaceSidebarWidthWide - workspaceFilesWidthWideMin);
}

function readWorkspacePreviewDimension(key: string, fallback: number, min: number, max: number) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const value = Number(raw);
    if (!Number.isFinite(value)) return fallback;
    return Math.round(clampNumber(value, min, max, fallback));
  } catch {
    return fallback;
  }
}

function saveWorkspacePreviewDimension(key: string, value: number) {
  try {
    localStorage.setItem(key, String(Math.round(value)));
  } catch {
    // Preview sizing is a display preference; ignore storage failures.
  }
}

function readThemePreferences(): ThemePreferences {
  try {
    const raw = localStorage.getItem(themeStorageKey);
    if (!raw) return defaultThemePreferences;
    const parsed = JSON.parse(raw) as Partial<ThemePreferences>;
    return normalizeThemePreferences(parsed);
  } catch {
    return defaultThemePreferences;
  }
}

function cacheThemePreferences(preferences: ThemePreferences) {
  localStorage.setItem(themeStorageKey, JSON.stringify(preferences));
  localStorage.setItem('janestra:view', preferences.defaultView);
}

function normalizeThemePreferences(input: Partial<ThemePreferences>): ThemePreferences {
  const mode = input.mode && input.mode in themeModeLabels ? input.mode : defaultThemePreferences.mode;
  const accent = typeof input.accent === 'string' && /^#[0-9a-fA-F]{6}$/.test(input.accent) ? input.accent : defaultThemePreferences.accent;
  const defaultView = input.defaultView && ['list', 'grid', 'compact'].includes(input.defaultView) ? input.defaultView : defaultThemePreferences.defaultView;
  return {
    mode,
    accent,
    backgroundImage: typeof input.backgroundImage === 'string' ? input.backgroundImage : '',
    backgroundBlur: clampNumber(input.backgroundBlur, 0, 28, defaultThemePreferences.backgroundBlur),
    backgroundDim: clampNumber(input.backgroundDim, 0, 92, defaultThemePreferences.backgroundDim),
    surfaceOpacity: clampNumber(input.surfaceOpacity, 10, 100, defaultThemePreferences.surfaceOpacity),
    defaultView
  };
}

function themeSettingsToPreferences(theme: ThemeSettings): ThemePreferences {
  return normalizeThemePreferences({
    mode: theme.mode as ThemeMode,
    accent: theme.accent,
    backgroundImage: theme.has_background ? themeServerBackgroundImageRef : '',
    backgroundBlur: theme.background_blur,
    backgroundDim: theme.background_dim,
    surfaceOpacity: theme.surface_opacity,
    defaultView: theme.default_view as ViewMode
  });
}

function themePreferencesToPayload(preferences: ThemePreferences) {
  const normalized = normalizeThemePreferences(preferences);
  return {
    mode: normalized.mode,
    accent: normalized.accent,
    background_blur: normalized.backgroundBlur,
    background_dim: normalized.backgroundDim,
    surface_opacity: normalized.surfaceOpacity,
    default_view: normalized.defaultView
  };
}

function themeBackgroundURL(theme: ThemeSettings) {
  return theme.has_background && theme.background_url ? theme.background_url : '';
}

function clampNumber(value: unknown, min: number, max: number, fallback: number) {
  const next = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(next)) return fallback;
  return Math.min(max, Math.max(min, next));
}

function applyResolvedThemePreferences(preferences: ThemePreferences, resolvedBackgroundImage: string) {
  const root = document.documentElement;
  const backgroundVisuals = backgroundVisualValues(preferences, Boolean(resolvedBackgroundImage));
  root.dataset.themeMode = preferences.mode;
  root.style.setProperty('--accent', preferences.accent);
  root.style.setProperty('--accent-muted', `${preferences.accent}1a`);
  root.style.setProperty('--theme-bg-image', resolvedBackgroundImage ? `url(${JSON.stringify(resolvedBackgroundImage)})` : 'none');
  root.style.setProperty('--theme-bg-blur', `${preferences.backgroundBlur}px`);
  root.style.setProperty('--theme-bg-opacity', resolvedBackgroundImage ? '1' : '0');
  root.style.setProperty('--theme-bg-dim', `${preferences.backgroundDim / 100}`);
  root.style.setProperty('--theme-bg-overlay-top', backgroundVisuals.overlayTop);
  root.style.setProperty('--theme-bg-overlay-mid', backgroundVisuals.overlayMid);
  root.style.setProperty('--theme-bg-overlay-bottom', backgroundVisuals.overlayBottom);
  root.style.setProperty('--theme-bg-vignette', backgroundVisuals.vignette);
  root.style.setProperty('--theme-bg-shadow', backgroundVisuals.shadow);
  root.style.setProperty('--surface-alpha', backgroundVisuals.surfaceBase);
  root.style.setProperty('--surface-alpha-soft', backgroundVisuals.surfaceSoft);
  root.style.setProperty('--surface-alpha-strong', backgroundVisuals.surfaceStrong);
  root.style.setProperty('--surface-alpha-solid', backgroundVisuals.surfaceSolid);
  root.style.setProperty('--surface-blur', `${backgroundVisuals.surfaceBlur}px`);
}

function backgroundVisualValues(preferences: ThemePreferences, hasBackground: boolean) {
  const dim = preferences.backgroundDim / 100;
  const surface = preferences.surfaceOpacity / 100;
  if (hasBackground) {
    const overlayMid = Math.min(0.56, Math.max(0.08, dim * 0.46 - 0.04));
    const surfaceSoft = Math.min(0.62, Math.max(0.18, 0.12 + surface * 0.5));
    const surfaceStrong = Math.min(0.76, Math.max(0.24, 0.18 + surface * 0.6));
    const surfaceSolid = Math.min(0.86, Math.max(0.36, 0.28 + surface * 0.58));
    return {
      overlayTop: formatAlpha(Math.min(0.68, overlayMid + 0.12)),
      overlayMid: formatAlpha(overlayMid),
      overlayBottom: formatAlpha(Math.min(0.72, overlayMid + 0.1)),
      vignette: formatAlpha(Math.min(0.34, Math.max(0.1, dim * 0.24))),
      shadow: formatAlpha(Math.min(0.22, Math.max(0.06, (1 - dim) * 0.28))),
      surfaceBase: formatAlpha(surfaceStrong),
      surfaceSoft: formatAlpha(surfaceSoft),
      surfaceStrong: formatAlpha(surfaceStrong),
      surfaceSolid: formatAlpha(surfaceSolid),
      surfaceBlur: Math.round(28 + preferences.backgroundBlur * 0.9)
    };
  }
  const surfaceBase = Math.min(1, Math.max(0.88, surface + 0.02));
  return {
    overlayTop: formatAlpha(hasBackground ? Math.min(0.97, dim + 0.13) : 0),
    overlayMid: formatAlpha(hasBackground ? Math.min(0.94, dim + 0.03) : 0),
    overlayBottom: formatAlpha(hasBackground ? Math.min(0.98, dim + 0.1) : 0),
    vignette: formatAlpha(hasBackground ? Math.min(0.42, Math.max(0.18, dim * 0.34)) : 0),
    shadow: formatAlpha(hasBackground ? Math.min(0.16, Math.max(0.04, (1 - dim) * 0.2)) : 0),
    surfaceBase: formatAlpha(surfaceBase),
    surfaceSoft: formatAlpha(Math.min(0.98, Math.max(0.74, surface - 0.07))),
    surfaceStrong: formatAlpha(Math.min(1, Math.max(0.9, surface + 0.04))),
    surfaceSolid: formatAlpha(Math.min(1, Math.max(0.96, surface + 0.08))),
    surfaceBlur: Math.round(16 + preferences.backgroundBlur * 0.45)
  };
}

function formatAlpha(value: number) {
  return value.toFixed(3);
}

function backgroundPreviewStyle(backgroundUrl: string, preferences: ThemePreferences): CSSProperties {
  const visuals = backgroundVisualValues(preferences, Boolean(backgroundUrl));
  return {
    '--preview-bg-image': backgroundUrl ? `url(${backgroundUrl})` : 'none',
    '--preview-bg-opacity': backgroundUrl ? '1' : '0',
    '--preview-bg-blur': `${preferences.backgroundBlur}px`,
    '--preview-bg-overlay-top': visuals.overlayTop,
    '--preview-bg-overlay-mid': visuals.overlayMid,
    '--preview-bg-overlay-bottom': visuals.overlayBottom,
    '--preview-bg-vignette': visuals.vignette,
    '--preview-bg-shadow': visuals.shadow,
    '--preview-surface-alpha': visuals.surfaceStrong
  } as CSSProperties;
}

function activeBackgroundPreset(preferences: ThemePreferences) {
  return backgroundPresetOptions.find(
    (option) => option.blur === preferences.backgroundBlur && option.dim === preferences.backgroundDim && option.surface === preferences.surfaceOpacity
  )?.id;
}

function previewQueryOptions(resourceId: string) {
  return {
    queryKey: ['preview', resourceId],
    queryFn: () => getResourcePreview(resourceId),
    staleTime: previewMetadataStaleMs
  };
}

function prefetchResourcePreview(queryClient: QueryClient, resourceId: string) {
  return queryClient.prefetchQuery(previewQueryOptions(resourceId));
}

function bytesLabel(bytes: number) {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

function readPendingThemeBackgroundUpload(): PendingThemeBackgroundUpload | null {
  try {
    const raw = localStorage.getItem(themePendingBackgroundUploadKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingThemeBackgroundUpload>;
    if (!parsed.size || parsed.size <= 0 || typeof parsed.uploadedAt !== 'string' || !parsed.expiresAt) return null;
    if (parsed.expiresAt < Date.now()) {
      localStorage.removeItem(themePendingBackgroundUploadKey);
      return null;
    }
    return { size: parsed.size, uploadedAt: parsed.uploadedAt, expiresAt: parsed.expiresAt };
  } catch {
    return null;
  }
}

function cachePendingThemeBackgroundUpload(upload: Pick<PendingThemeBackgroundUpload, 'size' | 'uploadedAt'>) {
  const next: PendingThemeBackgroundUpload = {
    ...upload,
    expiresAt: Date.now() + 6 * 60 * 60 * 1000
  };
  localStorage.setItem(themePendingBackgroundUploadKey, JSON.stringify(next));
  return next;
}

function clearPendingThemeBackgroundUpload() {
  localStorage.removeItem(themePendingBackgroundUploadKey);
}

function loadImageElement(file: File) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('图片读取失败'));
    };
    image.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('图片压缩失败'));
        return;
      }
      resolve(blob);
    }, type, quality);
  });
}

function openThemeAssetsDB() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(themeAssetsDBName, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(themeAssetsStoreName)) {
        db.createObjectStore(themeAssetsStoreName);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(new Error('背景图片存储打开失败'));
    request.onblocked = () => reject(new Error('背景图片存储被其他页面占用'));
  });
}

async function readThemeBackgroundImage() {
  const db = await openThemeAssetsDB();
  return new Promise<Blob | null>((resolve, reject) => {
    const transaction = db.transaction(themeAssetsStoreName, 'readonly');
    const request = transaction.objectStore(themeAssetsStoreName).get(themeBackgroundImageKey);
    request.onsuccess = () => resolve(request.result instanceof Blob ? request.result : null);
    request.onerror = () => reject(new Error('背景图片读取失败'));
    transaction.oncomplete = () => db.close();
    transaction.onerror = () => {
      db.close();
      reject(new Error('背景图片读取失败'));
    };
  });
}

async function saveThemeBackgroundImage(blob: Blob) {
  const db = await openThemeAssetsDB();
  return new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(themeAssetsStoreName, 'readwrite');
    transaction.objectStore(themeAssetsStoreName).put(blob, themeBackgroundImageKey);
    transaction.oncomplete = () => {
      db.close();
      resolve();
    };
    transaction.onerror = () => {
      db.close();
      reject(new Error('背景图片保存失败'));
    };
  });
}

async function deleteThemeBackgroundImage() {
  const db = await openThemeAssetsDB();
  return new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(themeAssetsStoreName, 'readwrite');
    transaction.objectStore(themeAssetsStoreName).delete(themeBackgroundImageKey);
    transaction.oncomplete = () => {
      db.close();
      resolve();
    };
    transaction.onerror = () => {
      db.close();
      reject(new Error('背景图片删除失败'));
    };
  });
}

function readThemeBackgroundImageSafely() {
  return Promise.race([
    readThemeBackgroundImage().catch(() => null),
    new Promise<Blob | null>((resolve) => window.setTimeout(() => resolve(null), 3000))
  ]);
}

function cleanupLocalThemeBackgroundImage() {
  void deleteThemeBackgroundImage().catch(() => undefined);
}

async function prepareBackgroundImage(file: File) {
  if (file.size > backgroundSourceMaxBytes) {
    throw new Error(`图片不能超过 ${bytesLabel(backgroundSourceMaxBytes)}`);
  }
  const image = await loadImageElement(file);
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  if (!sourceWidth || !sourceHeight) throw new Error('图片尺寸无效');

  let best: { blob: Blob; width: number; height: number } | null = null;
  for (const maxDimension of backgroundDimensions) {
    const scale = Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight));
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('浏览器不支持图片处理');
    ctx.fillStyle = '#f6f7f9';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(image, 0, 0, width, height);

    for (const format of backgroundOutputFormats) {
      for (const quality of backgroundQualities) {
        const blob = await canvasToBlob(canvas, format, quality);
        if (!best || blob.size < best.blob.size) best = { blob, width, height };
        if (blob.size <= backgroundTargetBytes) {
          return {
            blob,
            sourceBytes: file.size,
            storedBytes: blob.size,
            width,
            height
          };
        }
      }
    }
  }

  if (!best) throw new Error('图片处理失败');
  if (best.blob.size > backgroundTargetBytes) throw new Error('图片过于复杂，压缩后仍无法保存为背景，可以换一张图或先裁剪后再上传');
  return {
    blob: best.blob,
    sourceBytes: file.size,
    storedBytes: best.blob.size,
    width: best.width,
    height: best.height
  };
}

function useThemePreferences() {
  const [preferences, setPreferencesState] = useState<ThemePreferences>(() => readThemePreferences());
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(() => {
    const current = readThemePreferences();
    return current.backgroundImage === themeBackgroundImageRef || current.backgroundImage === themeServerBackgroundImageRef ? '' : current.backgroundImage;
  });

  useEffect(() => {
    const onThemeUpdated = (event: Event) => {
      const detail = event instanceof CustomEvent ? event.detail : undefined;
      if (detail && typeof detail === 'object' && 'preferences' in detail) {
        const next = normalizeThemePreferences((detail as { preferences: ThemePreferences }).preferences);
        setPreferencesState(next);
        if ('backgroundImageUrl' in detail) setBackgroundImageUrl(String((detail as { backgroundImageUrl?: string }).backgroundImageUrl ?? ''));
        return;
      }
      setPreferencesState(detail ? normalizeThemePreferences(detail as ThemePreferences) : readThemePreferences());
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === themeStorageKey || event.key === 'janestra:view') {
        const next = readThemePreferences();
        setPreferencesState(next);
        if (next.backgroundImage === themeServerBackgroundImageRef) {
          setBackgroundImageUrl(`/api/v1/theme/background?sync=${Date.now()}`);
        }
      }
    };
    window.addEventListener(themeUpdateEventName, onThemeUpdated);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(themeUpdateEventName, onThemeUpdated);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadServerTheme = async () => {
      try {
        const theme = await getThemeSettings();
        if (cancelled) return;
        const cached = readThemePreferences();
        const serverPreferences = themeSettingsToPreferences(theme);
        const next = !theme.has_background && cached.backgroundImage === themeBackgroundImageRef
          ? normalizeThemePreferences({ ...serverPreferences, backgroundImage: themeBackgroundImageRef })
          : serverPreferences;
        const nextBackgroundUrl = themeBackgroundURL(theme);
        cacheThemePreferences(next);
        setPreferencesState(next);
        setBackgroundImageUrl(nextBackgroundUrl);
      } catch {
        // Theme settings require a logged-in session; login and public pages can keep local fallback values.
      }
    };
    void loadServerTheme();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = '';

    const resolveBackgroundImage = async () => {
      if (preferences.backgroundImage === themeServerBackgroundImageRef) {
        setBackgroundImageUrl((current) => current || '/api/v1/theme/background');
        return;
      }
      if (preferences.backgroundImage !== themeBackgroundImageRef) {
        setBackgroundImageUrl(preferences.backgroundImage);
        return;
      }
      try {
        const blob = await readThemeBackgroundImage();
        if (cancelled) return;
        if (!blob) {
          setBackgroundImageUrl('');
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setBackgroundImageUrl(objectUrl);
      } catch {
        if (!cancelled) setBackgroundImageUrl('');
      }
    };

    void resolveBackgroundImage();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [preferences.backgroundImage]);

  useEffect(() => {
    applyResolvedThemePreferences(preferences, backgroundImageUrl);
  }, [preferences, backgroundImageUrl]);

  const setPreferences = (next: ThemePreferences, resolvedBackgroundImage = '') => {
    const normalized = normalizeThemePreferences(next);
    cacheThemePreferences(normalized);
    setPreferencesState(normalized);
    const nextBackgroundUrl = normalized.backgroundImage === themeServerBackgroundImageRef ? resolvedBackgroundImage : normalized.backgroundImage;
    setBackgroundImageUrl(nextBackgroundUrl);
    window.dispatchEvent(new CustomEvent(themeUpdateEventName, { detail: { preferences: normalized, backgroundImageUrl: nextBackgroundUrl } }));
  };

  return [preferences, setPreferences, backgroundImageUrl] as const;
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => (typeof window === 'undefined' ? false : window.matchMedia(query).matches));

  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [query]);

  return matches;
}

function isEditableEventTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
}

function AppShell({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [commandOpen, setCommandOpen] = useState(false);
  const [commandQuery, setCommandQuery] = useState('');
  const [shortcutChordActive, setShortcutChordActive] = useState(false);
  const commandSearchRef = useRef<HTMLInputElement>(null);
  const commandOpenRef = useRef(commandOpen);
  const userReadyRef = useRef(false);
  const shortcutActionsRef = useRef<Record<string, () => void>>({});
  const shortcutChordRef = useRef(false);
  const shortcutChordTimerRef = useRef<number | null>(null);
  const filesRouteActive = location.pathname === workspaceFilesPath || location.pathname.startsWith('/preview/');
  const knowledgeRouteActive = location.pathname.startsWith(workspaceKnowledgePath);
  const manageRouteActive = location.pathname.startsWith(workspaceManagePath);
  const userQuery = useQuery({ queryKey: ['me'], queryFn: me, staleTime: userSessionStaleMs, retryOnMount: false });
  const commandResourcesEnabled = commandOpen && !!userQuery.data;
  const commandRecentQuery = useQuery({ queryKey: ['recent'], queryFn: listRecent, enabled: commandResourcesEnabled });
  const commandFavoritesQuery = useQuery({ queryKey: ['favorites'], queryFn: listFavorites, enabled: commandResourcesEnabled });
  const [recentCommandIds, setRecentCommandIds] = useState(() => readCommandHistory());
  commandOpenRef.current = commandOpen;
  userReadyRef.current = !!userQuery.data;
  const logoutMutation = useMutation({
    mutationFn: logout,
    onSettled: async () => {
      queryClient.clear();
      navigate('/login', { replace: true });
    }
  });
  const savedWorkspace = commandOpen ? readWorkspaceState() : null;
  const activeWorkspaceTab = savedWorkspace?.openTabs.find((tab) => tab.id === savedWorkspace.activeTabId) ?? savedWorkspace?.openTabs.at(-1);
  const [activeCommandIndex, setActiveCommandIndex] = useState(0);
  const goWorkspaceCommand = (workspaceCommand?: WorkspaceRouteCommand, initialSearch?: string) => {
    if (!workspaceCommand) {
      navigate(workspaceFilesPath);
      return;
    }
    if (workspaceCommand === 'knowledge') {
      navigate(workspaceKnowledgePath);
      return;
    }
    if (workspaceCommand === 'manage') {
      navigate(workspaceManagePath);
      return;
    }
    const state: WorkspaceRouteState = { workspaceCommand, initialSearch };
    navigate(workspaceFilesPath, { state });
  };
  const clearShortcutChord = () => {
    shortcutChordRef.current = false;
    setShortcutChordActive(false);
    if (shortcutChordTimerRef.current) {
      window.clearTimeout(shortcutChordTimerRef.current);
      shortcutChordTimerRef.current = null;
    }
  };
  const armShortcutChord = () => {
    shortcutChordRef.current = true;
    setShortcutChordActive(true);
    if (shortcutChordTimerRef.current) window.clearTimeout(shortcutChordTimerRef.current);
    shortcutChordTimerRef.current = window.setTimeout(clearShortcutChord, 3000);
  };
  const shortcutChordItems: Array<{ key: string; label: string; icon: ReactNode; action: () => void }> = [
    { key: 'H', label: '首页', icon: <Home size={15} />, action: () => navigate('/') },
    { key: 'W', label: '资料', icon: <ListTree size={15} />, action: () => goWorkspaceCommand() },
    { key: 'S', label: '设置', icon: <Settings size={15} />, action: () => navigate('/settings') }
  ];
  shortcutActionsRef.current = shortcutChordItems.reduce<Record<string, () => void>>((actions, item) => {
    actions[item.key.toLowerCase()] = item.action;
    return actions;
  }, {});
  const runShortcutChord = (key: string) => {
    const action = shortcutActionsRef.current[key.toLowerCase()];
    if (!action) return false;
    action();
    clearShortcutChord();
    return true;
  };
  type CommandPaletteGroup = 'recent' | 'workflow' | 'resource' | 'search';
  type CommandPaletteItem = { id: string; group: CommandPaletteGroup; label: string; description: string; shortcut?: string; icon: ReactNode; action: () => void };
  const commandGroupLabels: Record<CommandPaletteGroup, string> = {
    recent: '最近使用',
    workflow: '工作流',
    resource: '学习资料',
    search: '全库搜索'
  };
  const openResourceFromCommand = (result: SearchResult) => {
    const resource = result.resource;
    if (result.external_link) {
      void recordRecent(resource.id, 'open');
      window.open(result.external_link.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (resource.type === 'directory') {
      saveWorkspaceState({
        parent: { id: resource.id, path: resource.path, title: resource.name },
        parentHistory: [],
        openTabs: savedWorkspace?.openTabs ?? [],
        activeTabId: savedWorkspace?.activeTabId ?? '',
        mobilePanel: 'files'
      });
      void recordRecent(resource.id, 'open').then(() => queryClient.invalidateQueries({ queryKey: ['recent'] }));
      navigate(workspaceFilesPath);
      return;
    }
    if (resource.type === 'file') {
      const currentTabs = savedWorkspace?.openTabs ?? [];
      const openTabs = [...currentTabs.filter((tab) => tab.id !== resource.id), resource].slice(-8);
      saveWorkspaceState({
        parent: { path: resource.parent_path, title: resource.parent_path ? resource.parent_path.split('/').at(-1) || '资料库' : '资料库' },
        parentHistory: [],
        openTabs,
        activeTabId: resource.id,
        mobilePanel: 'preview'
      });
      void prefetchResourcePreview(queryClient, resource.id);
      void recordRecent(resource.id, 'preview').then(() => queryClient.invalidateQueries({ queryKey: ['recent'] }));
      navigate(workspaceFilesPath, { state: { workspaceCommand: 'preview' } });
    }
  };
  const commandSearchText = commandQuery.trim();
  const normalizedCommandQuery = commandSearchText.toLowerCase();
  const commandPageItems: CommandPaletteItem[] = [
    {
      id: 'home',
      group: 'workflow',
      label: '学习首页',
      description: '回到继续学习、收藏和课程入口',
      shortcut: 'G H',
      icon: <Home size={18} />,
      action: () => navigate('/')
    },
    {
      id: 'workspace',
      group: 'workflow',
      label: '打开资料库',
      description: savedWorkspace?.parent.path ? `回到 ${savedWorkspace.parent.title}` : '浏览目录、导入和预览资料',
      shortcut: 'G W',
      icon: <ListTree size={18} />,
      action: () => goWorkspaceCommand()
    },
    {
      id: 'workspace-search',
      group: 'workflow',
      label: '搜索全部资料',
      description: '聚焦工作台全库搜索框',
      shortcut: 'Ctrl K',
      icon: <Search size={18} />,
      action: () => goWorkspaceCommand('search')
    },
    {
      id: 'knowledge',
      group: 'workflow',
      label: '知识整理',
      description: '查看收藏、最近、标签、外链和合集',
      icon: <BookOpen size={18} />,
      action: () => goWorkspaceCommand('knowledge')
    },
    {
      id: 'preview',
      group: 'workflow',
      label: '继续预览',
      description: activeWorkspaceTab ? `回到 ${activeWorkspaceTab.name}` : '打开工作台预览面板',
      icon: <Eye size={18} />,
      action: () => goWorkspaceCommand('preview')
    },
    {
      id: 'manage',
      group: 'workflow',
      label: '安全管理',
      description: '管理分享链接、回收站和批量操作',
      icon: <ShieldCheck size={18} />,
      action: () => goWorkspaceCommand('manage')
    },
    {
      id: 'settings',
      group: 'workflow',
      label: '外观设置',
      description: '调整主题、背景、玻璃和默认视图',
      shortcut: 'G S',
      icon: <Settings size={18} />,
      action: () => navigate('/settings')
    },
    {
      id: 'legacy',
      group: 'workflow',
      label: '打开旧版目录',
      description: '进入兼容版课程目录和原上传管理',
      icon: <ArchiveRestore size={18} />,
      action: () => {
        window.location.assign('/legacy/');
      }
    }
  ];
  const commandRecent = (commandRecentQuery.data ?? []).filter((result) => result.resource.type === 'file' || result.resource.type === 'directory' || !!result.external_link).slice(0, 4);
  const recentIds = new Set(commandRecent.map((result) => result.resource.id));
  const commandFavorites = (commandFavoritesQuery.data ?? [])
    .filter((result) => !recentIds.has(result.resource.id) && (result.resource.type === 'file' || result.resource.type === 'directory' || !!result.external_link))
    .slice(0, 4);
  const commandResourceItems: CommandPaletteItem[] = [
    ...commandRecent.map((result) => ({ result, source: '最近' })),
    ...commandFavorites.map((result) => ({ result, source: '收藏' }))
  ].map(({ result, source }) => {
    const resource = result.resource;
    const descriptionParts = [source, resourceKindLabel(resource), resource.path || result.snippet].filter(Boolean);
    return {
      id: `resource-${source}-${resource.id}`,
      group: 'resource',
      label: resource.name,
      description: descriptionParts.join(' · '),
      icon: resource.type === 'directory' ? <Folder size={18} /> : resource.type === 'external_link' || result.external_link ? <ExternalLink size={18} /> : <FileText size={18} />,
      action: () => openResourceFromCommand(result)
    };
  });
  const commandSearchFallbackItem: CommandPaletteItem | null = commandSearchText ? {
    id: 'workspace-search-current-query',
    group: 'search',
    label: `搜索资料库：${commandSearchText}`,
    description: '在工作台全库搜索中查找文件、标签、合集和外链',
    icon: <Search size={18} />,
    action: () => goWorkspaceCommand('search', commandSearchText)
  } : null;
  const commandItems = [...commandPageItems, ...commandResourceItems, ...(commandSearchFallbackItem ? [commandSearchFallbackItem] : [])];
  const commandResourcesLoading = commandResourcesEnabled && (commandRecentQuery.isLoading || commandFavoritesQuery.isLoading);
  const commandResourcesError = commandRecentQuery.isError || commandFavoritesQuery.isError;
  const filteredCommandItems = normalizedCommandQuery
    ? commandItems.filter((item) => `${item.label} ${item.description}`.toLowerCase().includes(normalizedCommandQuery))
    : commandItems;
  const recentCommandItems = normalizedCommandQuery
    ? []
    : recentCommandIds.map((id) => filteredCommandItems.find((item) => item.id === id)).filter((item): item is CommandPaletteItem => !!item);
  const recentVisibleIds = new Set(recentCommandItems.map((item) => item.id));
  const visibleCommandItems = normalizedCommandQuery
    ? filteredCommandItems
    : [...recentCommandItems, ...filteredCommandItems.filter((item) => !recentVisibleIds.has(item.id))];
  const activeCommand = visibleCommandItems[activeCommandIndex] ?? null;
  const activeCommandElementId = activeCommand ? `command-option-${activeCommand.id}` : undefined;
  const firstVisibleCommandId = visibleCommandItems[0]?.id ?? '';
  const visibleCommandGroups = (['recent', 'workflow', 'resource', 'search'] as CommandPaletteGroup[])
    .map((group) => ({
      group,
      label: commandGroupLabels[group],
      items: visibleCommandItems.map((item, index) => ({ item, index })).filter(({ item }) => {
        if (group === 'recent') return recentVisibleIds.has(item.id);
        return item.group === group && !recentVisibleIds.has(item.id);
      })
    }))
    .filter((group) => group.items.length > 0);
  const rememberCommand = (item: CommandPaletteItem) => {
    if (item.group === 'search') return;
    setRecentCommandIds((current) => {
      const next = [item.id, ...current.filter((id) => id !== item.id)].slice(0, 5);
      saveCommandHistory(next);
      return next;
    });
  };
  const runCommand = (item: CommandPaletteItem) => {
    rememberCommand(item);
    item.action();
    setCommandOpen(false);
    setCommandQuery('');
    setActiveCommandIndex(0);
  };
  const clearRecentCommands = () => {
    clearCommandHistory();
    setRecentCommandIds([]);
    setActiveCommandIndex(0);
    window.requestAnimationFrame(() => commandSearchRef.current?.focus());
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommandOpen((open) => !open);
        return;
      }
      if (event.key === 'Escape') {
        setCommandOpen(false);
        clearShortcutChord();
        return;
      }
      if (!userReadyRef.current || commandOpenRef.current || event.altKey || event.ctrlKey || event.metaKey) return;
      const eventKey = event.key.toLowerCase();
      const codeKey = event.code.toLowerCase().startsWith('key') ? event.code.slice(3).toLowerCase() : '';
      const key = codeKey || eventKey;
      if (shortcutChordRef.current) {
        if (runShortcutChord(key)) {
          event.preventDefault();
          return;
        }
        if (key === 'g') {
          event.preventDefault();
          armShortcutChord();
          return;
        }
        clearShortcutChord();
        return;
      }
      if (isEditableEventTarget(event.target)) return;
      if (key === 'g') {
        event.preventDefault();
        armShortcutChord();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (shortcutChordTimerRef.current) window.clearTimeout(shortcutChordTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!commandOpen) {
      setCommandQuery('');
      setActiveCommandIndex(0);
      return;
    }
    window.requestAnimationFrame(() => commandSearchRef.current?.focus());
  }, [commandOpen]);

  useEffect(() => {
    if (!commandOpen) return;
    setActiveCommandIndex(0);
  }, [commandOpen, commandQuery, firstVisibleCommandId]);

  useEffect(() => {
    if (visibleCommandItems.length === 0) {
      if (activeCommandIndex !== 0) setActiveCommandIndex(0);
      return;
    }
    if (activeCommandIndex >= visibleCommandItems.length) {
      setActiveCommandIndex(visibleCommandItems.length - 1);
    }
  }, [activeCommandIndex, visibleCommandItems.length]);

  useEffect(() => {
    if (!commandOpen || !activeCommandElementId) return;
    document.getElementById(activeCommandElementId)?.scrollIntoView({ block: 'nearest' });
  }, [activeCommandElementId, commandOpen]);

  return (
    <div className="app-frame">
      <header className="topbar">
        <Link className="brand" to="/">
          <span className="brand-mark">芝</span>
          <span>
            <strong>芝士酷</strong>
            <small>学习资料工作台</small>
          </span>
        </Link>
        <nav className="topbar-nav" aria-label="主导航">
          <NavLink to="/" title="首页" end>
            <Home size={18} />
            <span>首页</span>
          </NavLink>
          <Link to={workspaceFilesPath} title="资料库" className={filesRouteActive ? 'active' : undefined} aria-current={filesRouteActive ? 'page' : undefined}>
            <ListTree size={18} />
            <span>资料</span>
          </Link>
          <Link to={workspaceKnowledgePath} title="知识整理" className={knowledgeRouteActive ? 'active' : undefined} aria-current={knowledgeRouteActive ? 'page' : undefined}>
            <BookOpen size={18} />
            <span>知识</span>
          </Link>
          <Link to={workspaceManagePath} title="安全管理" className={manageRouteActive ? 'active' : undefined} aria-current={manageRouteActive ? 'page' : undefined}>
            <ShieldCheck size={18} />
            <span>管理</span>
          </Link>
          <NavLink to="/settings" title="设置">
            <Settings size={18} />
            <span>设置</span>
          </NavLink>
          <a href="/legacy/" title="旧版目录">
            <ArchiveRestore size={18} />
            <span>旧版</span>
          </a>
        </nav>
        <div className="user-area">
          {userQuery.data ? (
            <button className="quick-command-trigger" type="button" title="快速命令" aria-label="打开快速命令" onClick={() => setCommandOpen(true)}>
              <Command size={16} />
              <span>快速</span>
              <kbd>Ctrl K</kbd>
            </button>
          ) : null}
          {userQuery.data ? <span className="user-pill">{userQuery.data.display_name || userQuery.data.username}</span> : null}
          {userQuery.data ? (
            <button className="icon-button" type="button" title="退出登录" onClick={() => logoutMutation.mutate()}>
              <LogOut size={18} />
            </button>
          ) : null}
          {!userQuery.data && !userQuery.isLoading ? (
            <Link className="text-action" to="/login" title="登录后可上传和整理资料">
              <KeyRound size={15} />
              <span>登录</span>
            </Link>
          ) : null}
        </div>
      </header>
      {shortcutChordActive && userQuery.data && !commandOpen ? (
        <div className="shortcut-chord-hint" role="status" aria-live="polite">
          <span className="shortcut-chord-prefix">G</span>
          <div className="shortcut-chord-copy">
            <strong>继续按一个键</strong>
            <div className="shortcut-chord-options">
              {shortcutChordItems.map((item) => (
                <span key={item.key}>
                  <kbd>{item.key}</kbd>
                  <span className="shortcut-chord-option-icon" aria-hidden="true">{item.icon}</span>
                  <span>{item.label}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : null}
      {commandOpen && userQuery.data ? (
        <div className="command-overlay" role="presentation" onMouseDown={() => setCommandOpen(false)}>
          <section className="command-palette" role="dialog" aria-modal="true" aria-label="快速命令" onMouseDown={(event) => event.stopPropagation()}>
            <div className="command-search">
              <Search size={18} />
              <input
                ref={commandSearchRef}
                value={commandQuery}
                onChange={(event) => setCommandQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown' && visibleCommandItems.length > 0) {
                    event.preventDefault();
                    setActiveCommandIndex((current) => (current + 1) % visibleCommandItems.length);
                    return;
                  }
                  if (event.key === 'ArrowUp' && visibleCommandItems.length > 0) {
                    event.preventDefault();
                    setActiveCommandIndex((current) => (current - 1 + visibleCommandItems.length) % visibleCommandItems.length);
                    return;
                  }
                  if (event.key === 'Home' && visibleCommandItems.length > 0) {
                    event.preventDefault();
                    setActiveCommandIndex(0);
                    return;
                  }
                  if (event.key === 'End' && visibleCommandItems.length > 0) {
                    event.preventDefault();
                    setActiveCommandIndex(visibleCommandItems.length - 1);
                    return;
                  }
                  if (event.key === 'Enter' && activeCommand) {
                    event.preventDefault();
                    runCommand(activeCommand);
                  }
                }}
                placeholder="输入命令、资料或页面名称"
                aria-label="搜索快速命令"
                aria-controls="command-listbox"
                aria-activedescendant={activeCommandElementId}
              />
              <kbd>Esc</kbd>
              <button className="command-close" type="button" title="关闭" aria-label="关闭快速命令" onClick={() => setCommandOpen(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="command-summary" aria-live="polite">
              <span>{commandResourcesLoading ? '正在同步最近和收藏' : `可用 ${visibleCommandItems.length} 项命令`}</span>
              {commandResourcesError ? <button type="button" onClick={() => { void commandRecentQuery.refetch(); void commandFavoritesQuery.refetch(); }}>重试资源</button> : null}
            </div>
            <div id="command-listbox" className="command-list" aria-label="可用命令">
              {visibleCommandGroups.map((group) => (
                <section key={group.group} className={`command-group command-group-${group.group}`} aria-label={group.label}>
                  <div className="command-group-label">
                    <span>{group.label}</span>
                    <span className="command-group-tools">
                      <small>{group.items.length}</small>
                      {group.group === 'recent' ? (
                        <button type="button" className="command-group-action" aria-label="清除最近使用" onClick={clearRecentCommands}>
                          清除
                        </button>
                      ) : null}
                    </span>
                  </div>
                  {group.items.map(({ item, index }) => (
                    <button
                      key={item.id}
                      id={`command-option-${item.id}`}
                      className={index === activeCommandIndex ? 'command-item active' : 'command-item'}
                      type="button"
                      aria-selected={index === activeCommandIndex}
                      onClick={() => runCommand(item)}
                      onFocus={() => setActiveCommandIndex(index)}
                    >
                      <span className="command-item-icon" aria-hidden="true">{item.icon}</span>
                      <span className="command-item-copy">
                        <strong>{item.label}</strong>
                        <small>{item.description}</small>
                      </span>
                      {item.shortcut ? <kbd>{item.shortcut}</kbd> : null}
                    </button>
                  ))}
                </section>
              ))}
              {visibleCommandItems.length === 0 ? (
                <div className="command-empty">
                  <Search size={18} />
                  <span>没有匹配的命令</span>
                </div>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}
      {children}
    </div>
  );
}

// 游客态：JANESTRA_PUBLIC_READ 打开后，未登录访客可以只读浏览资料。
// 写操作与个人数据(收藏/最近/管理/设置)仍由后端 401 兜底，这里只负责隐藏入口。
const GuestContext = createContext(false);

function useIsGuest() {
  return useContext(GuestContext);
}

function RequireAuth({ children, allowGuest = false }: { children: React.ReactNode; allowGuest?: boolean }) {
  const userQuery = useQuery({ queryKey: ['me'], queryFn: me, staleTime: userSessionStaleMs, retryOnMount: false });
  if (userQuery.isLoading) {
    return (
      <AuthGateState
        icon={<RefreshCw className="state-spin" size={25} />}
        eyebrow="默认私有"
        title="正在确认登录状态"
        description="正在确认当前会话和账号权限。资料库内容会在认证通过后显示。"
      />
    );
  }
  if (userQuery.isError) {
    if (userQuery.error instanceof APIError && userQuery.error.status === 401) {
      if (allowGuest) {
        return <GuestContext.Provider value={true}>{children}</GuestContext.Provider>;
      }
      return <Navigate to="/login" replace />;
    }
    const message = errorMessage(userQuery.error) || '认证服务暂时不可用';
    return (
      <AuthGateState
        icon={<ShieldCheck size={25} />}
        eyebrow="认证暂不可用"
        title="无法确认登录状态"
        description={`${message}。可以重新确认会话，或回到登录页重新进入。`}
        actions={(
          <>
            <button className="primary-button" type="button" onClick={() => userQuery.refetch()} disabled={userQuery.isFetching}>
              <RefreshCw className={userQuery.isFetching ? 'state-spin' : ''} size={16} />
              <span>{userQuery.isFetching ? '正在确认' : '重新确认'}</span>
            </button>
            <Link className="text-action" to="/login">
              <KeyRound size={15} />
              <span>去登录页</span>
            </Link>
          </>
        )}
      />
    );
  }
  return <>{children}</>;
}

function AuthGateState({ icon, eyebrow, title, description, actions }: { icon: ReactNode; eyebrow: string; title: string; description: string; actions?: ReactNode }) {
  return (
    <main className="auth-gate-screen">
      <section className="auth-gate-card">
        <div className="brand auth-gate-brand">
          <span className="brand-mark">芝</span>
          <span>
            <strong>芝士酷</strong>
            <small>学习资料工作台</small>
          </span>
        </div>
        <div className="auth-gate-state">
          <span className="auth-gate-icon" aria-hidden="true">{icon}</span>
          <span className="home-eyebrow">{eyebrow}</span>
          <h1>{title}</h1>
          <p>{description}</p>
          {actions ? <div className="auth-gate-actions">{actions}</div> : null}
        </div>
      </section>
    </main>
  );
}

function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const mutation = useMutation({
    mutationFn: () => login(username, password),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      navigate('/workspace', { replace: true });
    }
  });
  const error = mutation.error instanceof APIError ? mutation.error.message : mutation.error ? '登录失败' : '';

  return (
    <main className="login-screen">
      <section className="login-layout" aria-labelledby="login-title">
        <div className="login-intro">
          <div className="login-brand">
            <span className="brand-mark">芝</span>
            <span>
              <strong>芝士酷</strong>
              <small>学习资料工作台</small>
            </span>
          </div>
          <div className="login-copy">
            <span className="home-eyebrow">默认私有</span>
            <h1 id="login-title">进入个人资料库</h1>
            <p>资料浏览、知识整理和分享管理分开组织，所有资料默认保持私有。</p>
          </div>
          <div className="login-feature-grid" aria-label="工作台能力">
            <span>
              <Search size={17} />
              <strong>搜索资料</strong>
              <small>标题、标签、合集和全文索引</small>
            </span>
            <span>
              <BookOpen size={17} />
              <strong>继续学习</strong>
              <small>最近打开和重点收藏直达</small>
            </span>
            <span>
              <ShieldCheck size={17} />
              <strong>受控分享</strong>
              <small>匿名只进入授权范围</small>
            </span>
          </div>
        </div>

        <section className="login-panel" aria-label="登录表单">
          <div className="login-heading">
            <KeyRound size={21} />
            <div>
              <h2>账号登录</h2>
              <p>使用 owner/admin 账号继续</p>
            </div>
          </div>
          <form
            className="login-form"
            onSubmit={(event) => {
              event.preventDefault();
              mutation.mutate();
            }}
          >
            <label>
              账号
              <input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" />
            </label>
            <label>
              密码
              <input
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                type="password"
                autoComplete="current-password"
              />
            </label>
            {error ? (
              <div className="login-feedback error" role="alert">
                <ShieldCheck size={16} />
                <span>{error}</span>
              </div>
            ) : null}
            <button className="primary-button" type="submit" disabled={mutation.isPending || !username || !password}>
              <LogIn size={18} />
              <span>{mutation.isPending ? '正在登录' : '登录'}</span>
            </button>
          </form>
        </section>
      </section>
    </main>
  );
}

function HomePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [homeSearch, setHomeSearch] = useState('');
  const isGuest = useIsGuest();
  const rootQuery = useQuery({ queryKey: ['resources', 'root'], queryFn: () => listResources() });
  const recentQuery = useQuery({ queryKey: ['recent'], queryFn: listRecent, enabled: !isGuest });
  const favoritesQuery = useQuery({ queryKey: ['favorites'], queryFn: listFavorites, enabled: !isGuest });
  const homeSearchText = homeSearch.trim();
  const homeSearchQuery = useQuery({
    queryKey: ['home-search', homeSearchText],
    queryFn: () => searchResources({ q: homeSearchText, limit: 10 }),
    enabled: homeSearchText.length > 0
  });
  const resources = rootQuery.data?.resources ?? [];
  const folders = resources.filter((item) => item.type === 'directory').length;
  const files = resources.filter((item) => item.type === 'file').length;
  const topFolders = resources.filter((item) => item.type === 'directory').slice(0, 6);
  const recent = recentQuery.data ?? [];
  const favorites = favoritesQuery.data ?? [];
  const searchResults = homeSearchQuery.data ?? [];
  const favoriteMutation = useMutation({
    mutationFn: ({ resourceId, favorite }: { resourceId: string; favorite: boolean }) => setFavorite(resourceId, favorite),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['favorites'] }),
        queryClient.invalidateQueries({ queryKey: ['home-search'] })
      ]);
    }
  });
  const openResultFromHome = (result: SearchResult) => {
    if (result.external_link) {
      void recordRecent(result.resource.id, 'open');
      window.open(result.external_link.url, '_blank', 'noopener,noreferrer');
      return;
    }
    openFromHome(result.resource);
  };
  function openFromHome(resource: Resource) {
    if (resource.type === 'external_link') return;
    const parentState = resource.type === 'directory'
      ? { id: resource.id, path: resource.path, title: resource.name }
      : { path: resource.parent_path, title: resource.parent_path ? resource.parent_path.split('/').at(-1) || '资料库' : '资料库' };
    const openTabs = resource.type === 'file' ? [resource] : [];
    saveWorkspaceState({
      parent: parentState,
      parentHistory: [],
      openTabs,
      activeTabId: resource.type === 'file' ? resource.id : '',
      mobilePanel: resource.type === 'file' ? 'preview' : 'files'
    });
    if (resource.type === 'file') void recordRecent(resource.id, 'preview');
    navigate('/workspace');
  }

  return (
    <AppShell>
      <main className="home-dashboard">
        <section className="home-command-panel" aria-labelledby="home-title">
          <div className="home-command-copy">
            <span className="home-eyebrow">个人资料库</span>
            <h1 id="home-title">学习工作台</h1>
            <p>{rootQuery.isLoading ? '正在加载资料概览' : `${folders} 个课程入口，${files} 个顶层文件`}</p>
          </div>
          <div className="home-command-stack">
            <label className="home-search-command">
              <Search size={19} />
              <input
                value={homeSearch}
                onChange={(event) => setHomeSearch(event.target.value)}
                placeholder="搜索资料、标签、合集、链接"
                aria-label="搜索资料"
                autoComplete="off"
              />
            </label>
            <div className="home-command-actions">
              <Link className="primary-button" to="/workspace">
                <BookOpen size={18} />
                <span>进入工作台</span>
              </Link>
              <Link className="text-action" to="/settings">
                <Settings size={16} />
                <span>外观设置</span>
              </Link>
            </div>
          </div>
        </section>

        {rootQuery.isError ? (
          <WorkspaceStateCard
            className="home-state-card home-state-card-wide"
            icon={<RefreshCw size={22} />}
            eyebrow="资料概览"
            title="资料概览加载失败"
            description="首页暂时没有拿到资料库摘要，工作台和权限边界不会因此改变。"
            actions={
              <>
                <button className="text-action" type="button" onClick={() => void queryClient.invalidateQueries({ queryKey: ['resources', 'root'] })}>
                  <RefreshCw size={15} />
                  <span>重试加载</span>
                </button>
                <Link className="text-action" to="/workspace">
                  <ListTree size={15} />
                  <span>进入工作台</span>
                </Link>
              </>
            }
          />
        ) : null}

        <section className="home-overview-grid" aria-label="资料概览">
          <HomeMetric icon={<Folder size={18} />} label="顶层文件夹" value={folders} detail="课程入口" />
          <HomeMetric icon={<FileText size={18} />} label="顶层文件" value={files} detail="根目录资料" />
          <HomeMetric icon={<Eye size={18} />} label="最近打开" value={recent.length} detail="继续学习" />
          <HomeMetric icon={<Star size={18} />} label="收藏资料" value={favorites.length} detail="重点资料" />
        </section>

        {homeSearchText ? (
          <section className="home-panel home-search-results">
            <div className="section-heading">
              <h2>搜索结果</h2>
              <span className="home-section-meta">{homeSearchQuery.isLoading ? '搜索中' : `${searchResults.length} 项`}</span>
            </div>
            <div className="quiet-list">
              {searchResults.map((result) => (
                <SearchResultLine
                  key={result.resource.id}
                  result={result}
                  onOpen={() => openResultFromHome(result)}
                  onToggleFavorite={() => {
                    if (isGuest) {
                      navigate('/login');
                      return;
                    }
                    favoriteMutation.mutate({ resourceId: result.resource.id, favorite: !result.favorite });
                  }}
                />
              ))}
              {homeSearchQuery.isLoading ? (
                <WorkspaceStateCard
                  className="home-state-card"
                  icon={<RefreshCw className="state-spin" size={22} />}
                  eyebrow="首页搜索"
                  title="正在搜索资料"
                  description="芝士酷正在从文件、标签、合集和外链里整理匹配结果。"
                />
              ) : null}
              {homeSearchQuery.isError ? (
                <WorkspaceStateCard
                  className="home-state-card"
                  icon={<RefreshCw size={22} />}
                  eyebrow="首页搜索"
                  title="搜索失败"
                  description="搜索服务暂时不可用，可以保留关键词重试或进入工作台继续浏览。"
                  actions={
                    <>
                      <button className="text-action" type="button" onClick={() => void queryClient.invalidateQueries({ queryKey: ['home-search', homeSearchText] })}>
                        <RefreshCw size={15} />
                        <span>重试搜索</span>
                      </button>
                      <button className="text-action" type="button" onClick={() => setHomeSearch('')}>
                        <X size={15} />
                        <span>清空搜索</span>
                      </button>
                    </>
                  }
                />
              ) : null}
              {!homeSearchQuery.isLoading && !homeSearchQuery.isError && searchResults.length === 0 ? (
                <WorkspaceStateCard
                  className="home-state-card"
                  icon={<Search size={22} />}
                  eyebrow="没有结果"
                  title="没有搜索到资料"
                  description="换一个关键词，或进入工作台按目录、标签和收藏继续整理。"
                  actions={
                    <>
                      <button className="text-action" type="button" onClick={() => setHomeSearch('')}>
                        <X size={15} />
                        <span>清空搜索</span>
                      </button>
                      <Link className="text-action" to="/workspace">
                        <ListTree size={15} />
                        <span>打开资料库</span>
                      </Link>
                    </>
                  }
                />
              ) : null}
            </div>
          </section>
        ) : null}

        <section className="home-main-grid" aria-label="学习资料入口">
          <section className="home-panel home-panel-large">
            <div className="section-heading">
              <h2>继续学习</h2>
              <Link to="/workspace">全部资料</Link>
            </div>
            <div className="quiet-list">
              {recent.slice(0, 5).map((item) => (
                <ResourceLine key={item.resource.id} resource={item.resource} compact onOpen={() => openResultFromHome(item)} />
              ))}
              {recentQuery.isLoading ? (
                <WorkspaceStateCard
                  className="home-state-card"
                  icon={<RefreshCw className="state-spin" size={22} />}
                  eyebrow="继续学习"
                  title="正在加载最近打开"
                  description="最近阅读记录会按当前账号独立整理。"
                />
              ) : null}
              {!recentQuery.isLoading && recent.length === 0 ? (
                <WorkspaceStateCard
                  className="home-state-card"
                  icon={<BookOpen size={22} />}
                  eyebrow="继续学习"
                  title="还没有最近打开"
                  description="从工作台打开一个文件后，这里会自动出现继续阅读入口。"
                  actions={
                    <Link className="text-action" to="/workspace">
                      <ListTree size={15} />
                      <span>进入工作台</span>
                    </Link>
                  }
                />
              ) : null}
            </div>
          </section>

          <section className="home-panel">
            <div className="section-heading">
              <h2>收藏资料</h2>
              <Link to="/workspace">进入工作台</Link>
            </div>
            <div className="quiet-list">
              {favorites.slice(0, 5).map((item) => (
                <ResourceLine key={item.resource.id} resource={item.resource} compact onOpen={() => openResultFromHome(item)} />
              ))}
              {favoritesQuery.isLoading ? (
                <WorkspaceStateCard
                  className="home-state-card"
                  icon={<RefreshCw className="state-spin" size={22} />}
                  eyebrow="收藏资料"
                  title="正在加载收藏"
                  description="重点资料会在当前账号下保持同步。"
                />
              ) : null}
              {!favoritesQuery.isLoading && favorites.length === 0 ? (
                <WorkspaceStateCard
                  className="home-state-card"
                  icon={<Star size={22} />}
                  eyebrow="收藏资料"
                  title="暂无收藏资料"
                  description="在工作台资源行或搜索结果里点星标，就能把重点资料放到这里。"
                  actions={
                    <Link className="text-action" to="/workspace">
                      <Star size={15} />
                      <span>去整理收藏</span>
                    </Link>
                  }
                />
              ) : null}
            </div>
          </section>
        </section>

        <section className="home-panel">
          <div className="section-heading">
            <h2>课程目录</h2>
            <Link to="/workspace">打开资料库</Link>
          </div>
          <div className="home-folder-grid">
            {topFolders.map((folder) => (
              <button key={folder.id} className="home-folder-card" type="button" onClick={() => openFromHome(folder)}>
                <span className="resource-icon">
                  <Folder size={20} />
                </span>
                <span className="home-folder-copy">
                  <strong>{folder.name}</strong>
                  <small>{directoryMetaText(folder)}</small>
                </span>
                <ChevronRight size={18} />
              </button>
            ))}
            {rootQuery.isLoading ? (
              <WorkspaceStateCard
                className="home-state-card home-folder-state"
                icon={<RefreshCw className="state-spin" size={22} />}
                eyebrow="课程目录"
                title="正在加载课程目录"
                description="根目录课程入口会在这里快速展开。"
              />
            ) : null}
            {!rootQuery.isLoading && topFolders.length === 0 ? (
              <WorkspaceStateCard
                className="home-state-card home-folder-state"
                icon={<FolderPlus size={22} />}
                eyebrow="课程目录"
                title="根目录还没有文件夹"
                description="进入工作台上传资料或新建文件夹后，课程入口会显示在这里。"
                actions={
                  <Link className="primary-button" to="/workspace">
                    <Upload size={16} />
                    <span>导入资料</span>
                  </Link>
                }
              />
            ) : null}
          </div>
        </section>
      </main>
    </AppShell>
  );
}

function HomeMetric({ icon, label, value, detail }: { icon: React.ReactNode; label: string; value: number; detail: string }) {
  return (
    <div className="home-metric">
      <span className="home-metric-icon">{icon}</span>
      <span className="home-metric-copy">
        <span>{label}</span>
        <small>{detail}</small>
      </span>
      <strong>{value}</strong>
    </div>
  );
}

function WorkspaceStateCard({
  icon,
  eyebrow,
  title,
  description,
  actions,
  compact = false,
  className = ''
}: {
  icon: React.ReactNode;
  eyebrow: string;
  title: string;
  description: string;
  actions?: React.ReactNode;
  compact?: boolean;
  className?: string;
}) {
  const classes = ['workspace-state-card', compact ? 'compact' : '', className].filter(Boolean).join(' ');
  return (
    <section className={classes}>
      <div className="workspace-state-icon">{icon}</div>
      <div className="workspace-state-copy">
        <span className="home-eyebrow">{eyebrow}</span>
        <strong>{title}</strong>
        <p>{description}</p>
        {actions ? <div className="workspace-state-actions">{actions}</div> : null}
      </div>
    </section>
  );
}

function SettingsFeedbackPanel({
  kind,
  title,
  description,
  onDismiss
}: {
  kind: SettingsFeedbackKind;
  title: string;
  description: string;
  onDismiss?: () => void;
}) {
  const icon = kind === 'busy'
    ? <RefreshCw className="state-spin" size={20} />
    : kind === 'error'
      ? <AlertTriangle size={20} />
      : kind === 'success'
        ? <CheckCircle2 size={20} />
        : <ImagePlus size={20} />;
  const eyebrow = kind === 'busy' ? '处理中' : kind === 'error' ? '需要处理' : kind === 'success' ? '已同步' : '待应用';
  return (
    <section className={`settings-feedback ${kind}`} role={kind === 'error' ? 'alert' : 'status'} aria-live="polite">
      <div className="settings-feedback-icon">{icon}</div>
      <div className="settings-feedback-copy">
        <span>{eyebrow}</span>
        <strong>{title}</strong>
        <p>{description}</p>
      </div>
      {onDismiss ? (
        <button className="icon-button" type="button" title="关闭提示" aria-label="关闭提示" onClick={onDismiss}>
          <X size={16} />
        </button>
      ) : null}
    </section>
  );
}

function SettingsPage() {
  const [preferences, savePreferences, activeBackgroundUrl] = useThemePreferences();
  const [draft, setDraft] = useState<ThemePreferences>(preferences);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [backgroundBusy, setBackgroundBusy] = useState(false);
  const [backgroundBusyLabel, setBackgroundBusyLabel] = useState('');
  const [pendingBackgroundBlob, setPendingBackgroundBlob] = useState<Blob | null>(null);
  const [pendingBackgroundUpload, setPendingBackgroundUpload] = useState<PendingThemeBackgroundUpload | null>(() => readPendingThemeBackgroundUpload());
  const [draftBackgroundUrl, setDraftBackgroundUrl] = useState(activeBackgroundUrl);
  const draftObjectUrlRef = useRef('');

  useEffect(() => {
    if (pendingBackgroundUpload || pendingBackgroundBlob || draftObjectUrlRef.current) return;
    setDraft(preferences);
    if (!draftObjectUrlRef.current) setDraftBackgroundUrl(activeBackgroundUrl);
  }, [preferences, activeBackgroundUrl, pendingBackgroundUpload, pendingBackgroundBlob]);

  useEffect(() => {
    if (pendingBackgroundUpload && !message && !error) {
      setMessage(`检测到已上传背景，点击保存设置后应用：${bytesLabel(pendingBackgroundUpload.size)}`);
    }
  }, [pendingBackgroundUpload, message, error]);

  useEffect(() => {
    return () => {
      if (draftObjectUrlRef.current) URL.revokeObjectURL(draftObjectUrlRef.current);
    };
  }, []);

  const setDraftBackgroundPreview = (url: string, owned: boolean) => {
    if (draftObjectUrlRef.current && draftObjectUrlRef.current !== url) {
      URL.revokeObjectURL(draftObjectUrlRef.current);
    }
    draftObjectUrlRef.current = owned ? url : '';
    setDraftBackgroundUrl(url);
  };

  const updateDraft = (patch: Partial<ThemePreferences>) => {
    setMessage('');
    setError('');
    setDraft((current) => normalizeThemePreferences({ ...current, ...patch }));
  };
  const selectedBackgroundPreset = activeBackgroundPreset(draft);
  const selectedModeLabel = themeModeLabels[draft.mode];
  const selectedViewLabel = viewModeLabels[draft.defaultView];
  const selectedPresetLabel = backgroundPresetOptions.find((option) => option.id === selectedBackgroundPreset)?.label ?? '自定义';
  const selectedAccentLabel = accentOptions.find((option) => option.value.toLowerCase() === draft.accent.toLowerCase())?.label ?? '自定义';
  const backgroundStatus = draft.backgroundImage ? '已启用背景' : '纯色背景';
  const pendingBackgroundMessage = message.includes('保存后立即应用') || message.includes('点击保存设置后应用');
  const settingsFeedback = backgroundBusy
    ? {
        kind: 'busy' as const,
        title: backgroundBusyLabel || '正在处理设置',
        description: '请保持当前页面打开，芝士酷会在完成后更新预览和账号外观。'
      }
    : error
      ? {
          kind: 'error' as const,
          title: '设置未保存',
          description: error
        }
      : message
        ? {
            kind: pendingBackgroundMessage ? 'info' as const : 'success' as const,
            title: pendingBackgroundMessage ? '背景等待应用' : message.includes('保存') ? '设置已保存' : message.includes('恢复') ? '已恢复默认' : '已同步',
            description: message
          }
        : null;
  const dismissSettingsFeedback = settingsFeedback && settingsFeedback.kind !== 'busy'
    ? () => {
        setMessage('');
        setError('');
      }
    : undefined;

  const save = async () => {
    try {
      setBackgroundBusy(true);
      setBackgroundBusyLabel(pendingBackgroundUpload ? '正在应用已上传背景' : pendingBackgroundBlob ? '正在保存背景' : '正在保存设置');
      let backgroundToUpload = pendingBackgroundBlob;
      if (!backgroundToUpload && draft.backgroundImage === themeBackgroundImageRef) {
        setBackgroundBusyLabel('正在迁移旧背景');
        backgroundToUpload = await readThemeBackgroundImageSafely();
      }
      if (backgroundToUpload) setBackgroundBusyLabel(`正在保存背景 ${bytesLabel(backgroundToUpload.size)}`);
      const useUploadedBackground = Boolean(pendingBackgroundUpload && !backgroundToUpload);
      const theme = await saveThemeSettings({
        preferences: themePreferencesToPayload(draft),
        background: backgroundToUpload,
        useUploadedBackground,
        uploadedBackgroundSize: pendingBackgroundUpload?.size,
        removeBackground: draft.backgroundImage === '' && !useUploadedBackground
      });
      const nextPreferences = themeSettingsToPreferences(theme);
      const nextBackgroundUrl = themeBackgroundURL(theme);
      savePreferences(nextPreferences, nextBackgroundUrl);
      setPendingBackgroundBlob(null);
      if (draftObjectUrlRef.current) {
        URL.revokeObjectURL(draftObjectUrlRef.current);
        draftObjectUrlRef.current = '';
      }
      cleanupLocalThemeBackgroundImage();
      setDraft(nextPreferences);
      setDraftBackgroundUrl(nextBackgroundUrl);
      setPendingBackgroundUpload(null);
      clearPendingThemeBackgroundUpload();
      setMessage('设置已保存到当前账号');
      setError('');
    } catch (err) {
      setError(errorMessage(err) || '保存失败');
      setMessage('');
    } finally {
      setBackgroundBusy(false);
      setBackgroundBusyLabel('');
    }
  };

  const reset = async () => {
    setBackgroundBusy(true);
    setBackgroundBusyLabel('正在恢复默认');
    setPendingBackgroundBlob(null);
    setPendingBackgroundUpload(null);
    clearPendingThemeBackgroundUpload();
    setDraftBackgroundPreview('', false);
    setDraft(defaultThemePreferences);
    try {
      const theme = await saveThemeSettings({
        preferences: themePreferencesToPayload(defaultThemePreferences),
        removeBackground: true
      });
      const nextPreferences = themeSettingsToPreferences(theme);
      cleanupLocalThemeBackgroundImage();
      savePreferences(nextPreferences, themeBackgroundURL(theme));
      setDraft(nextPreferences);
      setMessage('已恢复默认');
      setError('');
    } catch (err) {
      setError(errorMessage(err));
      setMessage('');
    } finally {
      setBackgroundBusy(false);
      setBackgroundBusyLabel('');
    }
  };

  const loadBackgroundImage = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('请选择图片文件');
      return;
    }
    setBackgroundBusy(true);
    setBackgroundBusyLabel('正在处理图片');
    setPendingBackgroundUpload(null);
    clearPendingThemeBackgroundUpload();
    setError('');
    setMessage('');
    try {
      const prepared = await prepareBackgroundImage(file);
      const previewUrl = URL.createObjectURL(prepared.blob);
      setDraftBackgroundPreview(previewUrl, true);
      setBackgroundBusyLabel(`正在上传背景 ${bytesLabel(prepared.storedBytes)}`);
      const upload = await uploadThemeBackground(prepared.blob);
      const pendingUpload = cachePendingThemeBackgroundUpload({ size: upload.background_size, uploadedAt: upload.uploaded_at });
      setPendingBackgroundBlob(null);
      setPendingBackgroundUpload(pendingUpload);
      setDraft((current) => normalizeThemePreferences({ ...current, backgroundImage: themeServerBackgroundImageRef }));
      setMessage(`背景已上传：${bytesLabel(prepared.sourceBytes)} → ${bytesLabel(upload.background_size)}，保存后立即应用`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBackgroundBusy(false);
      setBackgroundBusyLabel('');
    }
  };

  return (
    <AppShell>
      <main className="settings-layout">
        <section className="settings-heading settings-hero">
          <div className="settings-hero-copy">
            <span className="home-eyebrow">外观控制台</span>
            <h1>设置</h1>
            <p>工作台外观和默认浏览偏好</p>
            <div className="settings-summary" aria-label="当前外观摘要">
              <span>{selectedModeLabel}模式</span>
              <span>{selectedAccentLabel}强调色</span>
              <span>{backgroundStatus}</span>
              <span>{selectedPresetLabel}</span>
              <span>{selectedViewLabel}视图</span>
            </div>
          </div>
          <div className="settings-actions">
            <button className="text-action" type="button" onClick={() => void reset()} disabled={backgroundBusy}>
              <RotateCcw size={15} />
              <span>恢复默认</span>
            </button>
            <button className="primary-button" type="button" onClick={() => void save()} disabled={backgroundBusy}>
              <Save size={16} />
              <span>{backgroundBusy ? backgroundBusyLabel || '处理中' : '保存设置'}</span>
            </button>
          </div>
        </section>
        {settingsFeedback ? (
          <SettingsFeedbackPanel
            kind={settingsFeedback.kind}
            title={settingsFeedback.title}
            description={settingsFeedback.description}
            onDismiss={dismissSettingsFeedback}
          />
        ) : null}
        <section className="settings-grid">
          <div className="settings-panel">
            <div className="settings-panel-title">
              <div className="mini-heading">
                <Palette size={16} />
                <span>主题</span>
              </div>
              <p>选择界面色调、强调色和资料浏览默认密度。</p>
            </div>
            <div className="setting-block">
              <div className="setting-block-heading">
                <span>模式</span>
                <small>选择整体色温和界面边界强度。</small>
              </div>
              <div className="theme-mode-options" role="group" aria-label="主题模式">
                {themeModeOptions.map((option) => (
                  <button
                    key={option.mode}
                    className={draft.mode === option.mode ? 'selected' : ''}
                    type="button"
                    aria-pressed={draft.mode === option.mode}
                    title={themeModeDescriptions[option.mode]}
                    onClick={() => updateDraft({ mode: option.mode })}
                  >
                    <span className="theme-mode-sample" style={{ '--mode-accent': option.accent } as CSSProperties} aria-hidden="true">
                      <span />
                      <span />
                    </span>
                    <span className="theme-mode-copy">
                      <strong>{themeModeLabels[option.mode]}</strong>
                      <small>{themeModeDescriptions[option.mode]}</small>
                    </span>
                    {draft.mode === option.mode ? <CheckCircle2 size={15} /> : null}
                  </button>
                ))}
              </div>
            </div>
            <div className="setting-block setting-block-compact">
              <div className="setting-block-heading">
                <span>主题色</span>
                <small>当前：{selectedAccentLabel}</small>
              </div>
              <div className="color-options" role="group" aria-label="主题色">
                {accentOptions.map((option) => (
                  <button
                    key={option.value}
                    className={draft.accent.toLowerCase() === option.value.toLowerCase() ? 'selected' : ''}
                    type="button"
                    aria-label={`${option.label}主题色`}
                    aria-pressed={draft.accent.toLowerCase() === option.value.toLowerCase()}
                    title={`${option.label} ${option.value}`}
                    style={{ background: option.value }}
                    onClick={() => updateDraft({ accent: option.value })}
                  >
                    {draft.accent.toLowerCase() === option.value.toLowerCase() ? <CheckCircle2 size={15} /> : null}
                  </button>
                ))}
              </div>
            </div>
            <label className="setting-row">
              <span>默认视图</span>
              <select value={draft.defaultView} onChange={(event) => updateDraft({ defaultView: event.target.value as ViewMode })}>
                <option value="list">清单</option>
                <option value="grid">卡片</option>
                <option value="compact">紧凑</option>
              </select>
            </label>
          </div>

          <div className="settings-panel">
            <div className="settings-panel-title">
              <div className="mini-heading">
                <ImagePlus size={16} />
                <span>背景</span>
              </div>
              <p>上传个人背景，并调节遮罩、虚化和玻璃面板强度。</p>
            </div>
            <div className="background-upload-card">
              <label className="upload-background">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => {
                    void loadBackgroundImage(event.target.files?.[0]);
                    event.currentTarget.value = '';
                  }}
                />
                <span className="background-upload-icon" aria-hidden="true">
                  <ImagePlus size={18} />
                </span>
                <span className="background-upload-copy">
                  <strong>{backgroundBusy ? backgroundBusyLabel || '处理中' : draft.backgroundImage ? '更换背景' : '选择图片'}</strong>
                  <small>自动压缩并保存到当前账号，未保存前只影响预览。</small>
                </span>
              </label>
              {draft.backgroundImage ? (
                <button
                  className="text-action background-remove-action"
                  type="button"
                  onClick={() => {
                    setPendingBackgroundBlob(null);
                    setPendingBackgroundUpload(null);
                    clearPendingThemeBackgroundUpload();
                    setDraftBackgroundPreview('', false);
                    updateDraft({ backgroundImage: '' });
                  }}
                >
                  <X size={15} />
                  <span>移除背景</span>
                </button>
              ) : null}
            </div>
            <div className="setting-block">
              <div className="setting-block-heading">
                <span>背景风格</span>
                <small>一键调整虚化、遮罩和面板强度。</small>
              </div>
              <div className="background-style-options" role="group" aria-label="背景风格">
                {backgroundPresetOptions.map((option) => (
                  <button
                    key={option.id}
                    className={selectedBackgroundPreset === option.id ? 'selected' : ''}
                    type="button"
                    aria-pressed={selectedBackgroundPreset === option.id}
                    title={option.description}
                    onClick={() => updateDraft({ backgroundBlur: option.blur, backgroundDim: option.dim, surfaceOpacity: option.surface })}
                  >
                    <span className="background-style-meter" aria-hidden="true">
                      <span style={{ width: `${option.surface}%` }} />
                    </span>
                    <span>
                      <strong>{option.label}</strong>
                      <small>{option.description}</small>
                    </span>
                    {selectedBackgroundPreset === option.id ? <CheckCircle2 size={15} /> : null}
                  </button>
                ))}
              </div>
            </div>
            <div className="range-control-stack" aria-label="背景微调">
              <div className="slider-field">
                <label htmlFor="background-blur">
                  <span>背景虚化</span>
                  <small>越大越柔和，越小越清晰</small>
                </label>
                <input id="background-blur" type="range" min="0" max="28" value={draft.backgroundBlur} onChange={(event) => updateDraft({ backgroundBlur: Number(event.target.value) })} />
                <strong>{draft.backgroundBlur}px</strong>
              </div>
              <div className="slider-field">
                <label htmlFor="background-dim">
                  <span>背景遮罩</span>
                  <small>越大背景越淡，文字更稳</small>
                </label>
                <input id="background-dim" type="range" min="0" max="92" value={draft.backgroundDim} onChange={(event) => updateDraft({ backgroundDim: Number(event.target.value) })} />
                <strong>{draft.backgroundDim}%</strong>
              </div>
              <div className="slider-field">
                <label htmlFor="surface-opacity">
                  <span>面板强度</span>
                  <small>越低越透，越高越像实色面板</small>
                </label>
                <input id="surface-opacity" type="range" min="10" max="100" value={draft.surfaceOpacity} onChange={(event) => updateDraft({ surfaceOpacity: Number(event.target.value) })} />
                <strong>{draft.surfaceOpacity}%</strong>
              </div>
            </div>
          </div>

          <div className="theme-preview-panel">
            <div className="settings-panel-title">
              <div className="mini-heading">
                <Eye size={16} />
                <span>实时预览</span>
              </div>
              <p>预览当前设置在资料工作台中的层级、背景和阅读面板。</p>
            </div>
            <div className="theme-preview" style={backgroundPreviewStyle(draftBackgroundUrl, draft)}>
              <div className="theme-preview-card settings-workbench-preview" style={{ borderColor: draft.accent }}>
                <aside className="settings-preview-sidebar" aria-hidden="true">
                  <span className="settings-preview-brand">芝士酷</span>
                  <span />
                  <span />
                  <span />
                </aside>
                <section className="settings-preview-main" aria-label="工作台预览">
                  <div className="settings-preview-toolbar">
                    <div>
                      <span className="settings-preview-kicker">资料工作台</span>
                      <strong>课程资料</strong>
                    </div>
                    <button type="button" style={{ background: draft.accent }}>
                      预览
                    </button>
                  </div>
                  <div className="settings-preview-grid">
                    <div className="settings-preview-list">
                      <div className="settings-preview-row active">
                        <BookOpen size={15} />
                        <span>机器学习笔记.pdf</span>
                      </div>
                      <div className="settings-preview-row">
                        <FileText size={15} />
                        <span>Transformer 论文摘录.md</span>
                      </div>
                      <div className="settings-preview-row">
                        <Folder size={15} />
                        <span>深度学习课程</span>
                      </div>
                    </div>
                    <div className="settings-preview-reader">
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </section>
      </main>
    </AppShell>
  );
}

function WorkspacePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [initialWorkspaceState] = useState(() => readWorkspaceState());
  const [parent, setParent] = useState<WorkspaceParentState>(initialWorkspaceState.parent);
  const [parentHistory, setParentHistory] = useState<WorkspaceParentState[]>(initialWorkspaceState.parentHistory);
  const [view, setView] = useState<ViewMode>(() => (localStorage.getItem('janestra:view') as ViewMode) || readThemePreferences().defaultView);
  const [sort, setSort] = useState<SortMode>('name');
  const [filter, setFilter] = useState<FilterMode>('all');
  const [query, setQuery] = useState('');
  const [globalQuery, setGlobalQuery] = useState('');
  const [knowledgeSearchFilter, setKnowledgeSearchFilter] = useState<KnowledgeSearchFilter | null>(null);
  const [openTabs, setOpenTabs] = useState<Resource[]>(initialWorkspaceState.openTabs);
  const [activeTabId, setActiveTabId] = useState(initialWorkspaceState.activeTabId);
  const [mobilePanel, setMobilePanel] = useState<MobileWorkspacePanel>(initialWorkspaceState.mobilePanel === 'preview' ? 'preview' : 'files');
  const isGuest = useIsGuest();
  const [importPanelOpen, setImportPanelOpen] = useState(false);
  const [isUploadDragging, setIsUploadDragging] = useState(false);
  const [folderCreatorOpen, setFolderCreatorOpen] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [workspaceMessage, setWorkspaceMessage] = useState('');
  const [workspaceError, setWorkspaceError] = useState('');
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedResourceIds, setSelectedResourceIds] = useState<Set<string>>(() => new Set());
  const [previewWidth, setPreviewWidth] = useState(() => {
    const inner = typeof window === 'undefined' ? 1280 : window.innerWidth;
    return readWorkspacePreviewDimension(workspacePreviewWidthKey, previewDefaultWidth, previewMinWidthFor(inner), computePreviewMaxWidth(inner));
  });
  const [previewHeight, setPreviewHeight] = useState(() => readWorkspacePreviewDimension(workspacePreviewHeightKey, previewDefaultHeight, previewMinHeight, 760));
  const [previewFocus, setPreviewFocus] = useState(false);
  const [collapsedPanes, setCollapsedPanes] = useState<Record<WorkspacePane, boolean>>({
    sidebar: false,
    files: false,
    preview: false
  });
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const globalSearchInputRef = useRef<HTMLInputElement>(null);
  const isMobileWorkspace = useMediaQuery('(max-width: 860px)');
  const queryClient = useQueryClient();
  const isRootParent = !parent.id && parent.path === '';
  const rootQuery = useQuery({ queryKey: ['resources', 'root'], queryFn: () => listResources(), staleTime: workspaceResourcesStaleMs });
  const currentQuery = useQuery({
    queryKey: ['resources', parent.id ?? parent.path],
    queryFn: () => listResources(parent.id ? { parentId: parent.id } : parent.path ? { parentPath: parent.path } : {}),
    enabled: !isRootParent,
    staleTime: workspaceResourcesStaleMs,
    placeholderData: (previousData) => previousData
  });
  const globalSearchQuery = useQuery({
    queryKey: ['search', globalQuery, knowledgeSearchFilter?.kind ?? '', knowledgeSearchFilter?.id ?? ''],
    queryFn: () => searchResources({
      q: globalQuery,
      tagId: knowledgeSearchFilter?.kind === 'tag' ? knowledgeSearchFilter.id : undefined,
      collectionId: knowledgeSearchFilter?.kind === 'collection' ? knowledgeSearchFilter.id : undefined,
      limit: 60
    }),
    enabled: globalQuery.trim().length > 0 || !!knowledgeSearchFilter
  });
  const breadcrumbItems = useMemo(() => {
    const parts = parent.path.split('/').filter(Boolean);
    return [
      defaultWorkspaceParent,
      ...parts.map((part, index) => ({
        path: parts.slice(0, index + 1).join('/'),
        title: part
      }))
    ];
  }, [parent.path]);
  const favoriteMutation = useMutation({
    mutationFn: ({ resourceId, favorite }: { resourceId: string; favorite: boolean }) => setFavorite(resourceId, favorite),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['favorites'] }),
        queryClient.invalidateQueries({ queryKey: ['search'] })
      ]);
    }
  });
  const resources = isRootParent ? rootQuery.data?.resources ?? [] : currentQuery.data?.resources ?? [];
  const isDirectoryLoading = isRootParent
    ? rootQuery.isLoading || (rootQuery.isFetching && !rootQuery.data)
    : currentQuery.isLoading || (currentQuery.isFetching && !currentQuery.data);
  const resourceListError = isRootParent ? rootQuery.isError : currentQuery.isError;
  const workspaceSideDataEnabled = !isDirectoryLoading && !resourceListError;
  const workspaceFavoritesQuery = useQuery({
    queryKey: ['favorites'],
    queryFn: listFavorites,
    enabled: workspaceSideDataEnabled && !isGuest,
    staleTime: workspaceResourcesStaleMs
  });
  const visible = useMemo(() => sortResources(filterResources(resources, filter, query), sort), [resources, filter, query, sort]);
  const favoriteIds = useMemo(() => new Set((workspaceFavoritesQuery.data ?? []).map((result) => result.resource.id)), [workspaceFavoritesQuery.data]);
  const searchResults = globalSearchQuery.data ?? [];
  const trimmedGlobalQuery = globalQuery.trim();
  const knowledgeSearchLabel = knowledgeSearchFilter ? `${knowledgeSearchFilter.kind === 'tag' ? '标签' : '合集'}：${knowledgeSearchFilter.label}` : '';
  const searchTitle = knowledgeSearchFilter ? knowledgeSearchLabel : `搜索：${trimmedGlobalQuery}`;
  const isGlobalSearch = trimmedGlobalQuery.length > 0 || !!knowledgeSearchFilter;
  const directories = resources.filter((item) => item.type === 'directory');
  const visibleDirectoryCount = visible.filter((item) => item.type === 'directory').length;
  const visibleFileCount = visible.filter((item) => item.type === 'file').length;
  const searchFileCount = searchResults.filter((result) => result.resource.type === 'file').length;
  const searchExternalCount = searchResults.filter((result) => result.resource.type === 'external_link' || result.external_link).length;
  const searchCollectionCount = searchResults.filter((result) => result.resource.type === 'collection' || result.collection).length;
  const searchTaggedCount = searchResults.filter((result) => result.tags.length > 0).length;
  const searchReadyCount = searchResults.filter((result) => result.index_status === 'ready').length;
  const searchPendingCount = searchResults.filter((result) => result.index_status && result.index_status !== 'ready').length;
  const activeListCount = isGlobalSearch ? searchResults.length : visible.length;
  const activeResource = openTabs.find((tab) => tab.id === activeTabId) ?? null;
  const currentFolderDownloadName = zipDownloadName(parent.title || '当前目录');
  const currentFolderDownloadHref = currentFolderZipURL({ parentId: parent.id, parentPath: parent.path });
  const hasDirectoryFilters = filter !== 'all' || query.trim().length > 0;
  const selectableResources = useMemo(() => visible.filter((resource) => isManageableWorkspaceResource(resource)), [visible]);
  const selectedResources = useMemo(() => selectableResources.filter((resource) => selectedResourceIds.has(resource.id)), [selectableResources, selectedResourceIds]);
  const selectedResourceIdList = useMemo(() => selectedResources.map((resource) => resource.id), [selectedResources]);
  const selectionActive = selectionMode || selectedResources.length > 0;
  const selectedSingleFile = selectedResources.length === 1 && selectedResources[0].type === 'file' ? selectedResources[0] : null;
  const selectedSingleFileHref = selectedSingleFile?.download_url || selectedSingleFile?.raw_url || '';
  const selectedUsesDirectFileDownload = Boolean(selectedSingleFile && selectedSingleFileHref);
  const selectedDownloadBaseName = selectedResources.length === 1 ? selectedResources[0].name : '选中资料';
  const selectedDownloadName = selectedUsesDirectFileDownload ? selectedSingleFile!.name : zipDownloadName(selectedDownloadBaseName);
  const selectedDownloadHref = selectedUsesDirectFileDownload ? selectedSingleFileHref : selectedResources.length > 0 ? resourcesZipURL({ resourceIds: selectedResourceIdList, name: selectedDownloadBaseName }) : '';
  const selectedDownloadActionLabel = selectedUsesDirectFileDownload ? '下载选中文件' : `打包下载选中（${selectedResources.length}）`;
  const knowledgeOrganizerTargets = useMemo(() => {
    if (selectedResources.length > 0) return selectedResources;
    return activeResource && isManageableWorkspaceResource(activeResource) ? [activeResource] : [];
  }, [activeResource, selectedResources]);
  const mobileFilesMeta = isGlobalSearch
    ? globalSearchQuery.isLoading ? '搜索中' : `${searchResults.length} 结果`
    : isDirectoryLoading ? '加载中' : resourceListError ? '待重试' : `${visible.length} 项`;
  const mobilePreviewMeta = openTabs.length > 0 ? `${openTabs.length} 标签` : '待选择';
  const mobileNavItems: Array<{ key: string; panel?: MobileWorkspacePanel; to?: string; label: string; meta: string; icon: ReactNode }> = [
    { key: 'files', panel: 'files', label: '资料', meta: mobileFilesMeta, icon: <ListTree size={18} /> },
    { key: 'preview', panel: 'preview', label: '预览', meta: mobilePreviewMeta, icon: <Eye size={18} /> },
    { key: 'knowledge', to: workspaceKnowledgePath, label: '知识', meta: '标签合集', icon: <BookOpen size={18} /> },
    { key: 'manage', to: workspaceManagePath, label: '管理', meta: '分享回收', icon: <ShieldCheck size={18} /> }
  ];

  useEffect(() => {
    saveWorkspaceState({ parent, parentHistory, openTabs, activeTabId, mobilePanel });
  }, [parent, parentHistory, openTabs, activeTabId, mobilePanel]);

  useEffect(() => {
    setSelectionMode(false);
    setSelectedResourceIds(new Set());
  }, [parent.id, parent.path]);

  useEffect(() => {
    if (!isGlobalSearch) return;
    setSelectionMode(false);
    setSelectedResourceIds(new Set());
  }, [isGlobalSearch]);

  useEffect(() => {
    setSelectedResourceIds((current) => {
      const allowed = new Set(selectableResources.map((resource) => resource.id));
      const next = new Set([...current].filter((resourceId) => allowed.has(resourceId)));
      return next.size === current.size ? current : next;
    });
  }, [selectableResources]);

  useEffect(() => {
    const routeState = location.state as WorkspaceRouteState | null;
    const workspaceCommand = routeState?.workspaceCommand;
    if (!workspaceCommand) return;
    setPreviewFocus(false);
    if (workspaceCommand === 'search') {
      setMobilePanel('files');
      setKnowledgeSearchFilter(routeState?.knowledgeSearchFilter ?? null);
      if (typeof routeState?.initialSearch === 'string') {
        setGlobalQuery(routeState.initialSearch);
      } else if (routeState?.knowledgeSearchFilter) {
        setGlobalQuery('');
      }
      window.requestAnimationFrame(() => {
        globalSearchInputRef.current?.focus();
        window.setTimeout(() => globalSearchInputRef.current?.focus(), 0);
      });
    }
    if (workspaceCommand === 'knowledge') {
      navigate(workspaceKnowledgePath, { replace: true });
      return;
    }
    if (workspaceCommand === 'preview') {
      setMobilePanel('preview');
    }
    if (workspaceCommand === 'manage') {
      navigate(workspaceManagePath, { replace: true });
      return;
    }
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!previewFocus) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPreviewFocus(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [previewFocus]);

  // Keep the stored preview width within the usable range as the viewport
  // changes, so the rendered width and the resize handle stay aligned with the
  // persisted value instead of silently diverging behind the grid's min().
  useEffect(() => {
    if (isMobileWorkspace) return undefined;
    const onResize = () => {
      const maxWidth = computePreviewMaxWidth(window.innerWidth);
      const minWidth = previewMinWidthFor(window.innerWidth);
      setPreviewWidth((current) => {
        const next = Math.round(clampNumber(current, minWidth, maxWidth, current));
        if (next !== current) saveWorkspacePreviewDimension(workspacePreviewWidthKey, next);
        return next;
      });
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isMobileWorkspace]);

  const refreshWorkspaceData = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['resources'] }),
      queryClient.invalidateQueries({ queryKey: ['search'] }),
      queryClient.invalidateQueries({ queryKey: ['search-status'] }),
      queryClient.invalidateQueries({ queryKey: ['tasks'] }),
      queryClient.invalidateQueries({ queryKey: ['recent'] })
    ]);
  };
  const handleUploadFiles = (files: File[]) => {
    if (isGuest || files.length === 0) return;
    setImportPanelOpen(true);
    setWorkspaceError('');
    setWorkspaceMessage('');
    uploadMutation.mutate(files);
  };
  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      const uploaded: Resource[] = [];
      for (const file of files) {
        uploaded.push(await uploadResource({ parentId: parent.id, parentPath: parent.path, file }));
      }
      return uploaded;
    },
    onSuccess: async (uploaded) => {
      setWorkspaceError('');
      setImportPanelOpen(true);
      setWorkspaceMessage(`已上传 ${uploaded.length} 个文件到 ${parent.title}`);
      await refreshWorkspaceData();
    },
    onError: (err) => {
      setWorkspaceMessage('');
      setWorkspaceError(errorMessage(err));
    }
  });
  const folderMutation = useMutation({
    mutationFn: () => createFolder({ parentId: parent.id, parentPath: parent.path, name: folderName.trim() }),
    onSuccess: async () => {
      setWorkspaceError('');
      setWorkspaceMessage(`已新建文件夹：${folderName.trim()}`);
      setFolderName('');
      setFolderCreatorOpen(false);
      setImportPanelOpen(true);
      await refreshWorkspaceData();
    },
    onError: (err) => {
      setWorkspaceMessage('');
      setWorkspaceError(errorMessage(err));
    }
  });
  const handleImportDragOver = (event: ReactDragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsUploadDragging(true);
  };
  const handleImportDragLeave = (event: ReactDragEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsUploadDragging(false);
  };
  const handleImportDrop = (event: ReactDragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsUploadDragging(false);
    handleUploadFiles(Array.from(event.dataTransfer.files ?? []));
  };
  const setViewMode = (next: ViewMode) => {
    setView(next);
    localStorage.setItem('janestra:view', next);
  };
  const toggleResourceSelection = (resourceId: string) => {
    setSelectedResourceIds((current) => {
      const next = new Set(current);
      if (next.has(resourceId)) {
        next.delete(resourceId);
      } else {
        next.add(resourceId);
      }
      return next;
    });
  };
  const selectAllVisibleResources = () => {
    setSelectionMode(true);
    setSelectedResourceIds(new Set(selectableResources.map((resource) => resource.id)));
  };
  const clearResourceSelection = () => {
    setSelectedResourceIds(new Set());
  };
  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedResourceIds(new Set());
  };
  const clearGlobalSearch = () => {
    setGlobalQuery('');
    setKnowledgeSearchFilter(null);
  };
  const clearKnowledgeSearchFilter = () => {
    setKnowledgeSearchFilter(null);
  };
  const applyKnowledgeSearchFilter = (nextFilter: KnowledgeSearchFilter) => {
    setKnowledgeSearchFilter(nextFilter);
    setGlobalQuery('');
    setPreviewFocus(false);
    setMobilePanel('files');
  };
  const openManagementForSelection = () => {
    if (selectedResources.length === 0) return;
    setWorkspaceError('');
    setWorkspaceMessage('');
    setPreviewFocus(false);
    navigate(workspaceManagePath, { state: { currentParent: parent, selectedResourceIds: selectedResourceIdList, shareSelectedId: selectedResources[0]?.id } as ManagementRouteState });
  };
  const navigateToParent = (next: WorkspaceParentState, pushHistory = true) => {
    setParent((current) => {
      if (current.path === next.path && current.id === next.id) return current;
      if (pushHistory) setParentHistory((history) => [...history, current].slice(-20));
      return next;
    });
    setMobilePanel('files');
  };
  const goBackDirectory = () => {
    setParentHistory((history) => {
      const previous = history.at(-1);
      if (!previous) return history;
      setParent(previous);
      setMobilePanel('files');
      return history.slice(0, -1);
    });
  };
  const goUpDirectory = () => {
    if (!parent.path) return;
    const parts = parent.path.split('/').filter(Boolean);
    const nextPath = parts.slice(0, -1).join('/');
    navigateToParent(nextPath ? { path: nextPath, title: parts.at(-2) || '资料库' } : defaultWorkspaceParent);
  };
  const goBackOrUpDirectory = () => {
    if (parentHistory.length > 0) {
      goBackDirectory();
      return;
    }
    goUpDirectory();
  };
  const openResource = (resource: Resource) => {
    if (resource.type === 'directory') {
      navigateToParent({ id: resource.id, path: resource.path, title: resource.name });
      return;
    }
    if (resource.type === 'file') void prefetchResourcePreview(queryClient, resource.id);
    void recordRecent(resource.id, resource.type === 'file' ? 'preview' : 'open').then(() => queryClient.invalidateQueries({ queryKey: ['recent'] }));
    setOpenTabs((current) => {
      if (current.some((tab) => tab.id === resource.id)) return current;
      return [...current, resource].slice(-8);
    });
    setActiveTabId(resource.id);
    if (isMobileWorkspace) setMobilePanel('preview');
  };
  const prefetchDirectory = (resource: Resource) => {
    if (resource.type !== 'directory') return;
    void queryClient.prefetchQuery({
      queryKey: ['resources', resource.id ?? resource.path],
      queryFn: () => listResources(resource.id ? { parentId: resource.id } : { parentPath: resource.path }),
      staleTime: workspaceResourcesStaleMs
    });
  };
  const closeTab = (resourceId: string) => {
    setOpenTabs((current) => {
      const next = current.filter((tab) => tab.id !== resourceId);
      if (activeTabId === resourceId) {
        setActiveTabId(next.at(-1)?.id ?? '');
      }
      return next;
    });
  };
  const closeAllTabs = () => {
    setOpenTabs([]);
    setActiveTabId('');
    setPreviewFocus(false);
    if (isMobileWorkspace) setMobilePanel('preview');
  };
  const startPreviewResize = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (isMobileWorkspace || previewFocus || collapsedPanes.files) return;
    event.preventDefault();
    const startX = event.clientX;
    // Base the drag on the actually rendered width, not the stored state value.
    // The stored value can diverge from the rendered width after window resizes,
    // so reading from the DOM keeps the handle pinned to the cursor and avoids jumps.
    const renderedWidth = event.currentTarget.parentElement?.getBoundingClientRect().width ?? previewWidth;
    const startWidth = Math.round(renderedWidth);
    let latestWidth = startWidth;
    document.body.classList.add('is-preview-column-resizing');
    const onMove = (moveEvent: PointerEvent) => {
      const maxWidth = computePreviewMaxWidth(window.innerWidth);
      const minWidth = previewMinWidthFor(window.innerWidth);
      latestWidth = Math.round(clampNumber(startWidth + startX - moveEvent.clientX, minWidth, maxWidth, startWidth));
      setPreviewWidth(latestWidth);
    };
    const onUp = () => {
      document.body.classList.remove('is-preview-column-resizing', 'is-preview-row-resizing');
      saveWorkspacePreviewDimension(workspacePreviewWidthKey, latestWidth);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };
  const togglePreviewFocus = () => {
    setPreviewFocus((focused) => {
      const next = !focused;
      if (next) {
        setMobilePanel('preview');
      }
      return next;
    });
  };
  const selectMobilePanel = (panel: MobileWorkspacePanel) => {
    setPreviewFocus(false);
    setMobilePanel(panel);
  };
  const mobilePanelForPane = (pane: WorkspacePane): MobileWorkspacePanel => (pane === 'preview' ? 'preview' : 'files');
  const firstAvailableMobilePanel = (nextCollapsed: Record<WorkspacePane, boolean>): MobileWorkspacePanel => {
    if (!nextCollapsed.files) return 'files';
    if (!nextCollapsed.preview) return 'preview';
    return 'files';
  };
  const collapseWorkspacePane = (pane: WorkspacePane) => {
    const nextCollapsed = { ...collapsedPanes, [pane]: true };
    setCollapsedPanes(nextCollapsed);
    if (pane === 'preview') {
      setPreviewFocus(false);
    }
    if (isMobileWorkspace && mobilePanel === mobilePanelForPane(pane)) {
      setMobilePanel(firstAvailableMobilePanel(nextCollapsed));
    }
  };
  const expandWorkspacePane = (pane: WorkspacePane) => {
    const nextCollapsed = { ...collapsedPanes, [pane]: false };
    setCollapsedPanes(nextCollapsed);
    if (isMobileWorkspace) setMobilePanel(mobilePanelForPane(pane));
  };
  const openKnowledgeForSelection = () => {
    if (selectedResources.length === 0) return;
    setPreviewFocus(false);
    navigate(workspaceKnowledgePath, { state: { organizationTargets: selectedResources } as KnowledgeRouteState });
  };
  const workspaceClasses = [
    'workspace',
    `mobile-panel-${mobilePanel}`,
    previewFocus ? 'preview-focus' : '',
    activeResource ? 'workspace-has-preview' : 'workspace-empty-preview',
    collapsedPanes.sidebar ? 'pane-sidebar-collapsed' : '',
    collapsedPanes.files ? 'pane-files-collapsed' : '',
    collapsedPanes.preview ? 'pane-preview-collapsed' : ''
  ].filter(Boolean).join(' ');
  const workspaceStyle = {
    '--workspace-preview-width': `${previewWidth}px`,
    '--workspace-preview-height': `${previewHeight}px`
  } as CSSProperties;

  return (
    <AppShell>
      <main className={workspaceClasses} style={workspaceStyle}>
        {collapsedPanes.sidebar || collapsedPanes.files || collapsedPanes.preview ? (
          <div className="workspace-restore-rail" aria-label="已收起的工作区">
            {collapsedPanes.sidebar ? (
              <button type="button" onClick={() => expandWorkspacePane('sidebar')} aria-label="展开目录栏" title="展开目录栏">
                <Folder size={16} />
                <span>目录</span>
              </button>
            ) : null}
            {collapsedPanes.files ? (
              <button type="button" onClick={() => expandWorkspacePane('files')} aria-label="展开资料栏" title="展开资料栏">
                <ListTree size={16} />
                <span>资料</span>
              </button>
            ) : null}
            {collapsedPanes.preview ? (
              <button type="button" onClick={() => expandWorkspacePane('preview')} aria-label="展开预览栏" title="展开预览栏">
                <Eye size={16} />
                <span>预览</span>
              </button>
            ) : null}
          </div>
        ) : null}
        <aside className="sidebar" aria-label="目录树">
          <div className="pane-header sidebar-pane-header">
            <button className="tree-root" type="button" onClick={() => navigateToParent(defaultWorkspaceParent)}>
              <Folder size={18} />
              <span>资料库</span>
            </button>
            <button className="icon-button pane-collapse-button" type="button" aria-label="收起目录栏" title="收起目录栏" onClick={() => collapseWorkspacePane('sidebar')}>
              <ChevronLeft size={17} />
            </button>
          </div>
          <div className="tree-list">
            {directories.map((item) => (
              <button key={item.id} className="tree-item" type="button" onPointerEnter={() => prefetchDirectory(item)} onFocus={() => prefetchDirectory(item)} onClick={() => navigateToParent({ id: item.id, path: item.path, title: item.name })}>
                <ChevronRight size={15} />
                <Folder size={17} />
                <span>{item.name}</span>
              </button>
            ))}
          </div>
          <section className="workspace-side-routes" aria-label="工作台页面">
            <Link className="workspace-route-card" to={workspaceKnowledgePath} state={{ organizationTargets: knowledgeOrganizerTargets } as KnowledgeRouteState}>
              <BookOpen size={17} />
              <span>
                <strong>知识整理</strong>
                <small>收藏、最近、标签、合集和外链</small>
              </span>
            </Link>
            <Link className="workspace-route-card" to={workspaceManagePath} state={{ currentParent: parent } as ManagementRouteState}>
              <ShieldCheck size={17} />
              <span>
                <strong>安全管理</strong>
                <small>分享、回收站、批量下载和删除</small>
              </span>
            </Link>
          </section>
        </aside>
        <section className="resource-pane">
          <div className="workspace-toolbar">
            <div className="workspace-context">
              <div className="breadcrumb">
                <div className="directory-nav">
                  <button className="directory-back-button" type="button" aria-label="返回上一目录" title="返回上一目录" disabled={parentHistory.length === 0 && !parent.path} onClick={goBackOrUpDirectory}>
                    <ChevronLeft size={17} />
                    <span>返回</span>
                  </button>
                  <nav className="directory-crumbs" aria-label="当前位置">
                    {breadcrumbItems.map((item, index) => {
                      const isCurrent = item.path === parent.path;
                      return (
                        <span className="directory-crumb-group" key={`${item.path || 'root'}-${index}`}>
                          {index > 0 ? <span className="directory-crumb-separator">/</span> : null}
                          <button
                            className={isCurrent ? 'current' : ''}
                            type="button"
                            aria-current={isCurrent ? 'page' : undefined}
                            disabled={isCurrent}
                            onClick={() => navigateToParent(item.path ? item : defaultWorkspaceParent)}
                          >
                            {item.title}
                          </button>
                        </span>
                      );
                    })}
                  </nav>
                </div>
                {parent.path ? <small>{parent.path}</small> : <small>根目录</small>}
              </div>
              <div className="workspace-context-meta" aria-label="当前目录摘要">
                <span>
                  <Folder size={14} />
                  {directories.length} 个文件夹
                </span>
                <span>
                  <FileText size={14} />
                  {resources.filter((item) => item.type === 'file').length} 个文件
                </span>
                <span>{openTabs.length} 个预览标签</span>
              </div>
            </div>
            <div className="workspace-tools">
              <div className="toolbar-actions toolbar-primary-actions">
                {!isMobileWorkspace ? (
                  <button className="icon-button pane-collapse-button" type="button" aria-label="收起资料栏" title="收起资料栏" onClick={() => collapseWorkspacePane('files')}>
                    <ChevronLeft size={17} />
                  </button>
                ) : null}
                <input
                  ref={uploadInputRef}
                  type="file"
                  multiple
                  className="hidden-file-input"
                  onChange={(event) => {
                    const files = Array.from(event.target.files ?? []);
                    event.currentTarget.value = '';
                    handleUploadFiles(files);
                  }}
                />
                {!isGuest ? (
                  <>
                    <button
                      className="text-action toolbar-command"
                      type="button"
                      aria-label={uploadMutation.isPending ? '上传中' : '上传'}
                      title={uploadMutation.isPending ? '上传中' : '上传'}
                      onClick={() => {
                        setImportPanelOpen(true);
                        uploadInputRef.current?.click();
                      }}
                      disabled={uploadMutation.isPending}
                    >
                      <Upload size={16} />
                      <span>{uploadMutation.isPending ? '上传中' : '上传'}</span>
                    </button>
                    <button
                      className="text-action toolbar-command"
                      type="button"
                      aria-label="新建文件夹"
                      title="新建文件夹"
                      onClick={() => {
                        setImportPanelOpen(true);
                        setFolderCreatorOpen((open) => !open);
                      }}
                    >
                      <FolderPlus size={16} />
                      <span>新建文件夹</span>
                    </button>
                  </>
                ) : null}
                <button
                  className="text-action toolbar-command"
                  type="button"
                  aria-label="刷新当前目录"
                  title="刷新当前目录"
                  onClick={() => {
                    setWorkspaceError('');
                    setWorkspaceMessage('');
                    void refreshWorkspaceData();
                  }}
                  disabled={isDirectoryLoading}
                >
                  <RefreshCw className={isDirectoryLoading ? 'state-spin' : undefined} size={16} />
                  <span>刷新</span>
                </button>
                <a
                  className="text-action toolbar-command"
                  href={currentFolderDownloadHref}
                  download={currentFolderDownloadName}
                  aria-label="下载目录"
                  title="下载目录"
                  onClick={() => {
                    setWorkspaceError('');
                    setWorkspaceMessage(`已开始下载：${currentFolderDownloadName}`);
                  }}
                >
                  <Download size={16} />
                  <span>下载目录</span>
                </a>
                <Link
                  className="icon-button"
                  to={workspaceManagePath}
                  state={{ currentParent: parent, selectedResourceIds: selectedResourceIdList, shareSelectedId: selectedResources[0]?.id } as ManagementRouteState}
                  title="回收站和分享"
                  aria-label="回收站和分享"
                  onClick={() => setPreviewFocus(false)}
                >
                  <ShieldCheck size={18} />
                  <span className="toolbar-icon-label">管理</span>
                </Link>
              </div>
              <div className="toolbar-actions toolbar-filter-actions">
                <label className="search-box">
                  <Search size={17} />
                  <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="筛选当前目录" />
                </label>
                <label className="search-box global-search">
                  <Search size={17} />
                  <input ref={globalSearchInputRef} value={globalQuery} onChange={(event) => setGlobalQuery(event.target.value)} placeholder="搜索全部资料、标签、合集、链接" />
                </label>
                <label className="select-control" title="排序">
                  <SlidersHorizontal size={16} />
                  <select value={sort} onChange={(event) => setSort(event.target.value as SortMode)}>
                    {Object.entries(sortLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="select-control" title="筛选">
                  <Filter size={16} />
                  <select value={filter} onChange={(event) => setFilter(event.target.value as FilterMode)}>
                    {Object.entries(filterLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="segmented" aria-label="视图切换">
                  <button className={view === 'list' ? 'active' : ''} type="button" title="清单视图" aria-label="清单视图" onClick={() => setViewMode('list')}>
                    <LayoutList size={17} />
                  </button>
                  <button className={view === 'grid' ? 'active' : ''} type="button" title="卡片视图" aria-label="卡片视图" onClick={() => setViewMode('grid')}>
                    <Grid3X3 size={17} />
                  </button>
                  <button className={view === 'compact' ? 'active' : ''} type="button" title="紧凑视图" aria-label="紧凑视图" onClick={() => setViewMode('compact')}>
                    <Rows3 size={17} />
                  </button>
                </div>
              </div>
            </div>
          </div>
          {importPanelOpen || folderCreatorOpen || uploadMutation.isPending ? (
            <section
              className={isUploadDragging ? 'workspace-import-panel dragging' : 'workspace-import-panel'}
              aria-label="资料导入"
              onDragOver={handleImportDragOver}
              onDragLeave={handleImportDragLeave}
              onDrop={handleImportDrop}
            >
              <div className="import-panel-main">
                <div className="import-panel-icon">
                  <Upload size={20} />
                </div>
                <div>
                  <span className="home-eyebrow">资料导入</span>
                  <strong>{parent.title}</strong>
                  <small>{parent.path || '根目录'} · 上传后同步刷新资料、搜索和最近打开</small>
                </div>
              </div>
              <div className="import-panel-actions">
                <button className="text-action" type="button" onClick={() => uploadInputRef.current?.click()} disabled={uploadMutation.isPending}>
                  <Upload size={15} />
                  <span>{uploadMutation.isPending ? '上传中' : '选择文件'}</span>
                </button>
                <button className={folderCreatorOpen ? 'text-action active' : 'text-action'} type="button" onClick={() => setFolderCreatorOpen((open) => !open)}>
                  <FolderPlus size={15} />
                  <span>新建文件夹</span>
                </button>
                <button
                  className="icon-button"
                  type="button"
                  title="关闭导入面板"
                  aria-label="关闭导入面板"
                  onClick={() => {
                    setImportPanelOpen(false);
                    setFolderCreatorOpen(false);
                    setIsUploadDragging(false);
                  }}
                >
                  <X size={16} />
                </button>
              </div>
              {folderCreatorOpen ? (
                <form
                  className="inline-folder-form import-folder-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (folderName.trim()) folderMutation.mutate();
                  }}
                >
                  <FolderPlus size={16} />
                  <input value={folderName} onChange={(event) => setFolderName(event.target.value)} placeholder={`在 ${parent.title} 中新建文件夹`} autoFocus />
                  <button className="primary-button" type="submit" disabled={!folderName.trim() || folderMutation.isPending}>
                    <span>{folderMutation.isPending ? '创建中' : '创建'}</span>
                  </button>
                  <button className="text-action" type="button" onClick={() => setFolderCreatorOpen(false)}>
                    <X size={15} />
                    <span>取消</span>
                  </button>
                </form>
              ) : null}
            </section>
          ) : null}
          {workspaceError ? <div className="workspace-feedback error">{workspaceError}</div> : null}
          {workspaceMessage ? <div className="workspace-feedback success">{workspaceMessage}</div> : null}
          <div className="resource-list-heading">
            <div>
              <span className="home-eyebrow">{isGlobalSearch ? '全库搜索' : '当前目录'}</span>
              <strong>{isGlobalSearch ? searchTitle : parent.title}</strong>
              <small>
                {isGlobalSearch
                  ? `${activeListCount} 个结果 · ${searchFileCount} 个文件 · ${searchExternalCount} 个外链 · ${searchCollectionCount} 个合集${trimmedGlobalQuery && knowledgeSearchFilter ? ` · 搜索 ${trimmedGlobalQuery}` : ''}`
                  : `${visible.length} 项 · ${visibleDirectoryCount} 个文件夹 · ${visibleFileCount} 个文件`}
              </small>
            </div>
            <div className="resource-list-mode" aria-label="列表视图状态">
              {isGlobalSearch ? (
                <>
                  <span>{searchReadyCount} 已索引</span>
                  <span>{searchPendingCount} 待同步</span>
                  <span>{searchTaggedCount} 带标签</span>
                  {knowledgeSearchFilter ? <span>{knowledgeSearchFilter.kind === 'tag' ? '标签筛选' : '合集筛选'}</span> : null}
                </>
              ) : (
                <>
                  <span>{viewModeLabels[view]}</span>
                  <span>{filterLabels[filter]}</span>
                  <span>按{sortLabels[sort]}</span>
                </>
              )}
            </div>
          </div>
          {!isGuest && !isGlobalSearch && selectableResources.length > 0 ? (
            <section className={selectionActive ? 'resource-selection-bar active' : 'resource-selection-bar idle'} aria-label="资料批量选择">
              <div className="resource-selection-summary">
                <span className="resource-selection-icon">
                  <CheckCircle2 size={18} />
                </span>
                <div>
                  <strong>{selectedResources.length > 0 ? `已选择 ${selectedResources.length} 项` : selectionActive ? '勾选当前目录资料' : '批量选择资料'}</strong>
                  <small>{selectionActive ? `${selectableResources.length} 项可选，可打包下载、整理到合集或进入安全管理。` : '选择当前目录的文件和文件夹后，可以打包下载或整理到合集。'}</small>
                </div>
              </div>
              <div className="resource-selection-actions">
                {selectionActive ? (
                  <>
                    <button className="text-action" type="button" disabled={selectableResources.length === 0} onClick={selectAllVisibleResources}>
                      <ListTree size={15} />
                      <span>全选当前列表</span>
                    </button>
                    <button className="text-action" type="button" disabled={selectedResources.length === 0} onClick={clearResourceSelection}>
                      <RotateCcw size={15} />
                      <span>清空选择</span>
                    </button>
                    <button className="text-action" type="button" onClick={exitSelectionMode}>
                      <X size={15} />
                      <span>退出选择</span>
                    </button>
                  </>
                ) : (
                  <button className="text-action" type="button" onClick={() => setSelectionMode(true)}>
                    <ListTree size={15} />
                    <span>批量选择</span>
                  </button>
                )}
                {selectionActive ? (
                  <>
                    {selectedResources.length > 0 ? (
                      <a
                        className="text-action"
                        href={selectedDownloadHref}
                        download={selectedDownloadName}
                        onClick={() => {
                          setWorkspaceError('');
                          setWorkspaceMessage(`已开始下载：${selectedDownloadName}`);
                        }}
                      >
                        <Download size={15} />
                        <span>{selectedDownloadActionLabel}</span>
                      </a>
                    ) : (
                      <button className="text-action" type="button" disabled>
                        <Download size={15} />
                        <span>打包下载选中</span>
                      </button>
                    )}
                    <button className="text-action" type="button" disabled={selectedResources.length === 0} onClick={openKnowledgeForSelection}>
                      <Tag size={15} />
                      <span>整理选中</span>
                    </button>
                    <button className="text-action" type="button" disabled={selectedResources.length === 0} onClick={openManagementForSelection}>
                      <ShieldCheck size={15} />
                      <span>安全管理选中</span>
                    </button>
                  </>
                ) : null}
              </div>
            </section>
          ) : null}
          {isGlobalSearch ? (
            <section className="search-overview" aria-label="全库搜索摘要">
              <div className="search-overview-copy">
                <span className="home-eyebrow">学习检索</span>
                <strong>{knowledgeSearchFilter ? knowledgeSearchLabel : trimmedGlobalQuery}</strong>
                <p>{knowledgeSearchFilter ? (trimmedGlobalQuery ? `范围内搜索：${trimmedGlobalQuery}` : '按学习组织筛选资料') : '按相关度整理学习资料'}</p>
                <div className="search-overview-actions">
                  {knowledgeSearchFilter ? (
                    <button className="text-action compact" type="button" onClick={clearKnowledgeSearchFilter}>
                      <X size={14} />
                      <span>清除筛选</span>
                    </button>
                  ) : null}
                  {trimmedGlobalQuery ? (
                    <button className="text-action compact" type="button" onClick={clearGlobalSearch}>
                      <RotateCcw size={14} />
                      <span>清空搜索</span>
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="search-overview-stats">
                <span>
                  <strong>{searchResults.length}</strong> 结果
                </span>
                <span>
                  <strong>{searchFileCount}</strong> 文件
                </span>
                <span>
                  <strong>{searchExternalCount}</strong> 外链
                </span>
                <span>
                  <strong>{searchCollectionCount}</strong> 合集
                </span>
              </div>
            </section>
          ) : null}
          <div className={`resource-list ${isGlobalSearch ? 'search-results-list' : view}`}>
            {isGlobalSearch ? (
              <>
                {searchResults.map((result) => (
                  <SearchResultLine
                    key={result.resource.id}
                    result={result}
                    onOpen={() => {
                      if (result.external_link) {
                        void recordRecent(result.resource.id, 'open').then(() => queryClient.invalidateQueries({ queryKey: ['recent'] }));
                        window.open(result.external_link.url, '_blank', 'noopener,noreferrer');
                        return;
                      }
                      openResource(result.resource);
                    }}
                    onToggleFavorite={() => {
                    if (isGuest) {
                      navigate('/login');
                      return;
                    }
                    favoriteMutation.mutate({ resourceId: result.resource.id, favorite: !result.favorite });
                  }}
                    onFilterByTag={(tag) => applyKnowledgeSearchFilter({ kind: 'tag', id: tag.id, label: tag.name })}
                    onFilterByCollection={(collection) => applyKnowledgeSearchFilter({ kind: 'collection', id: collection.resource.id, label: collection.resource.name })}
                  />
                ))}
                {globalSearchQuery.isLoading ? (
                  <WorkspaceStateCard
                    icon={<RefreshCw className="state-spin" size={22} />}
                    eyebrow="全库搜索"
                    title="正在检索资料库"
                    description="芝士酷正在合并文件、标签、合集和外链结果。"
                  />
                ) : null}
                {globalSearchQuery.isError ? (
                  <WorkspaceStateCard
                    icon={<RefreshCw size={22} />}
                    eyebrow="全库搜索"
                    title="搜索暂时失败"
                    description="网络或索引服务可能暂时不可用，可以保持当前关键词后重试。"
                    actions={
                      <button className="text-action" type="button" onClick={() => queryClient.invalidateQueries({ queryKey: ['search'] })}>
                        <RefreshCw size={15} />
                        <span>重试搜索</span>
                      </button>
                    }
                  />
                ) : null}
                {!globalSearchQuery.isLoading && !globalSearchQuery.isError && searchResults.length === 0 ? (
                  <WorkspaceStateCard
                    icon={<Search size={22} />}
                    eyebrow="没有结果"
                    title="没有找到匹配资料"
                    description="换一个关键词，或清空搜索回到当前目录继续浏览。"
                    actions={
                      <button className="text-action" type="button" onClick={clearGlobalSearch}>
                        <X size={15} />
                        <span>清空搜索</span>
                      </button>
                    }
                  />
                ) : null}
              </>
            ) : (
              <>
                {visible.map((item) => {
                  const isFavorite = favoriteIds.has(item.id);
                  const itemDownloadHref = item.type === 'directory'
                    ? resourcesZipURL({ resourceIds: [item.id], name: item.name })
                    : item.type === 'file'
                      ? item.download_url || item.raw_url || undefined
                      : undefined;
                  const itemDownloadName = item.type === 'directory' ? zipDownloadName(item.name) : item.type === 'file' ? item.name : undefined;
                  return (
                    <ResourceLine
                      key={item.id}
                      resource={item}
                      onOpen={() => openResource(item)}
                      onPrefetch={() => prefetchDirectory(item)}
                      isFavorite={isFavorite}
                      favoritePending={favoriteMutation.isPending}
                      onToggleFavorite={() => {
                        if (isGuest) {
                          navigate('/login');
                          return;
                        }
                        favoriteMutation.mutate({ resourceId: item.id, favorite: !isFavorite });
                      }}
                      selectable={selectionActive && isManageableWorkspaceResource(item)}
                      selected={selectedResourceIds.has(item.id)}
                      onToggleSelected={() => toggleResourceSelection(item.id)}
                      downloadHref={itemDownloadHref}
                      downloadName={itemDownloadName}
                      onDownloadStart={() => {
                        setWorkspaceError('');
                        setWorkspaceMessage(`已开始下载：${itemDownloadName}`);
                      }}
                    />
                  );
                })}
                {resourceListError ? (
                  <WorkspaceStateCard
                    icon={<RefreshCw size={22} />}
                    eyebrow="目录加载"
                    title="当前目录加载失败"
                    description="资料仍保持私有，当前列表没有刷新成功。可以重试加载，不会改动任何文件。"
                    actions={
                      <button className="text-action" type="button" onClick={refreshWorkspaceData}>
                        <RefreshCw size={15} />
                        <span>重试加载</span>
                      </button>
                    }
                  />
                ) : null}
                {isDirectoryLoading && !resourceListError ? (
                  <WorkspaceStateCard
                    icon={<RefreshCw className="state-spin" size={22} />}
                    eyebrow="当前目录"
                    title="正在加载资料"
                    description="保持当前目录上下文，只刷新这一层资源列表。"
                  />
                ) : null}
                {!isDirectoryLoading && !resourceListError && visible.length === 0 ? (
                  <WorkspaceStateCard
                    icon={hasDirectoryFilters ? <Filter size={22} /> : <Upload size={22} />}
                    eyebrow={hasDirectoryFilters ? '筛选结果' : '空资料库'}
                    title={hasDirectoryFilters ? '没有符合筛选的资料' : parent.path ? '这个目录还是空的' : '资料库还没有资料'}
                    description={hasDirectoryFilters ? '当前目录里没有匹配这些条件的资源，可以清除筛选或改用全库搜索。' : '从上传文件或新建文件夹开始，资料会继续保留在当前目录权限范围内。'}
                    actions={hasDirectoryFilters ? (
                      <>
                        <button
                          className="text-action"
                          type="button"
                          onClick={() => {
                            setQuery('');
                            setFilter('all');
                          }}
                        >
                          <RotateCcw size={15} />
                          <span>清除筛选</span>
                        </button>
                        {query.trim() ? (
                          <button className="text-action" type="button" onClick={() => setGlobalQuery(query.trim())}>
                            <Search size={15} />
                            <span>全库搜索</span>
                          </button>
                        ) : null}
                      </>
                    ) : isGuest ? null : (
                      <>
                        <button
                          className="primary-button"
                          type="button"
                          onClick={() => {
                            setImportPanelOpen(true);
                            uploadInputRef.current?.click();
                          }}
                        >
                          <Upload size={16} />
                          <span>上传文件</span>
                        </button>
                        <button
                          className="text-action"
                          type="button"
                          onClick={() => {
                            setImportPanelOpen(true);
                            setFolderCreatorOpen(true);
                          }}
                        >
                          <FolderPlus size={15} />
                          <span>新建文件夹</span>
                        </button>
                      </>
                    )}
                  />
                ) : null}
              </>
            )}
          </div>
          <footer className="statusbar">
            <span>{isGlobalSearch ? searchResults.length : visible.length} 项</span>
            <span>{isGlobalSearch ? knowledgeSearchLabel || '全库' : filterLabels[filter]}</span>
            <span>{isGlobalSearch ? '按相关度排序' : `按${sortLabels[sort]}排序`}</span>
          </footer>
        </section>
        <PreviewWorkspacePanel
          tabs={openTabs}
          activeId={activeTabId}
          focused={previewFocus}
          onSelect={setActiveTabId}
          onClose={closeTab}
          onCloseAll={closeAllTabs}
          onResizeStart={startPreviewResize}
          onToggleFocus={togglePreviewFocus}
          onCollapsePane={() => collapseWorkspacePane('preview')}
        />
        <nav className="mobile-workspace-nav" aria-label="工作台移动端视图">
          {mobileNavItems.map((item) => {
            const content = (
              <>
                <span className="mobile-workspace-nav-icon" aria-hidden="true">{item.icon}</span>
                <span className="mobile-workspace-nav-copy">
                  <strong>{item.label}</strong>
                  <small>{item.meta}</small>
                </span>
              </>
            );
            return item.to ? (
              <Link key={item.key} to={item.to} title={`${item.label}：${item.meta}`}>
                {content}
              </Link>
            ) : (
              <button
                key={item.key}
                className={mobilePanel === item.panel ? 'active' : ''}
                type="button"
                aria-pressed={mobilePanel === item.panel}
                title={`${item.label}：${item.meta}`}
                onClick={() => item.panel && selectMobilePanel(item.panel)}
              >
                {content}
              </button>
            );
          })}
        </nav>
      </main>
    </AppShell>
  );
}

function readRouteResources(input: unknown) {
  if (!Array.isArray(input)) return [];
  return input.filter((item): item is Resource => {
    const resource = item as Partial<Resource>;
    return !!resource && typeof resource.id === 'string' && typeof resource.name === 'string' && typeof resource.type === 'string';
  });
}

function readRouteResourceIds(input: unknown) {
  if (!Array.isArray(input)) return [];
  return input.filter((item): item is string => typeof item === 'string');
}

function KnowledgePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const routeState = location.state as KnowledgeRouteState | null;
  const organizationTargets = useMemo(() => readRouteResources(routeState?.organizationTargets).filter(isManageableWorkspaceResource), [routeState]);

  const openResult = (result: SearchResult) => {
    const resource = result.resource;
    if (result.external_link) {
      void recordRecent(resource.id, 'open').then(() => queryClient.invalidateQueries({ queryKey: ['recent'] }));
      window.open(result.external_link.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (resource.type === 'directory') {
      const savedWorkspace = readWorkspaceState();
      saveWorkspaceState({
        parent: { id: resource.id, path: resource.path, title: resource.name },
        parentHistory: [],
        openTabs: savedWorkspace.openTabs,
        activeTabId: savedWorkspace.activeTabId,
        mobilePanel: 'files'
      });
      void recordRecent(resource.id, 'open').then(() => queryClient.invalidateQueries({ queryKey: ['recent'] }));
      navigate(workspaceFilesPath);
      return;
    }
    if (resource.type === 'file') {
      const savedWorkspace = readWorkspaceState();
      const openTabs = [...savedWorkspace.openTabs.filter((tab) => tab.id !== resource.id), resource].slice(-8);
      saveWorkspaceState({
        parent: { path: resource.parent_path, title: resource.parent_path ? resource.parent_path.split('/').at(-1) || '资料库' : '资料库' },
        parentHistory: [],
        openTabs,
        activeTabId: resource.id,
        mobilePanel: 'preview'
      });
      void prefetchResourcePreview(queryClient, resource.id);
      void recordRecent(resource.id, 'preview').then(() => queryClient.invalidateQueries({ queryKey: ['recent'] }));
      navigate(workspaceFilesPath, { state: { workspaceCommand: 'preview' } as WorkspaceRouteState });
    }
  };
  const filterInWorkspace = (filter: KnowledgeSearchFilter) => {
    navigate(workspaceFilesPath, { state: { workspaceCommand: 'search', knowledgeSearchFilter: filter } as WorkspaceRouteState });
  };

  return (
    <AppShell>
      <main className="workspace-subpage knowledge-page">
        <section className="workspace-subpage-hero" aria-labelledby="knowledge-title">
          <div className="workspace-subpage-copy">
            <span className="home-eyebrow">知识整理</span>
            <h1 id="knowledge-title">把资料变成可复习的结构</h1>
            <p>收藏、最近、标签、合集和外链集中在这里；资料页只保留浏览、导入和预览。</p>
          </div>
          <div className="workspace-subpage-actions">
            <Link className="text-action" to={workspaceFilesPath}>
              <ListTree size={15} />
              <span>回资料库</span>
            </Link>
            <Link className="text-action" to={workspaceManagePath}>
              <ShieldCheck size={15} />
              <span>安全管理</span>
            </Link>
          </div>
        </section>
        <KnowledgeSidePanel
          enabled
          organizationTargets={organizationTargets}
          onOpen={openResult}
          onFilterByTag={(tag) => filterInWorkspace({ kind: 'tag', id: tag.id, label: tag.name })}
          onFilterByCollection={(collection) => filterInWorkspace({ kind: 'collection', id: collection.resource.id, label: collection.resource.name })}
        />
      </main>
    </AppShell>
  );
}

function ManagementPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const routeState = location.state as ManagementRouteState | null;
  const [initialParent] = useState(() => normalizeWorkspaceParent(routeState?.currentParent) ?? readWorkspaceState().parent ?? defaultWorkspaceParent);
  const [initialSelectedIds] = useState(() => readRouteResourceIds(routeState?.selectedResourceIds));
  const [initialShareSelectedId] = useState(() => typeof routeState?.shareSelectedId === 'string' ? routeState.shareSelectedId : '');
  const [savedActiveResource] = useState(() => {
    const savedWorkspace = readWorkspaceState();
    return savedWorkspace.openTabs.find((tab) => tab.id === savedWorkspace.activeTabId) ?? savedWorkspace.openTabs.at(-1) ?? null;
  });
  const resourcesQuery = useQuery({
    queryKey: ['resources', (initialParent.id ?? initialParent.path) || 'root'],
    queryFn: () => listResources(initialParent.id ? { parentId: initialParent.id } : initialParent.path ? { parentPath: initialParent.path } : {}),
    staleTime: workspaceResourcesStaleMs
  });
  const resources = resourcesQuery.data?.resources ?? [];
  const activeResource = savedActiveResource && resources.some((resource) => resource.id === savedActiveResource.id) ? savedActiveResource : null;

  return (
    <AppShell>
      <main className="workspace-subpage management-page">
        <section className="workspace-subpage-hero" aria-labelledby="management-title">
          <div className="workspace-subpage-copy">
            <span className="home-eyebrow">安全管理</span>
            <h1 id="management-title">分享、回收站和批量操作</h1>
            <p>{initialParent.path ? `当前目录：${initialParent.path}` : '当前目录：资料库'}</p>
          </div>
          <div className="workspace-subpage-actions">
            <Link className="text-action" to={workspaceFilesPath}>
              <ListTree size={15} />
              <span>回资料库</span>
            </Link>
            <Link className="text-action" to={workspaceKnowledgePath}>
              <BookOpen size={15} />
              <span>知识整理</span>
            </Link>
          </div>
        </section>
        {resourcesQuery.isLoading ? (
          <WorkspaceStateCard
            className="workspace-subpage-state"
            icon={<RefreshCw className="state-spin" size={22} />}
            eyebrow="安全管理"
            title="正在加载当前目录"
            description="加载完成后可以创建分享、打包下载、移入回收站或恢复资源。"
          />
        ) : resourcesQuery.isError ? (
          <WorkspaceStateCard
            className="workspace-subpage-state"
            icon={<AlertTriangle size={22} />}
            eyebrow="安全管理"
            title="当前目录加载失败"
            description="安全操作暂不执行，可以回到资料库或重试加载。"
            actions={(
              <>
                <button className="text-action" type="button" onClick={() => resourcesQuery.refetch()} disabled={resourcesQuery.isFetching}>
                  <RefreshCw className={resourcesQuery.isFetching ? 'state-spin' : ''} size={15} />
                  <span>{resourcesQuery.isFetching ? '正在重试' : '重试加载'}</span>
                </button>
                <Link className="text-action" to={workspaceFilesPath}>
                  <ListTree size={15} />
                  <span>回资料库</span>
                </Link>
              </>
            )}
          />
        ) : (
          <P6ManagementPanel
            resources={resources}
            currentParent={initialParent}
            activeResource={activeResource}
            initialSelectedIds={initialSelectedIds}
            initialShareSelectedId={initialShareSelectedId}
            onClose={() => navigate(workspaceFilesPath)}
            onResourceDeleted={(resourceId) => {
              void queryClient.invalidateQueries({ queryKey: ['resources'] });
              void queryClient.invalidateQueries({ queryKey: ['search'] });
              void queryClient.invalidateQueries({ queryKey: ['favorites'] });
              if (resourceId === activeResource?.id) {
                const savedWorkspace = readWorkspaceState();
                saveWorkspaceState({
                  ...savedWorkspace,
                  openTabs: savedWorkspace.openTabs.filter((tab) => tab.id !== resourceId),
                  activeTabId: savedWorkspace.activeTabId === resourceId ? savedWorkspace.openTabs.filter((tab) => tab.id !== resourceId).at(-1)?.id ?? '' : savedWorkspace.activeTabId
                });
              }
            }}
          />
        )}
      </main>
    </AppShell>
  );
}

function P6ManagementPanel({
  resources,
  currentParent,
  activeResource,
  initialSelectedIds = [],
  initialShareSelectedId = '',
  onClose,
  onResourceDeleted
}: {
  resources: Resource[];
  currentParent: WorkspaceParentState;
  activeResource: Resource | null;
  initialSelectedIds?: string[];
  initialShareSelectedId?: string;
  onClose: () => void;
  onResourceDeleted: (resourceId: string) => void;
}) {
  const queryClient = useQueryClient();
  const shareableResources = useMemo(() => resources.filter((resource) => resource.type === 'file' || resource.type === 'directory'), [resources]);
  const [shareSelectedId, setShareSelectedId] = useState(initialShareSelectedId);
  const [deleteSelectedIds, setDeleteSelectedIds] = useState<Set<string>>(() => new Set(initialSelectedIds));
  const [sharePassword, setSharePassword] = useState('');
  const [shareDays, setShareDays] = useState('7');
  const [allowPreview, setAllowPreview] = useState(true);
  const [allowDownload, setAllowDownload] = useState(false);
  const [createdShare, setCreatedShare] = useState<ShareInfo | null>(null);
  const [copyState, setCopyState] = useState('');
  const [managementNotice, setManagementNotice] = useState<ManagementFeedback | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Resource[] | null>(null);
  const [confirmPurge, setConfirmPurge] = useState<TrashItem | null>(null);
  const managementFeedbackRef = useRef<HTMLElement | null>(null);
  const trashQuery = useQuery({ queryKey: ['trash'], queryFn: listTrash });
  const sharesQuery = useQuery({ queryKey: ['shares'], queryFn: listShares });

  useEffect(() => {
    if (shareSelectedId && shareableResources.some((resource) => resource.id === shareSelectedId)) return;
    if (initialShareSelectedId && shareableResources.some((resource) => resource.id === initialShareSelectedId)) {
      setShareSelectedId(initialShareSelectedId);
      return;
    }
    if (activeResource && shareableResources.some((resource) => resource.id === activeResource.id)) {
      setShareSelectedId(activeResource.id);
      return;
    }
    setShareSelectedId(shareableResources[0]?.id ?? '');
  }, [activeResource, initialShareSelectedId, shareSelectedId, shareableResources]);

  useEffect(() => {
    setDeleteSelectedIds((current) => {
      const allowed = new Set(shareableResources.map((resource) => resource.id));
      const next = new Set([...current].filter((id) => allowed.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [shareableResources]);

  const selectedResource = shareableResources.find((resource) => resource.id === shareSelectedId) ?? null;
  const selectedDeleteResources = shareableResources.filter((resource) => deleteSelectedIds.has(resource.id));
  const trashItems = trashQuery.data ?? [];
  const shares = sharesQuery.data ?? [];
  const activeShares = shares.filter((share) => !share.revoked_at);
  const selectedResourceKind = selectedResource?.type === 'directory' ? '文件夹' : '文件';
  const currentFolderDownloadName = zipDownloadName(currentParent.title || '当前目录');
  const currentFolderDownloadHref = currentFolderZipURL({ parentId: currentParent.id, parentPath: currentParent.path });
  const selectedSingleFile = selectedDeleteResources.length === 1 && selectedDeleteResources[0].type === 'file' ? selectedDeleteResources[0] : null;
  const selectedSingleFileHref = selectedSingleFile?.download_url || selectedSingleFile?.raw_url || '';
  const selectedUsesDirectFileDownload = Boolean(selectedSingleFile && selectedSingleFileHref);
  const selectedDownloadName = selectedUsesDirectFileDownload ? selectedSingleFile!.name : zipDownloadName(selectedDeleteResources.length === 1 ? selectedDeleteResources[0].name : '选中资料');
  const selectedDownloadHref = selectedUsesDirectFileDownload
    ? selectedSingleFileHref
    : selectedDeleteResources.length > 0
      ? resourcesZipURL({ resourceIds: selectedDeleteResources.map((resource) => resource.id), name: selectedDeleteResources.length === 1 ? selectedDeleteResources[0].name : '选中资料' })
      : '';
  const selectedDownloadFeedbackTitle = selectedUsesDirectFileDownload ? '已开始下载' : '已开始打包下载';
  const selectedDownloadActionLabel = selectedUsesDirectFileDownload ? '下载选中文件' : `打包下载选中（${selectedDeleteResources.length}）`;
  const trashItemName = (item: TrashItem) => item.original_title || item.resource.name || item.original_path || '资源';
  const showManagementNotice = (kind: ManagementFeedbackKind, title: string, description: string) => {
    setManagementNotice({ kind, title, description });
  };
  const toggleDeleteSelection = (resourceId: string) => {
    setDeleteSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(resourceId)) {
        next.delete(resourceId);
      } else {
        next.add(resourceId);
      }
      return next;
    });
  };
  const refreshP6 = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['resources'] }),
      queryClient.invalidateQueries({ queryKey: ['trash'] }),
      queryClient.invalidateQueries({ queryKey: ['shares'] }),
      queryClient.invalidateQueries({ queryKey: ['search'] }),
      queryClient.invalidateQueries({ queryKey: ['favorites'] }),
      queryClient.invalidateQueries({ queryKey: ['recent'] })
    ]);
  };
  const deleteMutation = useMutation({
    mutationFn: async (resourcesToDelete: Resource[]) => {
      const trashItems: TrashItem[] = [];
      for (const resource of resourcesToDelete) {
        trashItems.push(await deleteResource(resource.id));
      }
      return trashItems;
    },
    onMutate: () => setManagementNotice(null),
    onSuccess: async (trashItems) => {
      setConfirmDelete(null);
      setDeleteSelectedIds(new Set());
      trashItems.forEach((trashItem) => onResourceDeleted(trashItem.resource_id));
      showManagementNotice(
        'success',
        '已移入回收站',
        trashItems.length === 1
          ? `「${trashItemName(trashItems[0])}」已进入回收站，7 天内可以恢复。`
          : `${trashItems.length} 项资料已进入回收站，7 天内可以恢复。`
      );
      await refreshP6();
    }
  });
  const restoreMutation = useMutation({
    mutationFn: (trashId: string) => restoreTrash(trashId),
    onMutate: () => setManagementNotice(null),
    onSuccess: async (trashItem) => {
      showManagementNotice('success', '已恢复资源', `「${trashItemName(trashItem)}」已恢复到原位置。`);
      await refreshP6();
    }
  });
  const purgeMutation = useMutation({
    mutationFn: (trashId: string) => purgeTrash(trashId),
    onMutate: () => setManagementNotice(null),
    onSuccess: async (_, trashId) => {
      const purgedItem = confirmPurge ?? trashItems.find((item) => item.id === trashId);
      setConfirmPurge(null);
      showManagementNotice('success', '已彻底删除', `「${purgedItem ? trashItemName(purgedItem) : '该资源'}」已从回收站移除。`);
      await refreshP6();
    }
  });
  const cleanupMutation = useMutation({
    mutationFn: cleanupTrash,
    onMutate: () => setManagementNotice(null),
    onSuccess: async (result) => {
      showManagementNotice(
        result.failed > 0 ? 'info' : 'success',
        result.failed > 0 ? '清理完成但有失败' : '过期项目清理完成',
        result.failed > 0 ? `已清理 ${result.removed} 项，${result.failed} 项需要稍后重试。` : `已清理 ${result.removed} 项，没有失败项。`
      );
      await refreshP6();
    }
  });
  const createShareMutation = useMutation({
    mutationFn: () => {
      if (!selectedResource) throw new Error('请选择要分享的资源');
      const days = Math.max(1, Math.min(90, Number.parseInt(shareDays, 10) || 7));
      return createShare({
        resource_id: selectedResource.id,
        password: sharePassword.trim() || undefined,
        allow_preview: allowPreview,
        allow_download: allowDownload,
        allow_zip_download: false,
        expires_at: new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString()
      });
    },
    onMutate: () => setManagementNotice(null),
    onSuccess: async (share) => {
      setCreatedShare(share);
      setCopyState('');
      setSharePassword('');
      showManagementNotice('success', '分享链接已生成', `「${share.resource.name || share.root_resource_id}」的公开入口已创建，只开放当前选择的资源范围。`);
      await queryClient.invalidateQueries({ queryKey: ['shares'] });
    }
  });
  const revokeMutation = useMutation({
    mutationFn: (shareId: string) => revokeShare(shareId),
    onMutate: () => setManagementNotice(null),
    onSuccess: async (_, shareId) => {
      const revokedShare = shares.find((share) => share.id === shareId);
      showManagementNotice('success', '分享已撤销', `「${revokedShare?.resource.name || '该资源'}」的公开链接已失效。`);
      await queryClient.invalidateQueries({ queryKey: ['shares'] });
    }
  });
  const createdShareURL = createdShare?.url ? absoluteShareURL(createdShare.url) : '';
  const panelError =
    errorMessage(deleteMutation.error) ||
    errorMessage(restoreMutation.error) ||
    errorMessage(purgeMutation.error) ||
    errorMessage(cleanupMutation.error) ||
    errorMessage(createShareMutation.error) ||
    errorMessage(revokeMutation.error);
  const busyFeedback: ManagementFeedback | null = deleteMutation.isPending
    ? { kind: 'busy', title: '正在移入回收站', description: '正在按顺序处理所选资源，完成前不会触发彻底删除。' }
    : restoreMutation.isPending
      ? { kind: 'busy', title: '正在恢复资源', description: '正在恢复到原位置，并刷新资料列表。' }
      : purgeMutation.isPending
        ? { kind: 'busy', title: '正在彻底删除', description: '正在执行已确认的回收站删除操作。' }
        : cleanupMutation.isPending
          ? { kind: 'busy', title: '正在清理过期项目', description: '只会清理已经过期的回收站项目。' }
          : createShareMutation.isPending
            ? { kind: 'busy', title: '正在生成分享链接', description: '正在应用预览、下载、密码和有效期设置。' }
            : revokeMutation.isPending
              ? { kind: 'busy', title: '正在撤销分享', description: '公开链接会在撤销完成后立即失效。' }
              : null;
  const activeManagementFeedback: ManagementFeedback | null = panelError
    ? { kind: 'error', title: '操作失败', description: panelError }
    : busyFeedback ?? managementNotice;
  const feedbackScrollKey = activeManagementFeedback ? `${activeManagementFeedback.kind}:${activeManagementFeedback.title}:${activeManagementFeedback.description}` : '';

  useEffect(() => {
    if (!feedbackScrollKey) return undefined;
    const frame = window.requestAnimationFrame(() => {
      managementFeedbackRef.current?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [feedbackScrollKey]);

  return (
    <section className="management-panel" aria-label="回收站和分享管理">
      <div className="management-header">
        <div className="management-hero-copy">
          <div className="mini-heading">
            <ShieldCheck size={16} />
            <span>安全管理</span>
          </div>
          <p>回收站默认保留 7 天；分享链接只开放你选中的资源范围。</p>
          <div className="management-summary" aria-label="安全管理摘要">
            <span>
              <strong>{shareableResources.length}</strong>
              项可管理
            </span>
            <span>
              <strong>{selectedDeleteResources.length}</strong>
              项已选
            </span>
            <span>
              <strong>{activeShares.length}</strong>
              个有效分享
            </span>
            <span>
              <strong>{trashItems.length}</strong>
              项在回收站
            </span>
          </div>
        </div>
        <button className="icon-button" type="button" title="关闭" onClick={onClose}>
          <X size={18} />
        </button>
      </div>
      {activeManagementFeedback ? (
        <ManagementFeedbackPanel
          feedback={activeManagementFeedback}
          panelRef={(node) => {
            managementFeedbackRef.current = node;
          }}
          onDismiss={!panelError && activeManagementFeedback.kind !== 'busy' ? () => setManagementNotice(null) : undefined}
        />
      ) : null}
      <div className="management-grid">
        <section className="management-card management-card-resources">
          <div className="management-card-title">
            <div className="mini-heading">
              <PanelRight size={15} />
              <span>选择资源</span>
            </div>
            <small>{currentParent.title || '资料库'} · 选择资料后可打包下载、移入回收站或设为分享目标。</small>
          </div>
          <div className="manager-resource-toolbar">
            <div>
              <strong>{selectedDeleteResources.length > 0 ? `已选择 ${selectedDeleteResources.length} 项` : '未选择删除项'}</strong>
              <small>{selectedResource ? `分享目标：${selectedResource.name}` : '请选择要分享的资源'}</small>
            </div>
            <div className="manager-selection-actions">
              <button className="text-action" type="button" disabled={shareableResources.length === 0} onClick={() => setDeleteSelectedIds(new Set(shareableResources.map((resource) => resource.id)))}>
                <span>全选</span>
              </button>
              <button className="text-action" type="button" disabled={deleteSelectedIds.size === 0} onClick={() => setDeleteSelectedIds(new Set())}>
                <span>清空</span>
              </button>
            </div>
          </div>
          <div className="manager-resource-list">
            {shareableResources.map((resource) => (
              <div
                key={resource.id}
                className={[
                  'manager-resource',
                  deleteSelectedIds.has(resource.id) ? 'selected' : '',
                  resource.id === shareSelectedId ? 'share-selected' : ''
                ].filter(Boolean).join(' ')}
              >
                <label className="manager-select-box">
                  <input type="checkbox" checked={deleteSelectedIds.has(resource.id)} onChange={() => toggleDeleteSelection(resource.id)} />
                  {resource.type === 'directory' ? <Folder size={17} /> : <FileText size={17} />}
                  <span>
                    <strong>{resource.name}</strong>
                    <small>{resource.path || '资料库'}</small>
                  </span>
                </label>
                <button className={resource.id === shareSelectedId ? 'text-action active' : 'text-action'} type="button" onClick={() => setShareSelectedId(resource.id)}>
                  <Share2 size={14} />
                  <span>分享</span>
                </button>
              </div>
            ))}
            {shareableResources.length === 0 ? (
              <ManagementStateCard
                icon={<PanelRight size={20} />}
                title="当前列表没有可管理资源"
                description="当前目录没有可删除、分享或打包下载的资料。返回资料列表后可以导入文件或切换目录。"
                actions={(
                  <button className="text-action" type="button" onClick={onClose}>
                    <ListTree size={14} />
                    <span>返回资料列表</span>
                  </button>
                )}
              />
            ) : null}
          </div>
          <div className="manager-bulk-bar">
            <div className="manager-download-actions">
              <a
                className="text-action"
                href={currentFolderDownloadHref}
                download={currentFolderDownloadName}
                onClick={() => showManagementNotice('success', '已开始打包下载', `已开始下载：${currentFolderDownloadName}`)}
              >
                <Download size={15} />
                <span>下载当前目录</span>
              </a>
              {selectedDeleteResources.length > 0 ? (
                <a
                  className="text-action"
                  href={selectedDownloadHref}
                  download={selectedDownloadName}
                  onClick={() => showManagementNotice('success', selectedDownloadFeedbackTitle, `已开始下载：${selectedDownloadName}`)}
                >
                  <Download size={15} />
                  <span>{selectedDownloadActionLabel}</span>
                </a>
              ) : (
                <button className="text-action" type="button" disabled>
                  <Download size={15} />
                  <span>打包下载选中</span>
                </button>
              )}
            </div>
            <button className="danger-button" type="button" disabled={selectedDeleteResources.length === 0} onClick={() => setConfirmDelete(selectedDeleteResources)}>
              <Trash2 size={15} />
              <span>{selectedDeleteResources.length > 0 ? `移入回收站（${selectedDeleteResources.length}）` : '移入回收站'}</span>
            </button>
          </div>
        </section>
        <section className="management-card management-card-share">
          <div className="management-card-title">
            <div className="mini-heading">
              <Share2 size={15} />
              <span>创建分享</span>
            </div>
            <small>分享链接默认只允许预览；开启下载需要显式勾选。</small>
          </div>
          <div className="share-target" aria-label="当前分享对象">
            {selectedResource ? (
              <>
                <span>{selectedResourceKind}</span>
                <strong>{selectedResource.name}</strong>
                <small>{selectedResource.path || '资料库'}</small>
              </>
            ) : (
              <>
                <span>未选择</span>
                <strong>请选择资源</strong>
                <small>从左侧列表点击“分享”设为目标</small>
              </>
            )}
          </div>
          <form
            className="share-form"
            onSubmit={(event) => {
              event.preventDefault();
              createShareMutation.mutate();
            }}
          >
            <label>
              有效期
              <select value={shareDays} onChange={(event) => setShareDays(event.target.value)}>
                <option value="1">1 天</option>
                <option value="7">7 天</option>
                <option value="30">30 天</option>
                <option value="90">90 天</option>
              </select>
            </label>
            <label>
              访问密码
              <input value={sharePassword} onChange={(event) => setSharePassword(event.target.value)} placeholder="可选，至少 6 位" />
            </label>
            <div className="toggle-row">
              <label>
                <input type="checkbox" checked={allowPreview} onChange={(event) => setAllowPreview(event.target.checked)} />
                允许预览
              </label>
              <label>
                <input type="checkbox" checked={allowDownload} onChange={(event) => setAllowDownload(event.target.checked)} />
                允许下载
              </label>
            </div>
            <button className="primary-button" type="submit" disabled={!selectedResource || createShareMutation.isPending}>
              <Link2 size={15} />
              <span>{createShareMutation.isPending ? '创建中' : '生成链接'}</span>
            </button>
          </form>
          {createdShareURL ? (
            <div className="created-share">
              <input value={createdShareURL} readOnly aria-label="新分享链接" />
              <button
                className="icon-button"
                type="button"
                title="复制分享链接"
                onClick={async () => {
                  try {
                    await navigator.clipboard?.writeText(createdShareURL);
                    setCopyState('已复制');
                    showManagementNotice('success', '分享链接已复制', '可以粘贴给需要访问这份资料的人。');
                  } catch {
                    setCopyState('复制失败');
                    showManagementNotice('error', '复制失败', '浏览器没有开放剪贴板权限，可以直接复制输入框中的链接。');
                  }
                }}
              >
                <Copy size={17} />
              </button>
              {copyState ? <small>{copyState}</small> : null}
            </div>
          ) : null}
        </section>
        <section className="management-card management-card-trash">
          <div className="management-card-title">
            <div className="mini-heading">
              <ArchiveRestore size={15} />
              <span>回收站</span>
            </div>
            <small>恢复会回到原位置；彻底删除仍需二次确认。</small>
          </div>
          <div className="manager-list">
            {trashItems.map((item) => (
              <div key={item.id} className="manager-list-row">
                <span>
                  <strong>{item.original_title || item.resource.name || item.original_path}</strong>
                  <small>{formatDate(item.deleted_at)} 删除 · {formatDate(item.expires_at)} 清理</small>
                </span>
                <button className="icon-button" type="button" title="恢复" onClick={() => restoreMutation.mutate(item.id)}>
                  <RotateCcw size={16} />
                </button>
                <button className="icon-button danger" type="button" title="彻底删除" onClick={() => setConfirmPurge(item)}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {trashQuery.isLoading ? (
              <ManagementStateCard
                icon={<RefreshCw className="state-spin" size={20} />}
                title="正在加载回收站"
                description="正在读取可恢复和即将清理的资源，期间不会改变任何文件。"
              />
            ) : null}
            {trashQuery.isError ? (
              <ManagementStateCard
                icon={<ArchiveRestore size={20} />}
                title="回收站加载失败"
                description="回收站暂时没有刷新成功，可以重试，不会触发恢复或彻底删除。"
                actions={(
                  <button className="text-action" type="button" onClick={() => trashQuery.refetch()} disabled={trashQuery.isFetching}>
                    <RefreshCw className={trashQuery.isFetching ? 'state-spin' : ''} size={14} />
                    <span>{trashQuery.isFetching ? '正在重试' : '重试加载'}</span>
                  </button>
                )}
              />
            ) : null}
            {!trashQuery.isLoading && !trashQuery.isError && trashItems.length === 0 ? (
              <ManagementStateCard
                icon={<ArchiveRestore size={20} />}
                title="回收站为空"
                description="当前没有待恢复或等待清理的资源。删除的资料会先进入这里，默认保留 7 天。"
              />
            ) : null}
          </div>
          <button className="text-action" type="button" onClick={() => cleanupMutation.mutate()} disabled={cleanupMutation.isPending}>
            <RefreshCw size={14} />
            <span>{cleanupMutation.isPending ? '清理中' : '清理过期项目'}</span>
          </button>
        </section>
        <section className="management-card management-card-shares">
          <div className="management-card-title">
            <div className="mini-heading">
              <KeyRound size={15} />
              <span>现有分享</span>
            </div>
            <small>撤销后公开链接立即失效，历史记录保留在列表中。</small>
          </div>
          <div className="manager-list">
            {shares.map((share) => (
              <div key={share.id} className={share.revoked_at ? 'manager-list-row muted' : 'manager-list-row'}>
                <span>
                  <strong>{share.resource.name || share.root_resource_id}</strong>
                  <small>
                    {share.requires_password ? '有密码' : '无密码'} · {share.allow_download ? '可下载' : '仅预览'} · {formatDate(share.expires_at)}
                  </small>
                </span>
                <button className="icon-button danger" type="button" title="撤销分享" disabled={!!share.revoked_at} onClick={() => revokeMutation.mutate(share.id)}>
                  <X size={16} />
                </button>
              </div>
            ))}
            {sharesQuery.isLoading ? (
              <ManagementStateCard
                icon={<RefreshCw className="state-spin" size={20} />}
                title="正在加载分享"
                description="正在读取仍有效或已撤销的公开链接，分享权限不会因此改变。"
              />
            ) : null}
            {sharesQuery.isError ? (
              <ManagementStateCard
                icon={<KeyRound size={20} />}
                title="分享列表加载失败"
                description="现有分享暂时没有刷新成功，可以重试；撤销和新建分享仍保持原安全边界。"
                actions={(
                  <button className="text-action" type="button" onClick={() => sharesQuery.refetch()} disabled={sharesQuery.isFetching}>
                    <RefreshCw className={sharesQuery.isFetching ? 'state-spin' : ''} size={14} />
                    <span>{sharesQuery.isFetching ? '正在重试' : '重试加载'}</span>
                  </button>
                )}
              />
            ) : null}
            {!sharesQuery.isLoading && !sharesQuery.isError && shares.length === 0 ? (
              <ManagementStateCard
                icon={<KeyRound size={20} />}
                title="暂无分享链接"
                description="还没有公开分享。选择左侧资源并生成链接后，会在这里集中管理和撤销。"
              />
            ) : null}
          </div>
        </section>
      </div>
      {confirmDelete ? (
        <ConfirmDialog
          title="移入回收站"
          message={confirmDelete.length === 1 ? `将「${confirmDelete[0].name}」移入回收站，7 天内可以恢复。` : `将选中的 ${confirmDelete.length} 个资源移入回收站，7 天内可以恢复。`}
          confirmLabel={deleteMutation.isPending ? '处理中' : '确认删除'}
          busy={deleteMutation.isPending}
          danger
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => deleteMutation.mutate(confirmDelete)}
        />
      ) : null}
      {confirmPurge ? (
        <ConfirmDialog
          title="彻底删除"
          message={`将「${confirmPurge.original_title || confirmPurge.original_path}」从回收站彻底删除，此操作不可恢复。`}
          confirmLabel={purgeMutation.isPending ? '处理中' : '彻底删除'}
          busy={purgeMutation.isPending}
          danger
          onCancel={() => setConfirmPurge(null)}
          onConfirm={() => purgeMutation.mutate(confirmPurge.id)}
        />
      ) : null}
    </section>
  );
}

function ManagementFeedbackPanel({ feedback, panelRef, onDismiss }: { feedback: ManagementFeedback; panelRef?: (node: HTMLElement | null) => void; onDismiss?: () => void }) {
  const icon = feedback.kind === 'busy'
    ? <RefreshCw className="state-spin" size={20} />
    : feedback.kind === 'error'
      ? <AlertTriangle size={20} />
      : feedback.kind === 'success'
        ? <CheckCircle2 size={20} />
        : <ShieldCheck size={20} />;
  const label = feedback.kind === 'busy' ? '处理中' : feedback.kind === 'error' ? '需要处理' : feedback.kind === 'success' ? '已完成' : '安全提示';
  return (
    <section ref={panelRef} className={`management-feedback-panel ${feedback.kind}`} role={feedback.kind === 'error' ? 'alert' : 'status'} aria-live="polite">
      <div className="management-feedback-icon" aria-hidden="true">{icon}</div>
      <div className="management-feedback-copy">
        <span>{label}</span>
        <strong>{feedback.title}</strong>
        <p>{feedback.description}</p>
      </div>
      {onDismiss ? (
        <button className="icon-button" type="button" title="关闭提示" aria-label="关闭提示" onClick={onDismiss}>
          <X size={16} />
        </button>
      ) : null}
    </section>
  );
}

function ManagementStateCard({ icon, title, description, actions }: { icon: ReactNode; title: string; description: string; actions?: ReactNode }) {
  return (
    <div className="management-state-card">
      <span className="management-state-icon" aria-hidden="true">{icon}</span>
      <strong>{title}</strong>
      <p>{description}</p>
      {actions ? <div className="management-state-actions">{actions}</div> : null}
    </div>
  );
}

function KnowledgeStateCard({ icon, title, description, actions, compact = false }: { icon: ReactNode; title: string; description: string; actions?: ReactNode; compact?: boolean }) {
  return (
    <div className={compact ? 'knowledge-state-card compact' : 'knowledge-state-card'}>
      <span className="knowledge-state-icon" aria-hidden="true">{icon}</span>
      <div className="knowledge-state-copy">
        <strong>{title}</strong>
        <p>{description}</p>
      </div>
      {actions ? <div className="knowledge-state-actions">{actions}</div> : null}
    </div>
  );
}

function ConfirmDialog({
  title,
  message,
  confirmLabel,
  busy,
  danger,
  onCancel,
  onConfirm
}: {
  title: string;
  message: string;
  confirmLabel: string;
  busy: boolean;
  danger?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="confirm-backdrop" role="presentation">
      <div className="confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
        <div>
          <h3 id="confirm-title">{title}</h3>
          <p>{message}</p>
        </div>
        <div className="confirm-actions">
          <button className="text-action" type="button" onClick={onCancel} disabled={busy}>
            取消
          </button>
          <button className={danger ? 'danger-button' : 'primary-button'} type="button" onClick={onConfirm} disabled={busy}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function KnowledgeSidePanel({
  enabled,
  organizationTargets,
  onOpen,
  onFilterByTag,
  onFilterByCollection
}: {
  enabled: boolean;
  organizationTargets: Resource[];
  onOpen: (result: SearchResult) => void;
  onFilterByTag: (tag: KnowledgeTag) => void;
  onFilterByCollection: (collection: CollectionInfo) => void;
}) {
  const queryClient = useQueryClient();
  const [tagName, setTagName] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [collectionTitle, setCollectionTitle] = useState('');
  const [organizerNewCollectionTitle, setOrganizerNewCollectionTitle] = useState('');
  const [organizerCollectionId, setOrganizerCollectionId] = useState('');
  const [organizerNotice, setOrganizerNotice] = useState<ManagementFeedback | null>(null);
  const [organizerNoticeCollection, setOrganizerNoticeCollection] = useState<CollectionInfo | null>(null);
  const sideQueryOptions = { enabled, staleTime: workspaceResourcesStaleMs };
  const tagsQuery = useQuery({ queryKey: ['tags'], queryFn: listTags, ...sideQueryOptions });
  const collectionsQuery = useQuery({ queryKey: ['collections'], queryFn: listCollections, ...sideQueryOptions });
  const favoritesQuery = useQuery({ queryKey: ['favorites'], queryFn: listFavorites, ...sideQueryOptions });
  const recentQuery = useQuery({ queryKey: ['recent'], queryFn: listRecent, ...sideQueryOptions });
  const statusQuery = useQuery({ queryKey: ['search-status'], queryFn: getSearchStatus, enabled, staleTime: workspaceResourcesStaleMs, refetchInterval: enabled ? 10000 : false });
  const tagMutation = useMutation({
    mutationFn: (name: string) => createTag(name),
    onSuccess: async () => {
      setTagName('');
      await queryClient.invalidateQueries({ queryKey: ['tags'] });
    }
  });
  const linkMutation = useMutation({
    mutationFn: createExternalLink,
    onSuccess: async () => {
      setLinkTitle('');
      setLinkUrl('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['search'] }),
        queryClient.invalidateQueries({ queryKey: ['recent'] })
      ]);
    }
  });
  const collectionMutation = useMutation({
    mutationFn: createCollection,
    onSuccess: async () => {
      setCollectionTitle('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['collections'] }),
        queryClient.invalidateQueries({ queryKey: ['search'] })
      ]);
    }
  });
  const addToCollectionMutation = useMutation({
    mutationFn: async ({ collectionId, resources }: { collectionId: string; resources: Resource[] }) => {
      for (const resource of resources) {
        await addCollectionItem(collectionId, resource.id);
      }
      return resources.length;
    },
    onMutate: () => {
      setOrganizerNotice(null);
      setOrganizerNoticeCollection(null);
    },
    onSuccess: async (count, variables) => {
      const collection = collections.find((item) => item.resource.id === variables.collectionId);
      setOrganizerNotice({
        kind: 'success',
        title: '已加入合集',
        description: count === 1
          ? `「${variables.resources[0].name}」已加入「${collection?.resource.name || '合集'}」。`
          : `${count} 项资料已加入「${collection?.resource.name || '合集'}」。`
      });
      setOrganizerNoticeCollection(collection ?? null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['collections'] }),
        queryClient.invalidateQueries({ queryKey: ['search'] })
      ]);
    }
  });
  const createAndAddCollectionMutation = useMutation({
    mutationFn: async ({ title, resources }: { title: string; resources: Resource[] }) => {
      const collection = await createCollection({ title });
      for (const resource of resources) {
        await addCollectionItem(collection.resource.id, resource.id);
      }
      return { collection, count: resources.length, resources };
    },
    onMutate: () => {
      setOrganizerNotice(null);
      setOrganizerNoticeCollection(null);
    },
    onSuccess: async ({ collection, count, resources }) => {
      setOrganizerNewCollectionTitle('');
      setOrganizerCollectionId(collection.resource.id);
      setOrganizerNotice({
        kind: 'success',
        title: '已新建合集',
        description: count === 1
          ? `已新建「${collection.resource.name}」，并加入「${resources[0].name}」。`
          : `已新建「${collection.resource.name}」，并加入 ${count} 项资料。`
      });
      setOrganizerNoticeCollection(collection);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['collections'] }),
        queryClient.invalidateQueries({ queryKey: ['search'] })
      ]);
    }
  });
  const favorites = favoritesQuery.data ?? [];
  const recent = recentQuery.data ?? [];
  const tags = tagsQuery.data ?? [];
  const collections = collectionsQuery.data ?? [];
  const searchStatus = statusQuery.data;
  const indexLabel = searchStatus ? `${searchStatus.ready}/${searchStatus.total}` : '待同步';
  const pendingIndexCount = searchStatus ? searchStatus.queued + searchStatus.indexing : 0;
  const collectionItems = collections.reduce((total, collection) => total + collection.item_count, 0);
  const organizerBusy = addToCollectionMutation.isPending || createAndAddCollectionMutation.isPending;
  const organizerError = errorMessage(addToCollectionMutation.error || createAndAddCollectionMutation.error);
  const organizerTargetSummary = organizationTargets.length === 0
    ? '待选择'
    : organizationTargets.length === 1 ? organizationTargets[0].name : `${organizationTargets.length} 项资料`;

  useEffect(() => {
    if (organizerCollectionId && collections.some((collection) => collection.resource.id === organizerCollectionId)) return;
    setOrganizerCollectionId(collections[0]?.resource.id ?? '');
  }, [collections, organizerCollectionId]);

  const renderMiniResource = (result: SearchResult) => {
    const subtitle = result.external_link?.domain || result.resource.path || result.snippet || result.resource.type;
    return (
      <button key={result.resource.id} className="mini-resource" type="button" onClick={() => onOpen(result)}>
        <span>{result.resource.name}</span>
        <small>{subtitle}</small>
      </button>
    );
  };

  return (
    <div className="knowledge-panel">
      <section className="knowledge-hero" aria-label="学习组织概览">
        <div className="mini-heading">
          <BookOpen size={15} />
          <span>学习组织</span>
        </div>
        <p>从素材到复习路径</p>
        <div className="knowledge-summary">
          <span>
            <strong>{favorites.length}</strong> 个收藏
          </span>
          <span>
            <strong>{recent.length}</strong> 个最近
          </span>
          <span>
            <strong>{tags.length}</strong> 个标签
          </span>
          <span>
            <strong>{indexLabel}</strong> 索引
          </span>
        </div>
        <div className="knowledge-status" aria-label="索引状态">
          <span>索引 {indexLabel}</span>
          {pendingIndexCount ? <span>{pendingIndexCount} 待处理</span> : null}
          {searchStatus?.failed ? <span>{searchStatus.failed} 失败</span> : null}
        </div>
      </section>
      <section className="knowledge-block knowledge-organizer" aria-label="整理当前资料">
        <div className="knowledge-block-title">
          <div className="mini-heading">
            <PanelRight size={15} />
            <span>整理资料</span>
          </div>
          <small>{organizerTargetSummary}</small>
        </div>
        {organizationTargets.length > 0 ? (
          <>
            <div className="organizer-target-list">
              {organizationTargets.slice(0, 3).map((resource) => (
                <span key={resource.id}>
                  {resource.type === 'directory' ? <Folder size={14} /> : <FileText size={14} />}
                  <strong>{resource.name}</strong>
                </span>
              ))}
              {organizationTargets.length > 3 ? <span>{`+${organizationTargets.length - 3}`}</span> : null}
            </div>
            <div className="organizer-actions">
              <label className="organizer-select">
                <span>合集</span>
                <select value={organizerCollectionId} disabled={collections.length === 0 || organizerBusy} onChange={(event) => setOrganizerCollectionId(event.target.value)}>
                  {collections.length ? collections.map((collection) => (
                    <option key={collection.resource.id} value={collection.resource.id}>
                      {collection.resource.name}
                    </option>
                  )) : (
                    <option value="">先创建合集</option>
                  )}
                </select>
              </label>
              <button
                className="text-action"
                type="button"
                disabled={!organizerCollectionId || collections.length === 0 || organizerBusy}
                onClick={() => addToCollectionMutation.mutate({ collectionId: organizerCollectionId, resources: organizationTargets })}
              >
                <Plus size={14} />
                <span>{addToCollectionMutation.isPending ? '加入中' : '加入合集'}</span>
              </button>
            </div>
            <form
              className="organizer-create-form"
              aria-label="新建合集并整理"
              onSubmit={(event) => {
                event.preventDefault();
                const title = organizerNewCollectionTitle.trim();
                if (title) createAndAddCollectionMutation.mutate({ title, resources: organizationTargets });
              }}
            >
              <input
                value={organizerNewCollectionTitle}
                onChange={(event) => setOrganizerNewCollectionTitle(event.target.value)}
                placeholder="新建合集名称"
                aria-label="新建合集名称"
                disabled={organizerBusy}
              />
              <button className="text-action" type="submit" disabled={!organizerNewCollectionTitle.trim() || organizerBusy}>
                <FolderPlus size={14} />
                <span>{createAndAddCollectionMutation.isPending ? '创建中' : '新建并加入'}</span>
              </button>
            </form>
            {organizerError ? (
              <div className="organizer-feedback error" role="status">{organizerError}</div>
            ) : organizerNotice ? (
              <div className="organizer-feedback success" role="status">
                <span>{organizerNotice.description}</span>
                {organizerNoticeCollection ? (
                  <button className="text-action compact" type="button" onClick={() => onFilterByCollection(organizerNoticeCollection)}>
                    <Folder size={14} />
                    <span>查看合集</span>
                  </button>
                ) : null}
              </div>
            ) : null}
          </>
        ) : (
          <KnowledgeStateCard compact icon={<PanelRight size={18} />} title="没有待整理资料" description="打开一个文件，或在资料列表进入批量选择后再整理。" />
        )}
      </section>
      <section className="knowledge-block knowledge-activity-block">
        <div className="knowledge-list" aria-label="收藏">
          <div className="knowledge-list-head">
            <div className="mini-heading">
              <Star size={15} />
              <span>收藏</span>
            </div>
            <small>{favorites.length}</small>
          </div>
          {favoritesQuery.isLoading ? (
            <KnowledgeStateCard
              icon={<RefreshCw className="state-spin" size={18} />}
              title="正在加载收藏"
              description="正在同步你的重点资料。"
            />
          ) : null}
          {favoritesQuery.isError ? (
            <KnowledgeStateCard
              icon={<Star size={18} />}
              title="收藏加载失败"
              description="收藏列表暂时没有刷新成功，可以重试。"
              actions={(
                <button className="text-action" type="button" onClick={() => favoritesQuery.refetch()} disabled={favoritesQuery.isFetching}>
                  <RefreshCw className={favoritesQuery.isFetching ? 'state-spin' : ''} size={14} />
                  <span>{favoritesQuery.isFetching ? '正在重试' : '重试加载'}</span>
                </button>
              )}
            />
          ) : null}
          {!favoritesQuery.isLoading && !favoritesQuery.isError && favorites.length ? favorites.slice(0, 4).map(renderMiniResource) : null}
          {!favoritesQuery.isLoading && !favoritesQuery.isError && favorites.length === 0 ? (
            <KnowledgeStateCard
              icon={<Star size={18} />}
              title="还没有收藏资料"
              description="在资料行点击星标，把常用讲义和复习材料固定到这里。"
            />
          ) : null}
        </div>
        <div className="knowledge-list" aria-label="最近打开">
          <div className="knowledge-list-head">
            <div className="mini-heading">
              <BookOpen size={15} />
              <span>最近打开</span>
            </div>
            <small>{recent.length}</small>
          </div>
          {recentQuery.isLoading ? (
            <KnowledgeStateCard
              icon={<RefreshCw className="state-spin" size={18} />}
              title="正在加载最近记录"
              description="正在整理刚打开过的学习资料。"
            />
          ) : null}
          {recentQuery.isError ? (
            <KnowledgeStateCard
              icon={<BookOpen size={18} />}
              title="最近记录加载失败"
              description="最近打开暂时没有刷新成功，可以重试。"
              actions={(
                <button className="text-action" type="button" onClick={() => recentQuery.refetch()} disabled={recentQuery.isFetching}>
                  <RefreshCw className={recentQuery.isFetching ? 'state-spin' : ''} size={14} />
                  <span>{recentQuery.isFetching ? '正在重试' : '重试加载'}</span>
                </button>
              )}
            />
          ) : null}
          {!recentQuery.isLoading && !recentQuery.isError && recent.length ? recent.slice(0, 4).map(renderMiniResource) : null}
          {!recentQuery.isLoading && !recentQuery.isError && recent.length === 0 ? (
            <KnowledgeStateCard
              icon={<BookOpen size={18} />}
              title="还没有最近打开"
              description="预览或打开资料后会自动记录，方便回到刚学习的位置。"
            />
          ) : null}
        </div>
      </section>
      <section className="knowledge-block">
        <div className="knowledge-block-title">
          <div className="mini-heading">
            <Tag size={15} />
            <span>标签</span>
          </div>
          <small>{tags.length} 个</small>
        </div>
        <form
          className="mini-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (tagName.trim()) tagMutation.mutate(tagName.trim());
          }}
        >
          <input value={tagName} onChange={(event) => setTagName(event.target.value)} placeholder="新标签" />
          <button type="submit" title="创建标签" disabled={tagMutation.isPending || !tagName.trim()}>
            <Plus size={15} />
          </button>
        </form>
        {tagsQuery.isLoading ? (
          <KnowledgeStateCard compact icon={<RefreshCw className="state-spin" size={18} />} title="正在加载标签" description="正在读取主题标签。" />
        ) : null}
        {tagsQuery.isError ? (
          <KnowledgeStateCard
            compact
            icon={<Tag size={18} />}
            title="标签加载失败"
            description="标签列表暂时没有刷新成功，可以重试。"
            actions={(
              <button className="text-action" type="button" onClick={() => tagsQuery.refetch()} disabled={tagsQuery.isFetching}>
                <RefreshCw className={tagsQuery.isFetching ? 'state-spin' : ''} size={14} />
                <span>{tagsQuery.isFetching ? '正在重试' : '重试加载'}</span>
              </button>
            )}
          />
        ) : null}
        {!tagsQuery.isLoading && !tagsQuery.isError && tags.length ? (
          <div className="tag-cloud">
            {tags.map((tag) => (
              <button key={tag.id} type="button" title={`筛选标签：${tag.name}`} onClick={() => onFilterByTag(tag)}>
                {tag.name}
              </button>
            ))}
          </div>
        ) : null}
        {!tagsQuery.isLoading && !tagsQuery.isError && tags.length === 0 ? (
          <KnowledgeStateCard compact icon={<Tag size={18} />} title="还没有标签" description="用上方输入框创建主题标签，后续可把资料按章节、课程或考试范围归类。" />
        ) : null}
      </section>
      <section className="knowledge-block">
        <div className="knowledge-block-title">
          <div className="mini-heading">
            <ExternalLink size={15} />
            <span>外链</span>
          </div>
          <small>URL</small>
        </div>
        <form
          className="mini-form stacked"
          onSubmit={(event) => {
            event.preventDefault();
            if (linkUrl.trim()) linkMutation.mutate({ title: linkTitle.trim(), url: linkUrl.trim() });
          }}
        >
          <input value={linkTitle} onChange={(event) => setLinkTitle(event.target.value)} placeholder="标题" />
          <input value={linkUrl} onChange={(event) => setLinkUrl(event.target.value)} placeholder="https://..." />
          <button type="submit" disabled={linkMutation.isPending || !linkUrl.trim()}>
            {linkMutation.isPending ? '添加中' : '添加外链'}
          </button>
        </form>
      </section>
      <section className="knowledge-block">
        <div className="knowledge-block-title">
          <div className="mini-heading">
            <Folder size={15} />
            <span>合集</span>
          </div>
          <small>{collectionItems} 项</small>
        </div>
        <form
          className="mini-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (collectionTitle.trim()) collectionMutation.mutate({ title: collectionTitle.trim() });
          }}
        >
          <input value={collectionTitle} onChange={(event) => setCollectionTitle(event.target.value)} placeholder="新合集" />
          <button type="submit" title="创建合集" disabled={collectionMutation.isPending || !collectionTitle.trim()}>
            <Plus size={15} />
          </button>
        </form>
        <div className="collection-list">
          {collectionsQuery.isLoading ? (
            <KnowledgeStateCard compact icon={<RefreshCw className="state-spin" size={18} />} title="正在加载合集" description="正在同步复习合集。" />
          ) : null}
          {collectionsQuery.isError ? (
            <KnowledgeStateCard
              compact
              icon={<Folder size={18} />}
              title="合集加载失败"
              description="合集列表暂时没有刷新成功，可以重试。"
              actions={(
                <button className="text-action" type="button" onClick={() => collectionsQuery.refetch()} disabled={collectionsQuery.isFetching}>
                  <RefreshCw className={collectionsQuery.isFetching ? 'state-spin' : ''} size={14} />
                  <span>{collectionsQuery.isFetching ? '正在重试' : '重试加载'}</span>
                </button>
              )}
            />
          ) : null}
          {!collectionsQuery.isLoading && !collectionsQuery.isError && collections.length ? (
            collections.slice(0, 5).map((collection) => (
              <button key={collection.resource.id} className="collection-chip" type="button" title={`筛选合集：${collection.resource.name}`} onClick={() => onFilterByCollection(collection)}>
                <span>{collection.resource.name}</span>
                <small>{collection.item_count}</small>
              </button>
            ))
          ) : null}
          {!collectionsQuery.isLoading && !collectionsQuery.isError && collections.length === 0 ? (
            <KnowledgeStateCard compact icon={<Folder size={18} />} title="还没有合集" description="用上方输入框创建复习合集，把同一主题的讲义、外链和笔记集中整理。" />
          ) : null}
        </div>
      </section>
    </div>
  );
}

function ResourceLine({
  resource,
  onOpen,
  isFavorite = false,
  favoritePending = false,
  onToggleFavorite,
  selectable = false,
  selected = false,
  onToggleSelected,
  downloadHref,
  downloadName,
  onDownloadStart,
  onPrefetch,
  compact = false
}: {
  resource: Resource;
  onOpen?: () => void;
  onPrefetch?: () => void;
  isFavorite?: boolean;
  favoritePending?: boolean;
  onToggleFavorite?: () => void;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelected?: () => void;
  downloadHref?: string;
  downloadName?: string;
  onDownloadStart?: () => void;
  compact?: boolean;
}) {
  const icon = resource.type === 'directory' || resource.type === 'collection'
    ? <Folder size={20} />
    : resource.thumbnail_url ? <img src={resource.thumbnail_url} alt="" loading="lazy" decoding="async" /> : <FileText size={20} />;
  const rowActionLabel = resource.type === 'directory' ? '打开' : resource.type === 'external_link' ? '访问' : '预览';
  const downloadTitle = resource.type === 'directory' ? '打包下载此文件夹' : '下载此文件';

  return (
    <article
      className={[compact ? 'resource-row compact-row' : 'resource-row', selected ? 'selected' : '', selectable ? 'selectable' : ''].filter(Boolean).join(' ')}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={`${rowActionLabel} ${resource.name}`}
      onPointerEnter={onPrefetch}
      onFocus={onPrefetch}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen?.();
        }
      }}
    >
      {selectable ? (
        <label className="resource-select-box" onClick={(event) => event.stopPropagation()}>
          <input
            type="checkbox"
            checked={selected}
            aria-label={`选择 ${resource.name}`}
            onChange={(event) => {
              event.stopPropagation();
              onToggleSelected?.();
            }}
            onClick={(event) => event.stopPropagation()}
          />
        </label>
      ) : null}
      <div className="resource-icon">{icon}</div>
      <div className="resource-main">
        <div className="resource-title-line">
          <strong>{resource.name}</strong>
          <span className="resource-kind-chip">{resourceKindLabel(resource)}</span>
        </div>
        <small>{resourceMetaLine(resource)}</small>
      </div>
      <div className="resource-actions">
        {onToggleFavorite ? (
          <button
            className={isFavorite ? 'icon-button resource-favorite-button active' : 'icon-button resource-favorite-button'}
            type="button"
            title={isFavorite ? '取消收藏' : '收藏'}
            aria-label={isFavorite ? `取消收藏 ${resource.name}` : `收藏 ${resource.name}`}
            disabled={favoritePending}
            onClick={(event) => {
              event.stopPropagation();
              onToggleFavorite();
            }}
            onDoubleClick={(event) => event.stopPropagation()}
          >
            <Star size={16} />
          </button>
        ) : null}
        {resource.type === 'directory' || resource.type === 'external_link' ? (
          <button
            className="text-action resource-open-action"
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onOpen?.();
            }}
          >
            {resource.type === 'directory' ? <ChevronRight size={15} /> : <ExternalLink size={15} />}
            <span>{resource.type === 'directory' ? '打开' : '访问'}</span>
          </button>
        ) : null}
        {downloadHref ? (
          <a
            className="text-action resource-download-action"
            href={downloadHref}
            download={downloadName}
            title={downloadTitle}
            aria-label={`下载 ${resource.name}`}
            onClick={(event) => {
              event.stopPropagation();
              onDownloadStart?.();
            }}
          >
            <Download size={15} />
            <span>下载</span>
          </a>
        ) : null}
      </div>
    </article>
  );
}

function SearchResultLine({
  result,
  onOpen,
  onToggleFavorite,
  onFilterByTag,
  onFilterByCollection
}: {
  result: SearchResult;
  onOpen: () => void;
  onToggleFavorite: () => void;
  onFilterByTag?: (tag: KnowledgeTag) => void;
  onFilterByCollection?: (collection: CollectionInfo) => void;
}) {
  const typeLabel = result.external_link ? '外链' : resourceKindLabel(result.resource);
  const meta = result.external_link?.domain || result.resource.path || result.resource.description || '资料库';
  const snippet = result.snippet || result.resource.description || result.resource.path || result.external_link?.url || '搜索结果';
  const indexLabel = result.index_status === 'ready' ? '已索引' : result.index_status === 'failed' ? '索引失败' : result.index_status ? '索引中' : '基础索引';
  const actionLabel = result.resource.type === 'external_link' ? '访问' : '打开';

  return (
    <article className="resource-row search-result-row" onDoubleClick={onOpen}>
      <div className="resource-icon search-result-icon">
        {result.resource.type === 'external_link'
          ? <ExternalLink size={20} />
          : result.resource.type === 'collection'
            ? <Folder size={20} />
            : result.resource.thumbnail_url ? <img src={result.resource.thumbnail_url} alt="" loading="lazy" decoding="async" /> : <FileText size={20} />}
      </div>
      <div className="resource-main search-result-main">
        <div className="resource-title-line">
          <strong>{result.resource.name}</strong>
          <span className="resource-kind-chip">{typeLabel}</span>
        </div>
        <p className="search-result-snippet">{snippet}</p>
        <div className="search-result-meta">
          <span>{meta}</span>
          <span>{indexLabel}</span>
          {result.collection ? (
            onFilterByCollection ? (
              <button
                type="button"
                className="search-chip-button"
                title={`筛选合集：${result.collection.resource.name}`}
                onClick={() => onFilterByCollection(result.collection as CollectionInfo)}
                onDoubleClick={(event) => event.stopPropagation()}
              >
                {result.collection.resource.name} · {result.collection.item_count} 项
              </button>
            ) : (
              <span>{result.collection.item_count} 项合集</span>
            )
          ) : null}
        </div>
        {result.tags.length ? (
          <div className="inline-tags">
            {result.tags.map((tag) => onFilterByTag ? (
              <button
                key={tag.id}
                type="button"
                title={`筛选标签：${tag.name}`}
                onClick={() => onFilterByTag(tag)}
                onDoubleClick={(event) => event.stopPropagation()}
              >
                {tag.name}
              </button>
            ) : (
              <span key={tag.id}>{tag.name}</span>
            ))}
          </div>
        ) : null}
      </div>
      <div className="resource-actions search-result-actions">
        <button className={result.favorite ? 'icon-button active' : 'icon-button'} type="button" title={result.favorite ? '取消收藏' : '收藏'} aria-label={result.favorite ? `取消收藏 ${result.resource.name}` : `收藏 ${result.resource.name}`} onClick={onToggleFavorite}>
          <Star size={17} />
        </button>
        <button className="text-action" type="button" onClick={onOpen}>
          {result.resource.type === 'external_link' ? <ExternalLink size={15} /> : <Eye size={15} />}
          <span>{actionLabel}</span>
        </button>
      </div>
    </article>
  );
}

function PreviewWorkspacePanel({
  tabs,
  activeId,
  focused,
  onSelect,
  onClose,
  onCloseAll,
  onResizeStart,
  onToggleFocus,
  onCollapsePane
}: {
  tabs: Resource[];
  activeId: string;
  focused: boolean;
  onSelect: (resourceId: string) => void;
  onClose: (resourceId: string) => void;
  onCloseAll: () => void;
  onResizeStart: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  onToggleFocus: () => void;
  onCollapsePane: () => void;
}) {
  const active = tabs.find((tab) => tab.id === activeId) ?? tabs[0];

  return (
    <aside className="preview-pane" aria-label="资料预览">
      <button className="preview-resize-handle" type="button" aria-label="拖拽调整预览窗口大小" title="拖拽调整预览窗口大小" onPointerDown={onResizeStart} />
      <div className="preview-tabs">
        <div className="preview-tab-list" aria-label="预览标签">
          {tabs.map((tab) => (
            <div key={tab.id} className={tab.id === active?.id ? 'preview-tab active' : 'preview-tab'}>
              <button className="preview-tab-main" type="button" onClick={() => onSelect(tab.id)} title={tab.name} aria-current={tab.id === active?.id ? 'page' : undefined}>
                <FileText size={15} />
                <span>{tab.name}</span>
                <small>{resourceKindLabel(tab)}</small>
              </button>
              <button className="preview-tab-close" type="button" aria-label={`关闭预览 ${tab.name}`} title={`关闭预览 ${tab.name}`} onClick={() => onClose(tab.id)}>
                <X size={14} />
              </button>
            </div>
          ))}
          {tabs.length === 0 ? (
            <div className="preview-empty-tab">
              <Eye size={15} />
              <span>打开文件后在这里预览</span>
            </div>
          ) : null}
        </div>
        <div className="preview-utility-actions">
          {tabs.length > 0 ? (
            <button className="preview-session-action" type="button" aria-label="关闭全部预览标签" title="关闭全部预览标签" onClick={onCloseAll}>
              <X size={16} />
              <span>关闭全部</span>
            </button>
          ) : null}
          {!focused ? (
            <button className="preview-collapse-button" type="button" aria-label="收起预览栏" title="收起预览栏" onClick={onCollapsePane}>
              <ChevronRight size={16} />
              <span>收起</span>
            </button>
          ) : null}
          <button
            className={focused ? 'preview-focus-button active' : 'preview-focus-button'}
            type="button"
            aria-label={focused ? '退出专注预览' : '专注预览'}
            title={focused ? '退出专注预览' : '专注预览'}
            onClick={onToggleFocus}
          >
            {focused ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            <span>{focused ? '退出' : '专注'}</span>
          </button>
        </div>
      </div>
      <div className="preview-stage">
        {active ? (
          <PreviewFrame resourceId={active.id} />
        ) : (
          <WorkspaceStateCard
            compact
            icon={<Eye size={22} />}
            eyebrow="预览区"
            title="选择资料开始阅读"
            description="从左侧资料列表打开文件后，会在这里保留阅读标签和分页预览。"
          />
        )}
      </div>
    </aside>
  );
}

function PreviewFrame({ resourceId }: { resourceId: string }) {
  const previewQuery = useQuery(previewQueryOptions(resourceId));
  if (previewQuery.isLoading) {
    return (
      <WorkspaceStateCard
        compact
        icon={<RefreshCw className="state-spin" size={22} />}
        eyebrow="资料预览"
        title="正在准备预览"
        description="芝士酷正在读取预览信息，阅读标签会继续保留。"
      />
    );
  }
  if (previewQuery.isError || !previewQuery.data) {
    return (
      <WorkspaceStateCard
        compact
        icon={<FileText size={22} />}
        eyebrow="资料预览"
        title="预览加载失败"
        description="资源可能已移动或暂时不可读，可以回到资料列表重新打开。"
      />
    );
  }
  const { resource, preview } = previewQuery.data;
  const isDocumentPreview = preview.kind === 'pdf' || preview.kind === 'office';
  if (isDocumentPreview) return <DocumentPagePreview resource={resource} preview={preview} />;
  return <GenericPreviewFrame resource={resource} preview={preview} />;
}

function clampPreviewZoom(value: number) {
  const bounded = clampNumber(value, previewZoomMin, previewZoomMax, 100);
  return Math.round(bounded / previewZoomStep) * previewZoomStep;
}

function centerScrollableX(element: HTMLElement | null) {
  if (!element) return;
  element.scrollLeft = Math.max(0, Math.round((element.scrollWidth - element.clientWidth) / 2));
}

function centerDocumentStageImage(stage: HTMLElement | null) {
  if (!stage) return;
  const images = Array.from(stage.querySelectorAll<HTMLImageElement>('.document-page img'));
  if (images.length === 0) {
    centerScrollableX(stage);
    return;
  }
  const stageBox = stage.getBoundingClientRect();
  const viewportCenterY = stageBox.top + stage.clientHeight / 2;
  let target = images[0];
  let closest = Number.POSITIVE_INFINITY;
  images.forEach((image) => {
    const imageBox = image.getBoundingClientRect();
    const distance = Math.abs(imageBox.top + imageBox.height / 2 - viewportCenterY);
    if (distance < closest) {
      closest = distance;
      target = image;
    }
  });
  const targetBox = target.getBoundingClientRect();
  const stageCenter = stageBox.left + stage.clientWidth / 2;
  const targetCenter = targetBox.left + targetBox.width / 2;
  const nextLeft = stage.scrollLeft + targetCenter - stageCenter;
  const maxLeft = Math.max(0, stage.scrollWidth - stage.clientWidth);
  stage.scrollLeft = Math.max(0, Math.min(maxLeft, Math.round(nextLeft)));
}

function PreviewZoomControls({ zoom, onChange }: { zoom: number; onChange: (zoom: number) => void }) {
  return (
    <div className="preview-zoom-controls" aria-label="预览缩放">
      <button className="icon-button" type="button" title="缩小预览内容" aria-label="缩小预览内容" onClick={() => onChange(clampPreviewZoom(zoom - previewZoomStep))} disabled={zoom <= previewZoomMin}>
        <ZoomOut size={17} />
      </button>
      <button className="zoom-value" type="button" title="重置缩放" onClick={() => onChange(100)}>
        {zoom}%
      </button>
      <button className="icon-button" type="button" title="放大预览内容" aria-label="放大预览内容" onClick={() => onChange(clampPreviewZoom(zoom + previewZoomStep))} disabled={zoom >= previewZoomMax}>
        <ZoomIn size={17} />
      </button>
    </div>
  );
}

function DocumentPreviewState({
  icon,
  eyebrow,
  title,
  description,
  variant,
  children
}: {
  icon: ReactNode;
  eyebrow: string;
  title: string;
  description: string;
  variant: 'panel' | 'full';
  children?: ReactNode;
}) {
  return (
    <div className={variant === 'full' ? 'document-preview-state full' : 'document-preview-state'}>
      <div className="document-preview-state-icon">{icon}</div>
      <span className="reader-mode-chip">{eyebrow}</span>
      <strong>{title}</strong>
      <p>{description}</p>
      {children ? <div className="document-preview-state-actions">{children}</div> : null}
    </div>
  );
}

function GenericPreviewFrame({ resource, preview }: { resource: Resource; preview: PreviewInfo }) {
  const [zoom, setZoom] = useState(100);
  const scale = zoom / 100;
  return (
    <div className="embed-preview-reader">
      <div className="embed-preview-toolbar compact-reader-toolbar">
        <div className="reader-control-deck">
          <PreviewZoomControls zoom={zoom} onChange={setZoom} />
          <a className="text-action" href={preview.embed_preview_url} target="_blank" rel="noreferrer" aria-label="新窗口打开" title="新窗口打开">
            <ExternalLink size={14} />
            <span>新窗口打开</span>
          </a>
        </div>
      </div>
      <div className="embed-preview-stage">
        <div className="embed-preview-zoom-surface" style={{ width: `${Math.max(100, zoom)}%`, height: `${Math.max(100, zoom)}%` }}>
          <iframe
            className="preview-frame"
            src={preview.embed_preview_url}
            title={`${resource.name} 预览`}
            style={{
              width: `${100 / scale}%`,
              height: `${100 / scale}%`,
              transform: `scale(${scale})`
            }}
          />
        </div>
      </div>
    </div>
  );
}

function DocumentPagePreview({ resource, preview, variant = 'panel' }: { resource: Resource; preview: PreviewInfo; variant?: 'panel' | 'full' }) {
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [zoom, setZoom] = useState(100);
  const stageRef = useRef<HTMLDivElement>(null);
  const pagesQuery = useQuery({
    queryKey: ['preview-pages', resource.id],
    queryFn: () => getResourcePreviewPages(resource.id),
    staleTime: previewMetadataStaleMs,
    retry: 1
  });
  const pages = pagesQuery.data?.pages.pages ?? [];
  useEffect(() => {
    setActivePageIndex(0);
    stageRef.current?.scrollTo({ top: 0 });
  }, [resource.id, pages.length]);
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => centerDocumentStageImage(stageRef.current));
    return () => window.cancelAnimationFrame(frame);
  }, [resource.id, pages.length, zoom, variant]);
  if (pagesQuery.isLoading) {
    return (
      <DocumentPreviewState
        icon={<RefreshCw className="state-spin" size={28} />}
        eyebrow={variant === 'full' ? '完整分页预览' : '工作台分页预览'}
        title="正在生成分页预览"
        description="阅读器正在准备可缩放的分页图片，当前资料会保持打开；也可以先用原阅读器继续。"
        variant={variant}
      >
        <a className="text-action" href={preview.embed_preview_url} target="_blank" rel="noreferrer">
          <ExternalLink size={15} />
          <span>打开原阅读器</span>
        </a>
      </DocumentPreviewState>
    );
  }
  if (pagesQuery.isError || !pagesQuery.data) {
    return (
      <DocumentPreviewState
        icon={<FileText size={28} />}
        eyebrow={variant === 'full' ? '完整分页预览' : '工作台分页预览'}
        title="分页预览暂不可用"
        description="分页图片生成失败或资源暂时不可读，可以重试分页，也可以打开原阅读器查看。"
        variant={variant}
      >
        <button className="text-action" type="button" onClick={() => void pagesQuery.refetch()} disabled={pagesQuery.isFetching}>
          <RefreshCw size={15} />
          <span>{pagesQuery.isFetching ? '重试中' : '重试分页'}</span>
        </button>
        <a className="text-action" href={preview.embed_preview_url} target="_blank" rel="noreferrer">
          <ExternalLink size={15} />
          <span>打开原阅读器</span>
        </a>
      </DocumentPreviewState>
    );
  }
  if (pagesQuery.data.pages.page_count === 0 || pages.length === 0) {
    const fallbackUrl = pagesQuery.data.pages.fallback_url || preview.embed_preview_url;
    return (
      <DocumentPreviewState
        icon={<FileText size={28} />}
        eyebrow={variant === 'full' ? '完整分页预览' : '工作台分页预览'}
        title="还没有可用分页"
        description="当前文档没有返回分页图片，原阅读器仍可打开，适合先完成阅读或核对源文件。"
        variant={variant}
      >
        <a className="text-action" href={fallbackUrl} target="_blank" rel="noreferrer">
          <ExternalLink size={15} />
          <span>打开原阅读器</span>
        </a>
      </DocumentPreviewState>
    );
  }
  const syncActivePageFromScroll = () => {
    const stage = stageRef.current;
    if (!stage) return;
    const pagesInStage = Array.from(stage.querySelectorAll<HTMLElement>('.document-page'));
    if (pagesInStage.length === 0) return;
    const maxScrollTop = Math.max(0, stage.scrollHeight - stage.clientHeight);
    if (stage.scrollTop <= 4) {
      setActivePageIndex(0);
      return;
    }
    if (stage.scrollTop >= maxScrollTop - 4) {
      setActivePageIndex(pagesInStage.length - 1);
      return;
    }
    const viewportCenter = stage.scrollTop + stage.clientHeight / 2;
    let nextIndex = 0;
    let closest = Number.POSITIVE_INFINITY;
    pagesInStage.forEach((page, index) => {
      const pageCenter = page.offsetTop + page.offsetHeight / 2;
      const distance = Math.abs(pageCenter - viewportCenter);
      if (distance < closest) {
        closest = distance;
        nextIndex = index;
      }
    });
    setActivePageIndex(nextIndex);
  };
  const scrollToPage = (nextIndex: number) => {
    const stage = stageRef.current;
    const bounded = Math.max(0, Math.min(pages.length - 1, nextIndex));
    const target = stage?.querySelectorAll<HTMLElement>('.document-page')[bounded];
    setActivePageIndex(bounded);
    if (stage && target) {
      stage.scrollTo({ top: Math.max(0, target.offsetTop - 14), behavior: 'smooth' });
    }
  };
  const pageMaxWidth = variant === 'full' ? 1180 : 1120;
  const pageImageStyle = {
    width: `min(${zoom}%, ${Math.round(pageMaxWidth * zoom / 100)}px)`
  } as CSSProperties;
  return (
    <div className={`document-reader ${variant === 'full' ? 'full-document-reader' : ''}`} aria-label={`${resource.name} 分页预览`}>
      <div className={variant === 'full' ? 'document-reader-toolbar' : 'document-reader-toolbar compact-reader-toolbar'}>
        {variant === 'full' ? (
          <div className="reader-context-card">
            <div className="reader-context-icon">
              <FileText size={17} />
            </div>
            <div className="reader-context-copy">
              <span className="reader-mode-chip">完整分页预览</span>
              <strong>{resource.name}</strong>
              <small>{resource.path || resource.name} · {formatSize(resource.size)}</small>
            </div>
          </div>
        ) : null}
        <div className="reader-control-deck">
          <div className="reader-page-controls" aria-label="分页导航">
            <button className="icon-button" type="button" title="上一页" onClick={() => scrollToPage(activePageIndex - 1)} disabled={activePageIndex === 0}>
              <ChevronLeft size={18} />
            </button>
            <span className="reader-page-status">第 {activePageIndex + 1} / {pages.length} 页</span>
            <button className="icon-button" type="button" title="下一页" onClick={() => scrollToPage(activePageIndex + 1)} disabled={activePageIndex >= pages.length - 1}>
              <ChevronRight size={18} />
            </button>
          </div>
          <PreviewZoomControls zoom={zoom} onChange={setZoom} />
          <a className="text-action" href={preview.embed_preview_url} target="_blank" rel="noreferrer" aria-label="原阅读器" title="原阅读器">
            <ExternalLink size={14} />
            <span>原阅读器</span>
          </a>
        </div>
      </div>
      <div className="document-page-stage" ref={stageRef} onScroll={syncActivePageFromScroll}>
        {pages.map((page, index) => (
          <figure className="document-page" key={page.page}>
            <img
              src={page.url}
              alt={`${resource.name} 第 ${page.page} 页`}
              loading={index < 2 ? 'eager' : 'lazy'}
              style={pageImageStyle}
              onLoad={() => window.requestAnimationFrame(() => centerDocumentStageImage(stageRef.current))}
            />
            <figcaption>{page.page} / {pages.length}</figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
}

function FullPreviewPage() {
  const { resourceId = '' } = useParams();
  const previewQuery = useQuery(previewQueryOptions(resourceId));
  if (previewQuery.isLoading) {
    return (
      <AppShell>
        <main className="full-preview-page">
          <section className="full-preview-state">
            <FileText size={28} />
            <strong>正在准备预览</strong>
            <span>芝士酷正在打开安全阅读视图。</span>
          </section>
        </main>
      </AppShell>
    );
  }
  if (previewQuery.isError || !previewQuery.data) {
    return (
      <AppShell>
        <main className="full-preview-page">
          <section className="full-preview-state">
            <FileText size={28} />
            <strong>预览加载失败</strong>
            <span>资源可能已删除、移动，或当前账号没有访问权限。</span>
            <Link className="text-action" to="/workspace">
              <ListTree size={15} />
              <span>返回工作台</span>
            </Link>
          </section>
        </main>
      </AppShell>
    );
  }
  const { resource, preview } = previewQuery.data;
  const isDocumentPreview = preview.kind === 'pdf' || preview.kind === 'office';
  const previewModeDescription = isDocumentPreview ? '分页阅读模式，保留原阅读器出口' : '安全嵌入模式，可打开原阅读器';
  return (
    <AppShell>
      <main className="full-preview-page">
        <section className="full-preview-shell">
          <header className="full-preview-hero">
            <div className="full-preview-title">
              <span className="home-eyebrow">私有阅读会话</span>
              <h1>{resource.name}</h1>
              <p>{resource.path || resource.description || '资料库资源'}</p>
            </div>
            <div className="full-preview-session-summary" aria-label="阅读会话">
              <span>
                <BookOpen size={14} />
                {isDocumentPreview ? '分页阅读器' : '安全预览'}
              </span>
              <strong>{previewKindLabel(preview.kind)}</strong>
              <small>{previewModeDescription}</small>
            </div>
            <div className="full-preview-utility">
              <div className="full-preview-meta" aria-label="预览信息">
                <span>{resourceKindLabel(resource)}</span>
                <span>{formatSize(resource.size)}</span>
                <span>{formatDate(resource.modified_at)}</span>
                <span>{previewKindLabel(preview.kind)}</span>
              </div>
              <div className="full-preview-actions" aria-label="完整预览操作">
                <Link className="text-action" to="/workspace">
                  <ListTree size={15} />
                  <span>返回工作台</span>
                </Link>
                <a className="text-action" href={preview.embed_preview_url} target="_blank" rel="noreferrer">
                  <ExternalLink size={15} />
                  <span>原阅读器</span>
                </a>
              </div>
            </div>
          </header>
          <div className="full-preview-body">
            {isDocumentPreview ? (
              <DocumentPagePreview resource={resource} preview={preview} variant="full" />
            ) : (
              <section className="full-preview-reader" aria-label={`${resource.name} 预览`}>
                <iframe className="preview-frame full-preview-frame" src={preview.embed_preview_url} title={`${resource.name} 预览`} />
              </section>
            )}
          </div>
        </section>
      </main>
    </AppShell>
  );
}

function PublicSharePage() {
  const { token = '' } = useParams();
  const storageKey = `janestra:share:${token}`;
  const [password, setPassword] = useState('');
  const [unlockToken, setUnlockToken] = useState(() => sessionStorage.getItem(storageKey) ?? '');
  const [selectedId, setSelectedId] = useState('');
  const shareQuery = useQuery({
    queryKey: ['public-share', token, unlockToken],
    queryFn: () => getPublicShare(token, unlockToken),
    retry: false
  });
  const unlockMutation = useMutation({
    mutationFn: () => unlockPublicShare(token, password),
    onSuccess: (nextUnlockToken) => {
      sessionStorage.setItem(storageKey, nextUnlockToken);
      setUnlockToken(nextUnlockToken);
      setPassword('');
    }
  });
  const data = shareQuery.data;
  const locked = data && 'locked' in data && data.locked;
  const resources = data && !locked ? [data.resource, ...(data.children ?? [])] : [];
  const previewable = resources.filter((resource) => resource.type === 'file');
  const selectedResource = previewable.find((resource) => resource.id === selectedId) ?? previewable[0] ?? null;
  const selectedIndex = selectedResource ? previewable.findIndex((resource) => resource.id === selectedResource.id) + 1 : 0;

  useEffect(() => {
    if (!selectedId && previewable[0]) setSelectedId(previewable[0].id);
  }, [previewable, selectedId]);

  if (shareQuery.isError) {
    return (
      <main className="public-share-page">
        <PublicShareStateCard
          eyebrow="访问受限"
          icon={<ShieldCheck size={26} />}
          title="分享不可用"
          description="这个链接可能已过期、被撤销，或不在可访问范围内。可以重试一次，或让分享人重新生成链接。"
          actions={(
            <button className="primary-button" type="button" onClick={() => shareQuery.refetch()} disabled={shareQuery.isFetching}>
              <RefreshCw className={shareQuery.isFetching ? 'state-spin' : ''} size={17} />
              <span>{shareQuery.isFetching ? '正在重试' : '重新尝试'}</span>
            </button>
          )}
        />
      </main>
    );
  }

  if (shareQuery.isLoading) {
    return (
      <main className="public-share-page">
        <PublicShareStateCard
          eyebrow="正在连接"
          icon={<RefreshCw className="state-spin" size={26} />}
          title="正在打开分享"
          description="正在确认分享链接、访问密码和授权范围。公开入口只会显示分享人允许访问的资料。"
        />
      </main>
    );
  }

  if (locked) {
    return (
      <main className="public-share-page">
        <section className="public-share-card narrow public-share-lock-card">
          <div className="brand public-brand">
            <span className="brand-mark">芝</span>
            <span>
              <strong>芝士酷</strong>
              <small>受保护分享</small>
            </span>
          </div>
          <div className="public-share-message">
            <KeyRound size={26} />
            <h1>需要访问密码</h1>
            <p>这是受保护的资料分享。输入分享密码后，只能查看授权范围内的内容。</p>
          </div>
          <form
            className="login-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (password.trim()) unlockMutation.mutate();
            }}
          >
            <label>
              访问密码
              <input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="off" />
            </label>
            {unlockMutation.isError ? (
              <div className="public-inline-feedback error" role="alert">
                <ShieldCheck size={16} />
                <span>密码不正确或分享不可用</span>
              </div>
            ) : null}
            <button className="primary-button" type="submit" disabled={!password.trim() || unlockMutation.isPending}>
              <KeyRound size={18} />
              <span>{unlockMutation.isPending ? '正在验证' : '打开分享'}</span>
            </button>
          </form>
        </section>
      </main>
    );
  }

  if (!data || 'locked' in data) {
    return null;
  }
  const sharedFileCount = resources.filter((resource) => resource.type === 'file').length;
  const downloadableFileCount = data.share.allow_download ? sharedFileCount : 0;
  const selectedMeta = selectedResource ? `${formatSize(selectedResource.size)} · ${formatDate(selectedResource.modified_at)}` : '';
  const selectedPath = selectedResource?.path || data.resource.name;

  return (
    <main className="public-share-page">
      <section className="public-share-card public-share-open-card">
        <header className="public-share-hero">
          <div className="public-share-hero-main">
            <div className="brand public-brand">
              <span className="brand-mark">芝</span>
              <span>
                <strong>芝士酷</strong>
                <small>资料分享</small>
              </span>
            </div>
            <div className="public-share-title">
              <span className="home-eyebrow">受控公开访问</span>
              <h1>{data.resource.name}</h1>
              <p>{data.resource.type === 'directory' ? `共享文件夹 · ${directoryMetaText(data.resource)}` : `共享文件 · ${formatSize(data.resource.size)}`}</p>
            </div>
          </div>
          <div className="public-share-status" aria-label="分享权限">
            <span>
              <Eye size={15} />
              {data.share.allow_preview ? '允许预览' : '不允许预览'}
            </span>
            <span>
              <Download size={15} />
              {data.share.allow_download ? '允许下载' : '禁止下载'}
            </span>
            <span>
              <ShieldCheck size={15} />
              {data.share.requires_password ? '密码保护' : '无需密码'}
            </span>
            <span>{formatDate(data.share.expires_at)} 到期</span>
          </div>
        </header>
        <section className="public-share-overview" aria-label="分享概览">
          <div>
            <span>授权范围</span>
            <strong>{resources.length} 项资料</strong>
            <small>{sharedFileCount} 个文件 · {data.resource.type === 'directory' ? '文件夹分享' : '单文件分享'}</small>
          </div>
          <div>
            <span>当前预览</span>
            <strong>{selectedResource ? selectedResource.name : '暂无文件'}</strong>
            <small>{selectedResource && data.share.allow_preview ? `第 ${selectedIndex}/${previewable.length} 个可预览文件` : data.share.allow_preview ? '没有可预览文件' : '预览权限未开放'}</small>
          </div>
          <div>
            <span>下载权限</span>
            <strong>{data.share.allow_download ? `${downloadableFileCount} 个可下载` : '禁止下载'}</strong>
            <small>{data.share.requires_password ? '密码验证后生效' : '公开链接直接生效'}</small>
          </div>
        </section>
        <div className="public-share-workspace">
          <aside className="public-resource-list" aria-label="分享资源列表">
            <div className="public-list-heading">
              <strong>分享内容</strong>
              <small>{resources.length} 项 · {sharedFileCount} 个文件</small>
            </div>
            {resources.map((resource) => (
              <article
                key={resource.id}
                className={resource.id === selectedResource?.id ? 'public-resource-card selected' : 'public-resource-card'}
              >
                <button
                  className="public-resource"
                  type="button"
                  disabled={resource.type !== 'file'}
                  aria-pressed={resource.id === selectedResource?.id}
                  onClick={() => setSelectedId(resource.id)}
                >
                  <span className="public-resource-icon" aria-hidden="true">
                    {resource.type === 'directory' ? <Folder size={18} /> : <FileText size={18} />}
                  </span>
                  <span>
                    <span className="public-resource-name-line">
                      <strong>{resource.name}</strong>
                      <em>{resource.type === 'directory' ? '范围' : resourceKindLabel(resource)}</em>
                    </span>
                    <small>{resource.type === 'directory' ? directoryMetaText(resource) : `${formatSize(resource.size)} · ${formatDate(resource.modified_at)}`}</small>
                  </span>
                </button>
                {resource.type === 'file' && data.share.allow_download ? (
                  <a className="public-resource-download" href={publicDownloadURL(token, resource.id, unlockToken)} title={`下载 ${resource.name}`}>
                    <Download size={15} />
                    <span>下载</span>
                  </a>
                ) : null}
              </article>
            ))}
          </aside>
          <section className="public-preview-panel">
            {selectedResource && data.share.allow_preview ? (
              <div className="public-preview-reader">
                <div className="public-preview-title">
                  <div className="public-preview-session">
                    <span className="public-preview-kicker">当前预览</span>
                    <strong>{selectedResource.name}</strong>
                    <div className="public-preview-meta" aria-label="当前文件信息">
                      <span>{resourceKindLabel(selectedResource)}</span>
                      <span>{selectedMeta}</span>
                      <span>{selectedPath}</span>
                    </div>
                  </div>
                  <div className="public-preview-actions">
                    <span>{selectedIndex}/{previewable.length}</span>
                    {data.share.allow_download ? (
                      <a className="text-action" href={publicDownloadURL(token, selectedResource.id, unlockToken)} title="下载当前文件">
                        <Download size={16} />
                        <span>下载当前文件</span>
                      </a>
                    ) : null}
                  </div>
                </div>
                <iframe className="preview-frame" src={publicPreviewURL(token, selectedResource.id, unlockToken)} title={`${selectedResource.name} 预览`} />
              </div>
            ) : (
              <div className="public-preview-empty">
                <Eye size={24} />
                <strong>{data.share.allow_preview ? '没有可预览文件' : '预览未开放'}</strong>
                <p>{selectedResource && data.share.allow_download ? '可以从左侧资源列表下载授权文件。' : '这个分享当前没有可在浏览器中打开的文件。'}</p>
                {selectedResource && data.share.allow_download ? (
                  <a className="primary-button" href={publicDownloadURL(token, selectedResource.id, unlockToken)}>
                    <Download size={16} />
                    <span>下载当前文件</span>
                  </a>
                ) : null}
              </div>
            )}
          </section>
        </div>
      </section>
    </main>
  );
}

function PublicShareStateCard({ eyebrow, icon, title, description, actions }: { eyebrow: string; icon: ReactNode; title: string; description: string; actions?: ReactNode }) {
  return (
    <section className="public-share-card narrow public-share-state-card">
      <div className="brand public-brand">
        <span className="brand-mark">芝</span>
        <span>
          <strong>芝士酷</strong>
          <small>资料分享</small>
        </span>
      </div>
      <div className="public-share-state">
        <span className="public-share-state-icon" aria-hidden="true">{icon}</span>
        <span className="home-eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
        {actions ? <div className="public-share-state-actions">{actions}</div> : null}
      </div>
    </section>
  );
}

function filterResources(resources: Resource[], filter: FilterMode, query: string) {
  const q = query.trim().toLowerCase();
  return resources.filter((resource) => {
    if (q && !`${resource.name} ${resource.path}`.toLowerCase().includes(q)) return false;
    if (filter === 'all') return true;
    if (filter === 'folders') return resource.type === 'directory';
    const ext = resource.extension.toLowerCase();
    if (filter === 'documents') return ['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.xls', '.xlsx', '.md', '.txt'].includes(ext);
    if (filter === 'media') return ['.mp4', '.webm', '.mp3', '.wav', '.m4a', '.flac'].includes(ext);
    if (filter === 'images') return ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'].includes(ext);
    if (filter === 'archives') return ['.zip', '.rar', '.7z', '.tar', '.gz'].includes(ext);
    if (filter === 'code') return ['.go', '.py', '.js', '.ts', '.tsx', '.java', '.c', '.cpp', '.html', '.css', '.json', '.yaml', '.yml', '.sql'].includes(ext);
    return true;
  });
}

function isManageableWorkspaceResource(resource: Resource) {
  return resource.type === 'file' || resource.type === 'directory';
}

function sortResources(resources: Resource[], sort: SortMode) {
  return [...resources].sort((a, b) => {
    if (a.type !== b.type) return a.type === 'directory' ? -1 : 1;
    if (sort === 'modified') return Date.parse(b.modified_at) - Date.parse(a.modified_at);
    if (sort === 'size') return b.size - a.size;
    if (sort === 'type') return a.extension.localeCompare(b.extension, 'zh-CN');
    return a.name.localeCompare(b.name, 'zh-CN');
  });
}

function formatSize(size: number) {
  if (size >= 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} MB`;
  if (size >= 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${size} B`;
}

function resourceMetaLine(resource: Resource) {
  if (resource.type === 'directory') {
    return `${resource.path || '根目录'} · 文件夹 · ${directoryMetaText(resource)} · ${formatDate(resource.modified_at)}`;
  }
  if (resource.type === 'external_link') {
    return `${resource.path || resource.description || '外部链接'} · ${formatDate(resource.modified_at)}`;
  }
  if (resource.type === 'collection') {
    return `${resource.path || resource.description || '合集'} · ${formatDate(resource.modified_at)}`;
  }
  return `${resource.path || resource.description || '资源'} · ${formatSize(resource.size)} · ${formatDate(resource.modified_at)}`;
}

function resourceKindLabel(resource: Resource) {
  if (resource.type === 'directory') return '文件夹';
  if (resource.type === 'external_link') return '外链';
  if (resource.type === 'collection') return '合集';
  const ext = resource.extension.replace(/^\./, '').toUpperCase();
  return ext || '文件';
}

function previewKindLabel(kind: PreviewInfo['kind']) {
  if (kind === 'pdf') return '分页 PDF';
  if (kind === 'office') return 'Office 预览';
  if (kind === 'markdown') return 'Markdown';
  if (kind === 'html') return 'HTML';
  if (kind === 'image') return '图片';
  if (kind === 'video') return '视频';
  if (kind === 'audio') return '音频';
  if (kind === 'text') return '文本';
  if (kind === 'csv') return 'CSV';
  return '预览';
}

function directoryMetaText(resource: Resource) {
  const count = Math.max(0, resource.file_count || 0);
  return `${count} 个文件 · ${formatSize(resource.size || 0)}`;
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '未知时间';
  return date.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function errorMessage(error: unknown) {
  if (!error) return '';
  if (error instanceof APIError) return error.message;
  if (error instanceof Error) return error.message;
  return '操作失败';
}

function absoluteShareURL(path: string) {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
}

export function App() {
  useThemePreferences();
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/share/:token" element={<PublicSharePage />} />
      <Route
        path="/"
        element={
          <RequireAuth allowGuest>
            <HomePage />
          </RequireAuth>
        }
      />
      <Route
        path="/workspace"
        element={
          <RequireAuth allowGuest>
            <WorkspacePage />
          </RequireAuth>
        }
      />
      <Route
        path="/workspace/knowledge"
        element={
          <RequireAuth>
            <KnowledgePage />
          </RequireAuth>
        }
      />
      <Route
        path="/workspace/manage"
        element={
          <RequireAuth>
            <ManagementPage />
          </RequireAuth>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireAuth>
            <SettingsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/preview/:resourceId"
        element={
          <RequireAuth allowGuest>
            <FullPreviewPage />
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
